import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import envConfig from '../config/environment';

const VISTA_INICIO = 'inicio';
/** Submenú de ingreso (solo Carga; guía especial y Datafast retirados del producto). */
const VISTA_MENU_INGRESO = 'menu_ingreso';
const VISTA_MENU_SALIDA = 'menu_salida';
const VISTA_INGRESO_AIRE_DISTRIB = 'ingreso_aire_distrib';
const VISTA_INGRESO_ALFA_DISTRIB = 'ingreso_alfa_distrib';
const VISTA_SALIDA_DIST_AIRE = 'salida_dist_aire';
const VISTA_SALIDA_DIST_TRANSFERENCIA = 'salida_dist_transferencia';
const VISTA_SALIDA_DIST_ALFA = 'salida_dist_alfa';
const VISTA_SALIDA_DIST_R = 'salida_dist_r';
const VISTA_CARGA = 'carga';
const VISTA_INGRESO = 'ingreso';

/** Salida Z. Distribución → Z. Aire
 *  'vehiculo' agrupa camión/mula/tractomula; 'personal' agrupa operadores/sierra oscar/sin denominativo/acompañamiento. */
const PASOS_SALIDA_DIST_AIRE = [
  'vehiculo',
  'personal',
  'carga_lleva',
  'servicios_especiales'
];

/** Salida Z. Distribución → Z. Transferencia (mismos pasos que Aire) */
const PASOS_SALIDA_DIST_TRANSFERENCIA = [
  'vehiculo',
  'personal',
  'carga_lleva',
  'servicios_especiales'
];

const buildSalidaDistSlotOrder = (serviciosSi, n) => {
  const slots = [];
  if (!serviciosSi || !Number.isFinite(n) || n < 1) return slots;
  const capped = Math.max(1, Math.floor(n));
  for (let i = 0; i < capped; i++) {
    slots.push(`salida_dist_svc_${i}_guia`, `salida_dist_svc_${i}_bultos`);
  }
  return slots;
};

const buildSalidaXferSlotOrder = (serviciosSi, n) => {
  const slots = [];
  if (!serviciosSi || !Number.isFinite(n) || n < 1) return slots;
  const capped = Math.max(1, Math.floor(n));
  for (let i = 0; i < capped; i++) {
    slots.push(`salida_xfer_svc_${i}_guia`, `salida_xfer_svc_${i}_bultos`);
  }
  return slots;
};

/** Salida Z. Distribución → Z. Alfa (dolis, carretas y texto en un solo paso final) */
const PASOS_SALIDA_DIST_ALFA = [
  'operadores',
  'vehiculo',
  'jacpalet',
  'jacpalet_manual',
  'observaciones'
];

/** Ingreso Z. Alfa → Z. Distribución (mismos pasos que Salida Dist→Alfa) */
const PASOS_INGRESO_ALFA_DISTRIB = [
  'operadores',
  'vehiculo',
  'jacpalet',
  'jacpalet_manual',
  'observaciones'
];

/** Salida Z. Distribución → Z. R
 *  'personal' agrupa operador / estivador / sierra oscar (uno solo).
 *  'vehiculo' agrupa mula / montacargas (uno solo). */
const PASOS_SALIDA_DIST_R = ['personal', 'vehiculo', 'observacion'];

/** Opciones (radio) personal para Salida Dist→R. */
const SALIDA_R_PERSONAL_OPCIONES = [
  { valor: 'operador', etiqueta: 'Operador' },
  { valor: 'estivador', etiqueta: 'Estivador' },
  { valor: 'sierra_oscar', etiqueta: 'Sierra Oscar' }
];

/** Opciones (radio) vehículo para Salida Dist→R. */
const SALIDA_R_VEHICULO_OPCIONES = [
  { valor: 'mula', etiqueta: 'Mula' },
  { valor: 'montacargas', etiqueta: 'Montacargas' }
];

const ETIQUETAS_MATERIAL_OBS_R = {
  plastico: 'Con plástico',
  madera: 'Con madera',
  no: 'No las lleva'
};

const ADD_SENTINEL = '__agregar__';
/** Calidad JPEG alta: reduce peso del archivo sin pixelar tanto la imagen. */
const JPEG_QUALITY_AT2 = 0.88;
/** Solo se reduce ancho si la foto es muy grande (pesa menos al subir); imágenes ya pequeñas no se tocan. */
const MAX_IMAGE_WIDTH = 1920;
const MAX_FOTOS_SLOT = 3;

const PASOS_CARGA = ['unidad', 'chofer', 'aerolinea', 'foto_elementos'];

/** Ingreso Z. Aire → Z. Distribución (un campo o bloque por paso; confirmación en modal).
 *  'vehiculo' agrupa camión/mula/tractomula (se elige el tipo y se llena solo ese).
 *  'personal' agrupa operadores/sierra oscar/operador sin denominativo/acompañantes (igual, uno solo). */
const PASOS_INGRESO_AIRE_DISTRIB = [
  'vehiculo',
  'personal',
  'aerolinea_vuelo',
  'carga_tipos'
];

/** Opciones (radio) para el paso "vehiculo" del ingreso aire→distribución. */
const INGRESO_AIRE_VEHICULO_OPCIONES = [
  { valor: 'camion', etiqueta: 'Camión' },
  { valor: 'mula', etiqueta: 'Mula' },
  { valor: 'tractomula', etiqueta: 'Tractomula' }
];

/** Opciones (radio) para el paso "vehiculo" del ingreso alfa→distribución. */
const INGRESO_ALFA_VEHICULO_OPCIONES = [
  { valor: 'camion', etiqueta: 'Camión' },
  { valor: 'mula', etiqueta: 'Mula' },
  { valor: 'tractomula', etiqueta: 'Tractomula' },
  { valor: 'montacargas', etiqueta: 'Montacargas' }
];

/** Opciones (radio) vehículo para Salida Dist→Alfa (camión, mula o montacargas). */
const SALIDA_ALFA_VEHICULO_OPCIONES = [
  { valor: 'camion', etiqueta: 'Camión' },
  { valor: 'mula', etiqueta: 'Mula' },
  { valor: 'montacargas', etiqueta: 'Montacargas' }
];

/** Opciones (radio) para el paso "personal" del ingreso aire→distribución. */
const INGRESO_AIRE_PERSONAL_OPCIONES = [
  { valor: 'operadores', etiqueta: 'Operadores' },
  { valor: 'sierra_oscar', etiqueta: 'Sierra Oscar' },
  { valor: 'operador_sin_den', etiqueta: 'Operador sin denominativo' },
  { valor: 'acompanantes', etiqueta: 'Acompañantes' }
];

/** Opciones (radio) personal para Salida Dist→Aire (misma lógica; etiqueta Acompañamiento). */
const SALIDA_DIST_AIRE_PERSONAL_OPCIONES = [
  { valor: 'operadores', etiqueta: 'Operadores' },
  { valor: 'sierra_oscar', etiqueta: 'Sierra Oscar' },
  { valor: 'operador_sin_den', etiqueta: 'Operador sin denominativo' },
  { valor: 'acompanantes', etiqueta: 'Acompañamiento' }
];

const AT2_ORDER_CARGA = ['carga_elementos'];

/** Solo tope de caracteres en el campo (evita cadenas absurdas); no es un límite de negocio. */
const SALIDA_CANTIDAD_SVC_DIGITOS_MAX = 10;

const maxFotosPorDestinoAt2 = (dest) => {
  const s = String(dest || '');
  if (/^salida_svc_\d+_bultos$/.test(s)) return 1;
  if (/^salida_dist_svc_\d+_guia$/.test(s)) return 1;
  if (/^salida_dist_svc_\d+_bultos$/.test(s)) return 2;
  if (/^salida_xfer_svc_\d+_guia$/.test(s)) return 1;
  if (/^salida_xfer_svc_\d+_bultos$/.test(s)) return 2;
  return MAX_FOTOS_SLOT;
};

/** Solo dígitos, acotado en longitud (evita `type="number"` y spinners en 0). */
const soloDigitosMax = (s, maxLen) => String(s ?? '').replace(/\D/g, '').slice(0, maxLen);

const buildEmptyFotoMap = () => ({
  carga_elementos: []
});

const buildSalidaSlotOrder = (serviciosSi, n) => {
  const slots = [];
  if (!serviciosSi || !Number.isFinite(n) || n < 1) return slots;
  const capped = Math.max(1, Math.floor(n));
  for (let i = 0; i < capped; i++) {
    slots.push(`salida_svc_${i}_guia`, `salida_svc_${i}_bultos`);
  }
  return slots;
};

/** Asegura arrays vacíos por slot de salida para no leer undefined.length en la UI. */
const ensureSalidaFotoSlotsInMap = (map, nServicios) => {
  const slots = buildSalidaSlotOrder(true, nServicios);
  if (!slots.length) return map;
  const next = { ...map };
  for (const slot of slots) {
    if (!Array.isArray(next[slot])) next[slot] = [];
  }
  return next;
};

const ensureSalidaDistFotoSlotsInMap = (map, nServicios) => {
  const slots = buildSalidaDistSlotOrder(true, nServicios);
  if (!slots.length) return map;
  const next = { ...map };
  for (const slot of slots) {
    if (!Array.isArray(next[slot])) next[slot] = [];
  }
  return next;
};

const ensureSalidaXferFotoSlotsInMap = (map, nServicios) => {
  const slots = buildSalidaXferSlotOrder(true, nServicios);
  if (!slots.length) return map;
  const next = { ...map };
  for (const slot of slots) {
    if (!Array.isArray(next[slot])) next[slot] = [];
  }
  return next;
};
const AT2_CATALOGO_DESCRIPCION = Object.freeze({
  ALFA_TANGO_2_AEROLINEA: 'Alfa Tango 2 - Aerolínea',
  ALFA_TANGO_2_MULA: 'Alfa Tango 2 - Mula',
  ALFA_TANGO_2_TRACTOMULA: 'Alfa Tango 2 - Tractomula',
  ALFA_TANGO_2_OPERADOR: 'Alfa Tango 2 - Operador',
  ALFA_TANGO_2_SIERRO_OSCAR: 'Alfa Tango 2 - Sierra Oscar',
  ALFA_TANGO_2_ACOMPANANTE: 'Alfa Tango 2 - Acompañante',
  ALFA_TANGO_2_MONTACARGAS: 'Alfa Tango 2 - Montacargas',
  ALFA_TANGO_2_JAC_PALET: 'Alfa Tango 2 - JacPalet',
  CONDUCTOR: 'Alfa Tango 2 - Chofer',
  VEHICULO: 'Alfa Tango 2 - Unidad'
});

const SLOT_LABELS = {
  carga_elementos: 'Fotos de los elementos (máx. 3)',
  ingreso_unidad: 'Foto(s) camión / dolly / carreta (histórico)'
};

const parseSalidaSvcPaso = (paso) => {
  const m = /^svc_(\d+)_(codigo|guia|bultos)$/.exec(paso || '');
  if (!m) return null;
  return { idx: parseInt(m[1], 10), part: m[2] };
};

const parseSalidaDistServicioPaso = (paso) => {
  const m = /^servicio_(\d+)$/.exec(paso || '');
  if (!m) return null;
  return { idx: parseInt(m[1], 10) };
};

const labelSalidaFotoSlot = (slot) => {
  const mg = /^salida_svc_(\d+)_guia$/.exec(slot);
  if (mg) return `Servicio ${parseInt(mg[1], 10) + 1} — guía master`;
  const mb = /^salida_svc_(\d+)_bultos$/.exec(slot);
  if (mb) return `Servicio ${parseInt(mb[1], 10) + 1} — bultos`;
  return slot;
};

const isMobileDeviceForCamera = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > MAX_IMAGE_WIDTH) {
        height = (height * MAX_IMAGE_WIDTH) / width;
        width = MAX_IMAGE_WIDTH;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], file.name || 'at2.jpg', { type: 'image/jpeg' }));
          else reject(new Error('Compresión fallida'));
        },
        'image/jpeg',
        JPEG_QUALITY_AT2
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error cargando imagen'));
    };
    img.src = url;
  });
};

/** Paletas por zona (misma en ingreso y salida): fondo claro y borde completo del mismo tono. */
const ESTILOS_ZONA_RUTA_AT2 = Object.freeze({
  aire: 'border-2 border-sky-400 bg-sky-50 text-sky-950 hover:bg-sky-100 hover:border-sky-500 shadow-sm shadow-sky-100/60',
  alfa: 'border-2 border-teal-500 bg-teal-50 text-teal-950 hover:bg-teal-100 hover:border-teal-600 shadow-sm shadow-teal-100/60',
  transferencia:
    'border-2 border-orange-400 bg-orange-50 text-orange-950 hover:bg-orange-100 hover:border-orange-500 shadow-sm shadow-orange-100/60',
  r: 'border-2 border-violet-500 bg-violet-50 text-violet-950 hover:bg-violet-100 hover:border-violet-600 shadow-sm shadow-violet-100/60'
});

const TextoRutaAt2 = ({ origen, destino }) => (
  <span className="inline-flex items-center justify-center gap-4 sm:gap-10 max-w-full text-center leading-snug">
    <span className="font-semibold">{origen}</span>
    <span className="font-normal opacity-70 shrink-0 px-0.5 sm:px-1" aria-hidden="true">
      a
    </span>
    <span className="font-semibold">{destino}</span>
  </span>
);

const BotonRutaAt2 = ({ children, origen, destino, zona, onClick, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    className={`w-full flex items-center justify-center rounded-2xl font-medium transition-colors duration-200 py-4 px-3 sm:px-5 min-h-[3.25rem] ${ESTILOS_ZONA_RUTA_AT2[zona] || ESTILOS_ZONA_RUTA_AT2.aire}`}
  >
    {origen && destino ? <TextoRutaAt2 origen={origen} destino={destino} /> : children}
  </button>
);

const SelectConAgregar = ({
  titulo,
  descripcion,
  opciones,
  valor,
  onValorChange,
  inputNuevo,
  onInputNuevoChange,
  onAnadir,
  placeholderNuevo,
  etiquetaAnadir,
  textoBotonGuardar = 'Guardar en la lista y seleccionar'
}) => (
  <div className="space-y-4">
    <div>
      <p className="text-xl sm:text-2xl font-bold text-gray-900">{titulo}</p>
      {descripcion && <p className="text-sm text-gray-600 mt-1">{descripcion}</p>}
    </div>

    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Seleccione</label>
      <select
        value={valor === '' ? '' : valor}
        onChange={(e) => onValorChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
      >
        <option value="">Seleccione una opción…</option>
        <option value={ADD_SENTINEL}>{etiquetaAnadir}</option>
        {opciones.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
    </div>

    {valor === ADD_SENTINEL && (
      <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/60 p-4 space-y-3">
        <p className="text-sm font-medium text-indigo-900">Agregar</p>
        <input
          type="text"
          value={inputNuevo}
          onChange={(e) => onInputNuevoChange(e.target.value)}
          placeholder={placeholderNuevo}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="button"
          onClick={onAnadir}
          disabled={!inputNuevo.trim()}
          className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {textoBotonGuardar}
        </button>
      </div>
    )}
  </div>
);

const BloqueSiNoCantidad = ({ titulo, tiene, setTiene, cantidad, setCantidad, placeholderCantidad }) => (
  <div className="space-y-3">
    <p className="text-base sm:text-lg font-semibold text-gray-900">{titulo}</p>
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => setTiene(true)}
        className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
          tiene === true
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
      >
        Sí
      </button>
      <button
        type="button"
        onClick={() => {
          setTiene(false);
          setCantidad('');
        }}
        className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
          tiene === false
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
      >
        No
      </button>
      {tiene === true && (
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={cantidad}
          onChange={(e) => setCantidad(soloDigitosMax(e.target.value, SALIDA_CANTIDAD_SVC_DIGITOS_MAX))}
          className="flex-1 min-w-[7.5rem] max-w-[11rem] rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          placeholder={placeholderCantidad}
          aria-label={placeholderCantidad}
        />
      )}
    </div>
  </div>
);

const selCatalogoValido = (valor) => Boolean(valor && valor !== ADD_SENTINEL);

const formatSiNoCantidad = (tiene, cantidad) => {
  if (tiene === true) {
    const n = String(cantidad || '').replace(/\D/g, '');
    return n ? `Sí (${n})` : 'Sí';
  }
  if (tiene === false) return 'No';
  return '—';
};

const formatTipoCantidad = (tipo, cantidad, etiquetas) => {
  if (!tipo || tipo === 'no') return 'No';
  const n = String(cantidad || '').replace(/\D/g, '');
  const lbl = etiquetas[tipo] || tipo;
  return n ? `${lbl} (${n})` : lbl;
};

/** Tres opciones (p. ej. plástico / madera / no) en un bloque. */
const BloqueRadioOpcionesAt2 = ({ titulo, valor, setValor, opciones }) => (
  <div className="space-y-3">
    <p className="text-base sm:text-lg font-semibold text-gray-900">{titulo}</p>
    <div className="flex flex-wrap gap-2">
      {opciones.map((op) => (
        <button
          key={op.value}
          type="button"
          onClick={() => setValor(op.value)}
          className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
            valor === op.value
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {op.label}
        </button>
      ))}
    </div>
  </div>
);

const OPCIONES_MATERIAL_OBS_R = [
  { value: 'plastico', label: 'Con plástico' },
  { value: 'madera', label: 'Con madera' },
  { value: 'no', label: 'No las lleva' }
];

/** Vacío/lleno (o vacía/llena) + cantidad en un solo bloque. */
const BloqueTipoCantidadAt2 = ({ titulo, tipo, setTipo, cantidad, setCantidad, opciones, placeholderCantidad }) => {
  const muestraCantidad = tipo && tipo !== 'no';
  return (
    <div className="space-y-3">
      <p className="text-base sm:text-lg font-semibold text-gray-900">{titulo}</p>
      <div className="flex flex-wrap items-center gap-2">
        {opciones.map((op) => (
          <button
            key={op.value}
            type="button"
            onClick={() => {
              setTipo(op.value);
              if (op.value === 'no') setCantidad('');
            }}
            className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
              tipo === op.value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {op.label}
          </button>
        ))}
        {muestraCantidad && (
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={cantidad}
            onChange={(e) => setCantidad(soloDigitosMax(e.target.value, SALIDA_CANTIDAD_SVC_DIGITOS_MAX))}
            className="flex-1 min-w-[7.5rem] max-w-[11rem] rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            placeholder={placeholderCantidad}
            aria-label={placeholderCantidad}
          />
        )}
      </div>
    </div>
  );
};

const BloqueFotosMultiAt2 = ({
  destino,
  useDeviceCamera,
  previewUrls,
  previewAltBase,
  maxCount,
  procesandoFoto,
  cameraAbierta,
  cameraDestinoActivo,
  videoRef,
  canvasRef,
  onAbrirCapturaMovil,
  onIniciarCamaraEscritorio,
  onAbrirArchivoEscritorio,
  onCapturarFotograma,
  onCerrarCamara,
  onQuitarEnIndice
}) => {
  const urls = Array.isArray(previewUrls) ? previewUrls : [];
  const n = urls.length;
  const puedeMas = n < maxCount && !procesandoFoto;
  const showDesktopLive = !useDeviceCamera && cameraAbierta && cameraDestinoActivo === destino;

  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-gray-700">
        Fotos en este paso: <span className="text-indigo-700">{n}</span> / {maxCount}. Se pueden adjuntar máximo{' '}
        {maxCount} fotos.
      </p>

      {procesandoFoto ? (
        <div
          className="flex flex-col items-center justify-center gap-3 py-6 rounded-lg border border-indigo-100 bg-indigo-50/80"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div
            className="h-10 w-10 rounded-full border-[3px] border-indigo-200 border-t-indigo-600 animate-spin"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-indigo-800">Subiendo foto…</p>
          <p className="text-xs text-gray-600 text-center px-4">Espere un momento mientras se procesa la imagen.</p>
        </div>
      ) : null}

      {n >= maxCount && !showDesktopLive && !procesandoFoto ? (
        <p className="text-sm font-medium text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Listo: ya completaste las {maxCount} fotos permitidas en este paso.
        </p>
      ) : null}

      {!showDesktopLive && n > 0 && (
        <div className="flex flex-wrap gap-2">
          {urls.map((url, i) => (
            <div key={`${url}-${i}`} className="relative inline-block">
              <img
                src={url}
                alt={`${previewAltBase} ${i + 1}`}
                className="h-24 w-auto max-w-[140px] rounded-lg border object-cover bg-gray-50"
              />
              <button
                type="button"
                onClick={() => onQuitarEnIndice(destino, i)}
                disabled={procesandoFoto}
                className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-red-600 text-white text-sm font-bold shadow hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Quitar foto"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {!showDesktopLive && !procesandoFoto && (
        <div className="flex flex-col items-center justify-center gap-3 py-4">
          {useDeviceCamera ? (
            <>
              <button
                type="button"
                onClick={() => onAbrirCapturaMovil(destino)}
                disabled={!puedeMas}
                className="w-full max-w-sm px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-medium text-base hover:bg-indigo-700 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
              >
                {n === 0 ? 'Tomar foto' : 'Tomar otra foto'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onIniciarCamaraEscritorio(destino)}
                disabled={!puedeMas}
                className="w-full max-w-sm px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-medium text-base hover:bg-indigo-700 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
              >
                Agregar foto
              </button>
              <button
                type="button"
                onClick={() => onAbrirArchivoEscritorio(destino)}
                disabled={!puedeMas}
                className="w-full max-w-sm px-6 py-3.5 rounded-xl border-2 border-indigo-600 text-indigo-700 font-medium text-base hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                {n === 0 ? 'Subir desde archivos' : 'Agregar otra desde archivos'}
              </button>
            </>
          )}
        </div>
      )}

      {showDesktopLive && (
        <>
          <div className="relative rounded-lg overflow-hidden bg-black mb-3" style={{ minHeight: 280 }}>
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
            <canvas ref={canvasRef} className="hidden" />
            <button
              type="button"
              onClick={() => onCapturarFotograma(destino)}
              disabled={procesandoFoto}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Capturar foto
            </button>
          </div>
          <button type="button" onClick={onCerrarCamara} className="text-sm text-gray-600 hover:text-gray-800">
            Cerrar cámara
          </button>
        </>
      )}

    </div>
  );
};

const AlfaTango2 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [vista, setVista] = useState(VISTA_INICIO);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [puedeBitacora, setPuedeBitacora] = useState(null);
  const [revisionTipo, setRevisionTipo] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [aerolineas, setAerolineas] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [mulas, setMulas] = useState([]);
  const [tractomulas, setTractomulas] = useState([]);
  const [acompanantes, setAcompanantes] = useState([]);
  /** Listas locales distintas: Operadores ≠ Sierra Oscar (ingreso/salida Aire↔Distrib). */
  const [operadores, setOperadores] = useState([]);
  const [sierroOscar, setSierroOscar] = useState([]);
  const [jacPalets, setJacPalets] = useState([]);
  const [montacargas, setMontacargas] = useState([]);
  const [inputNuevaAerolinea, setInputNuevaAerolinea] = useState('');
  const [inputNuevoChofer, setInputNuevoChofer] = useState('');
  const [inputNuevaUnidad, setInputNuevaUnidad] = useState('');
  const [inputNuevaMula, setInputNuevaMula] = useState('');
  const [inputNuevaTractomula, setInputNuevaTractomula] = useState('');
  const [inputNuevoAcompanante, setInputNuevoAcompanante] = useState('');

  const [cargaPaso, setCargaPaso] = useState('unidad');
  const [ingresoAirePaso, setIngresoAirePaso] = useState('vehiculo');
  const [ingresoVehiculoTipo, setIngresoVehiculoTipo] = useState('camion');
  const [ingresoPersonalTipo, setIngresoPersonalTipo] = useState('operadores');

  const [salidaPaso, setSalidaPaso] = useState('camion');
  const [salidaConductorNombre, setSalidaConductorNombre] = useState('');
  const [salidaDollyQty, setSalidaDollyQty] = useState('');
  const [salidaCarretasQty, setSalidaCarretasQty] = useState('');
  const [salidaServiciosAdicionales, setSalidaServiciosAdicionales] = useState(null);
  const [salidaCantidadServicios, setSalidaCantidadServicios] = useState('');
  const [salidaSvcCodigos, setSalidaSvcCodigos] = useState([]);

  const [choferSel, setChoferSel] = useState('');
  const [aerolineaSel, setAerolineaSel] = useState('');
  const [unidadSel, setUnidadSel] = useState('');
  const [cargaCamionSel, setCargaCamionSel] = useState('');
  const [inputNuevaCargaCamion, setInputNuevaCargaCamion] = useState('');
  const [ingresoCamionSel, setIngresoCamionSel] = useState('');
  const [ingresoMulaSel, setIngresoMulaSel] = useState('');
  const [ingresoTractomulaSel, setIngresoTractomulaSel] = useState('');
  const [ingresoOperadorSierroSel, setIngresoOperadorSierroSel] = useState('');
  const [ingresoOperadorOscarSel, setIngresoOperadorOscarSel] = useState('');
  const [inputNuevoOperadorSierro, setInputNuevoOperadorSierro] = useState('');
  const [inputNuevoOperadorOscar, setInputNuevoOperadorOscar] = useState('');
  const [ingresoOperadorSinDenominativo, setIngresoOperadorSinDenominativo] = useState('');
  const [ingresoObservaciones, setIngresoObservaciones] = useState('');
  const [ingresoAcompananteSel, setIngresoAcompananteSel] = useState('');
  const [ingresoAerolineaSel, setIngresoAerolineaSel] = useState('');
  const [ingresoNumeroVuelo, setIngresoNumeroVuelo] = useState('');
  const [ingresoTieneContenedores, setIngresoTieneContenedores] = useState(null);
  const [ingresoCantidadContenedores, setIngresoCantidadContenedores] = useState('');
  const [ingresoTieneCanetas, setIngresoTieneCanetas] = useState(null);
  const [ingresoCantidadCanetas, setIngresoCantidadCanetas] = useState('');
  const [ingresoTienePalets, setIngresoTienePalets] = useState(null);
  const [ingresoCantidadPalets, setIngresoCantidadPalets] = useState('');
  const [ingresoTieneCorreos, setIngresoTieneCorreos] = useState(null);
  const [ingresoCantidadCorreos, setIngresoCantidadCorreos] = useState('');
  const [salidaDistPaso, setSalidaDistPaso] = useState('vehiculo');
  const [salidaDistVehiculoTipo, setSalidaDistVehiculoTipo] = useState('camion');
  const [salidaDistPersonalTipo, setSalidaDistPersonalTipo] = useState('operadores');
  const [salidaDistCamionSel, setSalidaDistCamionSel] = useState('');
  const [inputNuevaSalidaDistCamion, setInputNuevaSalidaDistCamion] = useState('');
  const [salidaDistTipoUnidad, setSalidaDistTipoUnidad] = useState('');
  const [salidaDistUnidadSel, setSalidaDistUnidadSel] = useState('');
  const [inputNuevaSalidaDistMula, setInputNuevaSalidaDistMula] = useState('');
  const [inputNuevaSalidaDistTractomula, setInputNuevaSalidaDistTractomula] = useState('');
  const [salidaDistOperadorSierroSel, setSalidaDistOperadorSierroSel] = useState('');
  const [salidaDistOperadorOscarSel, setSalidaDistOperadorOscarSel] = useState('');
  const [inputNuevoSalidaDistSierro, setInputNuevoSalidaDistSierro] = useState('');
  const [inputNuevoSalidaDistOscar, setInputNuevoSalidaDistOscar] = useState('');
  const [salidaDistAcompananteSel, setSalidaDistAcompananteSel] = useState('');
  const [inputNuevoSalidaDistAcompanante, setInputNuevoSalidaDistAcompanante] = useState('');
  const [salidaDistOperadorSinDen, setSalidaDistOperadorSinDen] = useState('');
  const [salidaDistDolisVacios, setSalidaDistDolisVacios] = useState(null);
  const [salidaDistCantDolisVacios, setSalidaDistCantDolisVacios] = useState('');
  const [salidaDistCanetasVacias, setSalidaDistCanetasVacias] = useState(null);
  const [salidaDistCantCanetasVacias, setSalidaDistCantCanetasVacias] = useState('');
  const [salidaDistObservaciones, setSalidaDistObservaciones] = useState('');
  const [salidaDistServiciosEspeciales, setSalidaDistServiciosEspeciales] = useState(null);
  const [salidaDistCantidadServicios, setSalidaDistCantidadServicios] = useState('');
  const [salidaDistObservacionesServicios, setSalidaDistObservacionesServicios] = useState([]);
  const [salidaXferPaso, setSalidaXferPaso] = useState('vehiculo');
  const [salidaXferVehiculoTipo, setSalidaXferVehiculoTipo] = useState('camion');
  const [salidaXferPersonalTipo, setSalidaXferPersonalTipo] = useState('operadores');
  const [salidaXferCamionSel, setSalidaXferCamionSel] = useState('');
  const [inputNuevaSalidaXferCamion, setInputNuevaSalidaXferCamion] = useState('');
  const [salidaXferTipoUnidad, setSalidaXferTipoUnidad] = useState('');
  const [salidaXferUnidadSel, setSalidaXferUnidadSel] = useState('');
  const [inputNuevaSalidaXferMula, setInputNuevaSalidaXferMula] = useState('');
  const [inputNuevaSalidaXferTractomula, setInputNuevaSalidaXferTractomula] = useState('');
  const [salidaXferOperadorSierroSel, setSalidaXferOperadorSierroSel] = useState('');
  const [salidaXferOperadorOscarSel, setSalidaXferOperadorOscarSel] = useState('');
  const [inputNuevoSalidaXferSierro, setInputNuevoSalidaXferSierro] = useState('');
  const [inputNuevoSalidaXferOscar, setInputNuevoSalidaXferOscar] = useState('');
  const [salidaXferAcompananteSel, setSalidaXferAcompananteSel] = useState('');
  const [inputNuevoSalidaXferAcompanante, setInputNuevoSalidaXferAcompanante] = useState('');
  const [salidaXferOperadorSinDen, setSalidaXferOperadorSinDen] = useState('');
  const [salidaXferDolisVacios, setSalidaXferDolisVacios] = useState(null);
  const [salidaXferCantDolisVacios, setSalidaXferCantDolisVacios] = useState('');
  const [salidaXferCanetasVacias, setSalidaXferCanetasVacias] = useState(null);
  const [salidaXferCantCanetasVacias, setSalidaXferCantCanetasVacias] = useState('');
  const [salidaXferObservaciones, setSalidaXferObservaciones] = useState('');
  const [salidaXferServiciosEspeciales, setSalidaXferServiciosEspeciales] = useState(null);
  const [salidaXferCantidadServicios, setSalidaXferCantidadServicios] = useState('');
  const [salidaXferObservacionesServicios, setSalidaXferObservacionesServicios] = useState([]);
  const [salidaAlfaPaso, setSalidaAlfaPaso] = useState('operadores');
  const [salidaAlfaVehiculoTipo, setSalidaAlfaVehiculoTipo] = useState('camion');
  const [salidaAlfaCamionSel, setSalidaAlfaCamionSel] = useState('');
  const [inputNuevaSalidaAlfaCamion, setInputNuevaSalidaAlfaCamion] = useState('');
  const [salidaAlfaMulaSel, setSalidaAlfaMulaSel] = useState('');
  const [inputNuevaSalidaAlfaMula, setInputNuevaSalidaAlfaMula] = useState('');
  const [salidaAlfaOperadorSel, setSalidaAlfaOperadorSel] = useState('');
  const [inputNuevoSalidaAlfaOperador, setInputNuevoSalidaAlfaOperador] = useState('');
  const [salidaAlfaJacpaletSel, setSalidaAlfaJacpaletSel] = useState('');
  const [inputNuevaSalidaAlfaJacpalet, setInputNuevaSalidaAlfaJacpalet] = useState('');
  const [salidaAlfaMontacargasSel, setSalidaAlfaMontacargasSel] = useState('');
  const [inputNuevaSalidaAlfaMontacargas, setInputNuevaSalidaAlfaMontacargas] = useState('');
  const [salidaAlfaJacpaletManual, setSalidaAlfaJacpaletManual] = useState('');
  const [salidaAlfaDolisTipo, setSalidaAlfaDolisTipo] = useState('');
  const [salidaAlfaCantDolis, setSalidaAlfaCantDolis] = useState('');
  const [salidaAlfaCanetasTipo, setSalidaAlfaCanetasTipo] = useState('');
  const [salidaAlfaCantCanetas, setSalidaAlfaCantCanetas] = useState('');
  const [salidaAlfaObservaciones, setSalidaAlfaObservaciones] = useState('');
  const [ingresoAlfaPaso, setIngresoAlfaPaso] = useState('operadores');
  const [ingresoAlfaVehiculoTipo, setIngresoAlfaVehiculoTipo] = useState('camion');
  const [ingresoAlfaCamionSel, setIngresoAlfaCamionSel] = useState('');
  const [inputNuevaIngresoAlfaCamion, setInputNuevaIngresoAlfaCamion] = useState('');
  const [ingresoAlfaMulaSel, setIngresoAlfaMulaSel] = useState('');
  const [inputNuevaIngresoAlfaMula, setInputNuevaIngresoAlfaMula] = useState('');
  const [ingresoAlfaTractomulaSel, setIngresoAlfaTractomulaSel] = useState('');
  const [ingresoAlfaOperadorSel, setIngresoAlfaOperadorSel] = useState('');
  const [inputNuevoIngresoAlfaOperador, setInputNuevoIngresoAlfaOperador] = useState('');
  const [ingresoAlfaJacpaletSel, setIngresoAlfaJacpaletSel] = useState('');
  const [inputNuevaIngresoAlfaJacpalet, setInputNuevaIngresoAlfaJacpalet] = useState('');
  const [ingresoAlfaMontacargasSel, setIngresoAlfaMontacargasSel] = useState('');
  const [inputNuevaIngresoAlfaMontacargas, setInputNuevaIngresoAlfaMontacargas] = useState('');
  const [ingresoAlfaJacpaletManual, setIngresoAlfaJacpaletManual] = useState('');
  const [ingresoAlfaDolisTipo, setIngresoAlfaDolisTipo] = useState('');
  const [ingresoAlfaCantDolis, setIngresoAlfaCantDolis] = useState('');
  const [ingresoAlfaCanetasTipo, setIngresoAlfaCanetasTipo] = useState('');
  const [ingresoAlfaCantCanetas, setIngresoAlfaCantCanetas] = useState('');
  const [ingresoAlfaObservaciones, setIngresoAlfaObservaciones] = useState('');
  const [salidaRPaso, setSalidaRPaso] = useState('personal');
  const [salidaRPersonalTipo, setSalidaRPersonalTipo] = useState('operador');
  const [salidaROperadorSel, setSalidaROperadorSel] = useState('');
  const [inputNuevoSalidaROperador, setInputNuevoSalidaROperador] = useState('');
  const [salidaREstrivador, setSalidaREstrivador] = useState('');
  const [salidaRSierroOscarSel, setSalidaRSierroOscarSel] = useState('');
  const [inputNuevoSalidaRSierroOscar, setInputNuevoSalidaRSierroOscar] = useState('');
  const [salidaRTipoUnidad, setSalidaRTipoUnidad] = useState('mula');
  const [salidaRUnidadSel, setSalidaRUnidadSel] = useState('');
  const [inputNuevaSalidaRMula, setInputNuevaSalidaRMula] = useState('');
  const [inputNuevaSalidaRMontacargas, setInputNuevaSalidaRMontacargas] = useState('');
  const [salidaRDolisObs, setSalidaRDolisObs] = useState('');
  const [salidaRCanetasObs, setSalidaRCanetasObs] = useState('');
  const [salidaRObservacionTexto, setSalidaRObservacionTexto] = useState('');
  const [fotoFiles, setFotoFiles] = useState(() => buildEmptyFotoMap());
  const [fotoPreview, setFotoPreview] = useState(() => buildEmptyFotoMap());
  /** Destino (slot) con imagen en compresión; bloquea nuevas capturas en ese bloque. */
  const [fotoProcesandoDestino, setFotoProcesandoDestino] = useState(null);
  const fotoFilesRef = useRef(fotoFiles);
  const fotoProcesandoRef = useRef(null);
  useEffect(() => {
    fotoFilesRef.current = fotoFiles;
  }, [fotoFiles]);

  const [cameraAbierta, setCameraAbierta] = useState(false);
  const [cameraDestinoActivo, setCameraDestinoActivo] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const inputFotoMobileRef = useRef(null);
  const inputFotoDesktopRef = useRef(null);
  const fotoDestinoRef = useRef('carga_elementos');
  const useDeviceCamera = isMobileDeviceForCamera();

  useEffect(() => {
    if (user && user.rol !== 'V') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.id_generado) return;
    const url = new URL(getApiUrl('BITACORA', 'STATUS'));
    url.searchParams.set('idGenerado', String(user.id_generado));
    url.searchParams.set('username', user?.username || '');
    url.searchParams.set('appid', user?.appid || '');
    fetch(url.toString(), { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d && d.success && d.data) setPuedeBitacora(d.data.puedeBitacora || 'NO');
        else setPuedeBitacora('NO');
      })
      .catch(() => setPuedeBitacora('NO'));
  }, [user?.id_generado, user?.username, user?.appid]);

  useEffect(() => {
    if (!user?.id_generado) return;
    const url = new URL(getApiUrl('SUPERVISION', 'CATALOGO'));
    url.searchParams.set('id_generado', String(user.id_generado));
    fetch(url.toString(), { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (!data?.success || !Array.isArray(data.data)) return;
        const rows = data.data;
        const aeros = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_AEROLINEA')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const chofs = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'CONDUCTOR')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const unids = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'VEHICULO')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const mulasCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_MULA')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const tractosCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_TRACTOMULA')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const operCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_OPERADOR')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const sierroCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_SIERRO_OSCAR')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const acompCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_ACOMPANANTE')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const montacargasCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_MONTACARGAS')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        const jacPaletsCat = rows
          .filter((r) => String(r.tipo || '').toUpperCase() === 'ALFA_TANGO_2_JAC_PALET')
          .map((r) => String(r.Dato || '').trim())
          .filter(Boolean);
        setAerolineas(Array.from(new Set(aeros)));
        setChoferes(Array.from(new Set(chofs)));
        setUnidades(Array.from(new Set(unids)));
        setMulas(Array.from(new Set(mulasCat)));
        setTractomulas(Array.from(new Set(tractosCat)));
        setOperadores(Array.from(new Set(operCat)));
        setSierroOscar(Array.from(new Set(sierroCat)));
        setAcompanantes(Array.from(new Set(acompCat)));
        setMontacargas(Array.from(new Set(montacargasCat)));
        setJacPalets(Array.from(new Set(jacPaletsCat)));
      })
      .catch(() => {});
  }, [user?.id_generado]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraAbierta(false);
    setCameraDestinoActivo(null);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    if (!cameraAbierta || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
  }, [cameraAbierta]);

  const revokeAllPreviews = useCallback(() => {
    Object.values(fotoPreview).forEach((arr) => {
      (arr || []).forEach((u) => {
        if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
      });
    });
  }, [fotoPreview]);

  const resetFlujoForms = useCallback(() => {
    revokeAllPreviews();
    setFotoFiles(buildEmptyFotoMap());
    setFotoPreview(buildEmptyFotoMap());
    fotoProcesandoRef.current = null;
    setFotoProcesandoDestino(null);
    setCargaPaso('unidad');
    setIngresoAirePaso('vehiculo');
    setIngresoVehiculoTipo('camion');
    setIngresoPersonalTipo('operadores');
    setSalidaPaso('camion');
    setSalidaConductorNombre('');
    setSalidaDollyQty('');
    setSalidaCarretasQty('');
    setSalidaServiciosAdicionales(null);
    setSalidaCantidadServicios('');
    setSalidaSvcCodigos([]);
    setChoferSel('');
    setAerolineaSel('');
    setUnidadSel('');
    setCargaCamionSel('');
    setInputNuevaCargaCamion('');
    setInputNuevaMula('');
    setInputNuevaTractomula('');
    setInputNuevoAcompanante('');
    setIngresoCamionSel('');
    setIngresoMulaSel('');
    setIngresoTractomulaSel('');
    setIngresoOperadorSierroSel('');
    setIngresoOperadorOscarSel('');
    setInputNuevoOperadorSierro('');
    setInputNuevoOperadorOscar('');
    setIngresoOperadorSinDenominativo('');
    setIngresoObservaciones('');
    setIngresoAcompananteSel('');
    setIngresoAerolineaSel('');
    setIngresoNumeroVuelo('');
    setIngresoTieneContenedores(null);
    setIngresoCantidadContenedores('');
    setIngresoTieneCanetas(null);
    setIngresoCantidadCanetas('');
    setIngresoTienePalets(null);
    setIngresoCantidadPalets('');
    setIngresoTieneCorreos(null);
    setIngresoCantidadCorreos('');
    setSalidaDistPaso('vehiculo');
    setSalidaDistVehiculoTipo('camion');
    setSalidaDistPersonalTipo('operadores');
    setSalidaDistCamionSel('');
    setInputNuevaSalidaDistCamion('');
    setSalidaDistTipoUnidad('');
    setSalidaDistUnidadSel('');
    setInputNuevaSalidaDistMula('');
    setInputNuevaSalidaDistTractomula('');
    setSalidaDistOperadorSierroSel('');
    setSalidaDistOperadorOscarSel('');
    setInputNuevoSalidaDistSierro('');
    setInputNuevoSalidaDistOscar('');
    setSalidaDistAcompananteSel('');
    setInputNuevoSalidaDistAcompanante('');
    setSalidaDistOperadorSinDen('');
    setSalidaDistDolisVacios(null);
    setSalidaDistCantDolisVacios('');
    setSalidaDistCanetasVacias(null);
    setSalidaDistCantCanetasVacias('');
    setSalidaDistObservaciones('');
    setSalidaDistServiciosEspeciales(null);
    setSalidaDistCantidadServicios('');
    setSalidaDistObservacionesServicios([]);
    setSalidaXferPaso('vehiculo');
    setSalidaXferVehiculoTipo('camion');
    setSalidaXferPersonalTipo('operadores');
    setSalidaXferCamionSel('');
    setInputNuevaSalidaXferCamion('');
    setSalidaXferTipoUnidad('');
    setSalidaXferUnidadSel('');
    setInputNuevaSalidaXferMula('');
    setInputNuevaSalidaXferTractomula('');
    setSalidaXferOperadorSierroSel('');
    setSalidaXferOperadorOscarSel('');
    setInputNuevoSalidaXferSierro('');
    setInputNuevoSalidaXferOscar('');
    setSalidaXferAcompananteSel('');
    setInputNuevoSalidaXferAcompanante('');
    setSalidaXferOperadorSinDen('');
    setSalidaXferDolisVacios(null);
    setSalidaXferCantDolisVacios('');
    setSalidaXferCanetasVacias(null);
    setSalidaXferCantCanetasVacias('');
    setSalidaXferObservaciones('');
    setSalidaXferServiciosEspeciales(null);
    setSalidaXferCantidadServicios('');
    setSalidaXferObservacionesServicios([]);
    setSalidaAlfaPaso('operadores');
    setSalidaAlfaVehiculoTipo('camion');
    setSalidaAlfaCamionSel('');
    setInputNuevaSalidaAlfaCamion('');
    setSalidaAlfaMulaSel('');
    setInputNuevaSalidaAlfaMula('');
    setSalidaAlfaOperadorSel('');
    setInputNuevoSalidaAlfaOperador('');
    setSalidaAlfaJacpaletSel('');
    setInputNuevaSalidaAlfaJacpalet('');
    setSalidaAlfaMontacargasSel('');
    setInputNuevaSalidaAlfaMontacargas('');
    setSalidaAlfaJacpaletManual('');
    setSalidaAlfaDolisTipo('');
    setSalidaAlfaCantDolis('');
    setSalidaAlfaCanetasTipo('');
    setSalidaAlfaCantCanetas('');
    setSalidaAlfaObservaciones('');
    setIngresoAlfaPaso('operadores');
    setIngresoAlfaVehiculoTipo('camion');
    setIngresoAlfaCamionSel('');
    setInputNuevaIngresoAlfaCamion('');
    setIngresoAlfaMulaSel('');
    setInputNuevaIngresoAlfaMula('');
    setIngresoAlfaTractomulaSel('');
    setIngresoAlfaOperadorSel('');
    setInputNuevoIngresoAlfaOperador('');
    setIngresoAlfaJacpaletSel('');
    setInputNuevaIngresoAlfaJacpalet('');
    setIngresoAlfaMontacargasSel('');
    setInputNuevaIngresoAlfaMontacargas('');
    setIngresoAlfaJacpaletManual('');
    setIngresoAlfaDolisTipo('');
    setIngresoAlfaCantDolis('');
    setIngresoAlfaCanetasTipo('');
    setIngresoAlfaCantCanetas('');
    setIngresoAlfaObservaciones('');
    setSalidaRPaso('personal');
    setSalidaRPersonalTipo('operador');
    setSalidaROperadorSel('');
    setInputNuevoSalidaROperador('');
    setSalidaREstrivador('');
    setSalidaRSierroOscarSel('');
    setInputNuevoSalidaRSierroOscar('');
    setSalidaRTipoUnidad('mula');
    setSalidaRUnidadSel('');
    setInputNuevaSalidaRMula('');
    setInputNuevaSalidaRMontacargas('');
    setSalidaRDolisObs('');
    setSalidaRCanetasObs('');
    setSalidaRObservacionTexto('');
    setRevisionTipo(null);
    setShowConfirmModal(false);
  }, [revokeAllPreviews]);

  const startCameraDesktop = useCallback(
    async (dest) => {
      stopCamera();
      setError('');
      fotoDestinoRef.current = dest;
      setCameraDestinoActivo(dest);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        streamRef.current = stream;
        setCameraAbierta(true);
      } catch (_) {
        setError('No se pudo acceder a la cámara.');
        setCameraDestinoActivo(null);
      }
    },
    [stopCamera]
  );

  const captureFromVideo = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.srcObject) return Promise.resolve(null);
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], 'captura.jpg', { type: 'image/jpeg' }));
          else resolve(null);
        },
        'image/jpeg',
        JPEG_QUALITY_AT2
      );
    });
  }, []);

  const appendFotoCompressed = useCallback(async (dest, file) => {
    if (!dest || !file) return;
    if (fotoProcesandoRef.current === dest) return;
    setError('');
    const cap = maxFotosPorDestinoAt2(dest);
    if ((fotoFilesRef.current[dest] || []).length >= cap) {
      setError(`Solo podés cargar hasta ${cap} foto(s) en este paso.`);
      return;
    }
    fotoProcesandoRef.current = dest;
    setFotoProcesandoDestino(dest);
    try {
      const compressed = await compressImage(file);
      setFotoFiles((prev) => {
        const cur = [...(prev[dest] || [])];
        if (cur.length >= cap) return prev;
        cur.push(compressed);
        return { ...prev, [dest]: cur };
      });
      setFotoPreview((prev) => {
        const cur = [...(prev[dest] || [])];
        if (cur.length >= cap) return prev;
        cur.push(URL.createObjectURL(compressed));
        return { ...prev, [dest]: cur };
      });
    } catch (_) {
      setError('Error al procesar la imagen.');
    } finally {
      if (fotoProcesandoRef.current === dest) fotoProcesandoRef.current = null;
      setFotoProcesandoDestino((actual) => (actual === dest ? null : actual));
    }
  }, []);

  const removeFotoAt = useCallback((dest, index) => {
    setFotoPreview((prev) => {
      const urls = [...(prev[dest] || [])];
      if (urls[index]) URL.revokeObjectURL(urls[index]);
      urls.splice(index, 1);
      return { ...prev, [dest]: urls };
    });
    setFotoFiles((prev) => {
      const fs = [...(prev[dest] || [])];
      fs.splice(index, 1);
      return { ...prev, [dest]: fs };
    });
  }, []);

  const handleFotoMobileChange = useCallback(
    async (e) => {
      const f = e.target.files?.[0];
      const dest = fotoDestinoRef.current;
      if (f && dest) await appendFotoCompressed(dest, f);
      e.target.value = '';
    },
    [appendFotoCompressed]
  );

  const handleFotoDesktopChange = useCallback(
    async (e) => {
      const f = e.target.files?.[0];
      const dest = fotoDestinoRef.current;
      if (f && dest) await appendFotoCompressed(dest, f);
      e.target.value = '';
    },
    [appendFotoCompressed]
  );

  const abrirCapturaMovil = useCallback((dest) => {
    setError('');
    fotoDestinoRef.current = dest;
    setCameraDestinoActivo(dest);
    inputFotoMobileRef.current?.click();
  }, []);

  const abrirArchivoEscritorio = useCallback(
    (dest) => {
      setError('');
      stopCamera();
      fotoDestinoRef.current = dest;
      setCameraDestinoActivo(dest);
      inputFotoDesktopRef.current?.click();
    },
    [stopCamera]
  );

  const capturarFotogramaDesktop = useCallback(
    async (dest) => {
      const file = await captureFromVideo();
      if (file) await appendFotoCompressed(dest, file);
      stopCamera();
    },
    [appendFotoCompressed, captureFromVideo, stopCamera]
  );

  const addToListAndSelect = useCallback((input, list, setList, setSelected, setInput) => {
    const t = input.trim();
    if (!t) return;
    const exists = list.some((x) => x.toLowerCase() === t.toLowerCase());
    if (!exists) setList((prev) => [...prev, t]);
    setSelected(t);
    setInput('');
  }, []);

  const saveAt2DatoInCatalogo = useCallback(
    async (dato, tipoCatalogo) => {
      if (!user?.id_generado) return;
      const datoTrim = String(dato || '').trim();
      if (!datoTrim) return;
      try {
        await fetch(getApiUrl('SUPERVISION', 'CATALOGO'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id_generado: user.id_generado,
            dato: datoTrim,
            descripcion: AT2_CATALOGO_DESCRIPCION[tipoCatalogo] || 'Alfa Tango 2',
            tipo: tipoCatalogo
          })
        });
      } catch (_) {
        // No bloquea el flujo: si falla catálogo, el usuario igual puede continuar.
      }
    },
    [user?.id_generado]
  );

  const addToListSelectAndCatalog = useCallback(
    async (input, list, setList, setSelected, setInput, tipoCatalogo) => {
      const t = String(input || '').trim();
      if (!t) return;
      addToListAndSelect(input, list, setList, setSelected, setInput);
      await saveAt2DatoInCatalogo(t, tipoCatalogo);
    },
    [addToListAndSelect, saveAt2DatoInCatalogo]
  );

  const irInicio = useCallback(() => {
    stopCamera();
    setVista(VISTA_INICIO);
    setError('');
    resetFlujoForms();
  }, [stopCamera, resetFlujoForms]);

  const openConfirmGuardar = useCallback(() => {
    setError('');
    if (puedeBitacora !== 'SI') {
      setError('La bitácora no está habilitada. Debe existir apertura de turno.');
      return;
    }
    setShowConfirmModal(true);
  }, [puedeBitacora]);

  const getFlujoBackend = useCallback((tipo) => {
    if (tipo === 'carga') return 'SALIDA_CARGA';
    if (tipo === 'salida_alfa2') return 'SALIDA_ALFA2';
    if (tipo === 'ingreso_aire_distrib') return 'INGRESO_AIRE_DISTRIB';
    if (tipo === 'ingreso_alfa_distrib') return 'INGRESO_ALFA_DISTRIB';
    if (tipo === 'salida_dist_aire') return 'SALIDA_DIST_AIRE';
    if (tipo === 'salida_dist_transferencia') return 'SALIDA_DIST_TRANSFERENCIA';
    if (tipo === 'salida_dist_r') return 'SALIDA_DIST_R';
    if (tipo === 'salida_dist_alfa') return 'SALIDA_DIST_ALFA';
    return '';
  }, []);

  const getSlotOrder = useCallback(
    (tipo) => {
      if (tipo === 'carga') return AT2_ORDER_CARGA;
      if (tipo === 'salida_alfa2') {
        const svcSi = salidaServiciosAdicionales === true;
        const nParsed = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
        const n = svcSi && Number.isFinite(nParsed) && nParsed >= 1 ? nParsed : 0;
        return buildSalidaSlotOrder(svcSi, n);
      }
      return [];
    },
    [salidaCantidadServicios, salidaServiciosAdicionales]
  );

  const buildResumenLineas = useCallback(
    (tipo) => {
      const n = (slot) => (fotoFiles[slot] || []).length;
      const lineas = [];
      if (tipo === 'carga') {
        lineas.push({
          k: 'Camión o equipo',
          v: cargaCamionSel && cargaCamionSel !== ADD_SENTINEL ? cargaCamionSel : '—'
        });
        lineas.push({ k: 'Chofer', v: choferSel && choferSel !== ADD_SENTINEL ? choferSel : '—' });
        lineas.push({ k: 'Aerolínea', v: aerolineaSel && aerolineaSel !== ADD_SENTINEL ? aerolineaSel : '—' });
        AT2_ORDER_CARGA.forEach((slot) => {
          lineas.push({ k: SLOT_LABELS[slot] || slot, v: `${n(slot)} foto(s)`, slot });
        });
      } else if (tipo === 'salida_alfa2') {
        lineas.push({ k: 'Número de Camión', v: unidadSel && unidadSel !== ADD_SENTINEL ? unidadSel : '—' });
        lineas.push({ k: 'Conductor', v: salidaConductorNombre.trim() || '—' });
        const dShow = String(salidaDollyQty || '').replace(/\D/g, '');
        const cShow = String(salidaCarretasQty || '').replace(/\D/g, '');
        lineas.push({ k: 'Dolly', v: dShow === '' ? '0' : dShow });
        lineas.push({ k: 'Carretas', v: cShow === '' ? '0' : cShow });
        lineas.push({
          k: 'Servicios adicionales',
          v: salidaServiciosAdicionales === true ? 'Sí' : salidaServiciosAdicionales === false ? 'No' : '—'
        });
        if (salidaServiciosAdicionales === true) {
          lineas.push({ k: 'Cant. servicios', v: String(salidaCantidadServicios || '—') });
          salidaSvcCodigos.forEach((c, idx) => {
            lineas.push({ k: `Servicio ${idx + 1} (código)`, v: c || '—' });
          });
        }
        const nRes = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
        const slotOrderRes = buildSalidaSlotOrder(
          salidaServiciosAdicionales === true,
          Number.isFinite(nRes) && nRes >= 1 ? nRes : 0
        );
        slotOrderRes.forEach((slot) => {
          lineas.push({ k: labelSalidaFotoSlot(slot), v: `${n(slot)} foto(s)`, slot });
        });
      } else if (tipo === 'ingreso_aire_distrib') {
        if (ingresoVehiculoTipo === 'camion') {
        lineas.push({
          k: 'Número de camión',
          v: selCatalogoValido(ingresoCamionSel) ? ingresoCamionSel : '—'
        });
        } else if (ingresoVehiculoTipo === 'mula') {
        lineas.push({ k: 'Número de mula', v: selCatalogoValido(ingresoMulaSel) ? ingresoMulaSel : '—' });
        } else if (ingresoVehiculoTipo === 'tractomula') {
        lineas.push({
          k: 'Número de tractomula',
          v: selCatalogoValido(ingresoTractomulaSel) ? ingresoTractomulaSel : '—'
        });
        }
        if (ingresoPersonalTipo === 'operadores') {
        lineas.push({
          k: 'Operadores',
          v: selCatalogoValido(ingresoOperadorSierroSel) ? ingresoOperadorSierroSel : '—'
        });
        } else if (ingresoPersonalTipo === 'sierra_oscar') {
        lineas.push({
            k: 'Sierra Oscar',
          v: selCatalogoValido(ingresoOperadorOscarSel) ? ingresoOperadorOscarSel : '—'
        });
        } else if (ingresoPersonalTipo === 'operador_sin_den') {
        lineas.push({
          k: 'Operadores sin denominativo',
          v: String(ingresoOperadorSinDenominativo || '').trim() || '—',
          multiline: true
        });
        } else if (ingresoPersonalTipo === 'acompanantes') {
        lineas.push({
          k: 'Acompañantes',
          v: selCatalogoValido(ingresoAcompananteSel) ? ingresoAcompananteSel : '—'
        });
        }
        lineas.push({
          k: 'Aerolínea',
          v: selCatalogoValido(ingresoAerolineaSel) ? ingresoAerolineaSel : '—'
        });
        lineas.push({ k: 'Número de vuelo', v: String(ingresoNumeroVuelo || '').trim() || '—' });
        lineas.push({
          k: 'Contenedores',
          v: formatSiNoCantidad(ingresoTieneContenedores, ingresoCantidadContenedores)
        });
        lineas.push({ k: 'Carretas', v: formatSiNoCantidad(ingresoTieneCanetas, ingresoCantidadCanetas) });
        lineas.push({ k: 'Palets', v: formatSiNoCantidad(ingresoTienePalets, ingresoCantidadPalets) });
        lineas.push({ k: 'Correos', v: formatSiNoCantidad(ingresoTieneCorreos, ingresoCantidadCorreos) });
        lineas.push({
          k: 'Observaciones',
          v: String(ingresoObservaciones || '').trim() || '—',
          multiline: true
        });
      } else if (tipo === 'salida_dist_aire') {
        if (salidaDistVehiculoTipo === 'camion') {
        lineas.push({
            k: 'Camión',
            v: selCatalogoValido(salidaDistCamionSel) ? salidaDistCamionSel : '—'
          });
        } else if (salidaDistVehiculoTipo === 'mula') {
          lineas.push({
            k: 'Mula',
          v: selCatalogoValido(salidaDistUnidadSel) ? salidaDistUnidadSel : '—'
        });
        } else if (salidaDistVehiculoTipo === 'tractomula') {
          lineas.push({
            k: 'Tractomula',
            v: selCatalogoValido(salidaDistUnidadSel) ? salidaDistUnidadSel : '—'
          });
        }
        if (salidaDistPersonalTipo === 'operadores') {
        lineas.push({
          k: 'Operadores',
          v: selCatalogoValido(salidaDistOperadorSierroSel) ? salidaDistOperadorSierroSel : '—'
        });
        } else if (salidaDistPersonalTipo === 'sierra_oscar') {
        lineas.push({
            k: 'Sierra Oscar',
          v: selCatalogoValido(salidaDistOperadorOscarSel) ? salidaDistOperadorOscarSel : '—'
        });
        } else if (salidaDistPersonalTipo === 'acompanantes') {
        lineas.push({
          k: 'Acompañamiento',
          v: selCatalogoValido(salidaDistAcompananteSel) ? salidaDistAcompananteSel : '—'
        });
        } else if (salidaDistPersonalTipo === 'operador_sin_den') {
        lineas.push({
          k: 'Operadores sin denominativo',
          v: String(salidaDistOperadorSinDen || '').trim() || '—',
          multiline: true
        });
        }
        lineas.push({
          k: 'Dolis vacíos',
          v: formatSiNoCantidad(salidaDistDolisVacios, salidaDistCantDolisVacios)
        });
        lineas.push({
          k: 'Carretas vacías',
          v: formatSiNoCantidad(salidaDistCanetasVacias, salidaDistCantCanetasVacias)
        });
        lineas.push({
          k: 'Observaciones',
          v: String(salidaDistObservaciones || '').trim() || '—',
          multiline: true
        });
        lineas.push({
          k: 'Servicios especiales',
          v: salidaDistServiciosEspeciales === true ? 'Sí' : salidaDistServiciosEspeciales === false ? 'No' : '—'
        });
        if (salidaDistServiciosEspeciales === true) {
          lineas.push({ k: 'Cant. servicios especiales', v: String(salidaDistCantidadServicios || '—') });
          const nServ = parseInt(String(salidaDistCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          const slots = buildSalidaDistSlotOrder(true, Number.isFinite(nServ) && nServ >= 1 ? nServ : 0);
          for (let i = 0; i < slots.length; i += 2) {
            const idx = Math.floor(i / 2);
            const slotGuia = slots[i];
            const slotBultos = slots[i + 1];
            lineas.push({ k: `Servicio ${idx + 1} guía`, v: `${n(slotGuia)} foto(s)`, slot: slotGuia });
            lineas.push({ k: `Servicio ${idx + 1} bultos`, v: `${n(slotBultos)} foto(s)`, slot: slotBultos });
          lineas.push({
              k: `Servicio ${idx + 1} observación`,
              v: String(salidaDistObservacionesServicios[idx] || '').trim() || '—',
              multiline: true
            });
          }
        }
      } else if (tipo === 'salida_dist_transferencia') {
        if (salidaXferVehiculoTipo === 'camion') {
          lineas.push({
            k: 'Camión',
            v: selCatalogoValido(salidaXferCamionSel) ? salidaXferCamionSel : '—'
          });
        } else if (salidaXferVehiculoTipo === 'mula') {
          lineas.push({
            k: 'Mula',
            v: selCatalogoValido(salidaXferUnidadSel) ? salidaXferUnidadSel : '—'
          });
        } else if (salidaXferVehiculoTipo === 'tractomula') {
          lineas.push({
            k: 'Tractomula',
            v: selCatalogoValido(salidaXferUnidadSel) ? salidaXferUnidadSel : '—'
          });
        }
        if (salidaXferPersonalTipo === 'operadores') {
          lineas.push({
            k: 'Operadores',
            v: selCatalogoValido(salidaXferOperadorSierroSel) ? salidaXferOperadorSierroSel : '—'
          });
        } else if (salidaXferPersonalTipo === 'sierra_oscar') {
          lineas.push({
            k: 'Sierra Oscar',
            v: selCatalogoValido(salidaXferOperadorOscarSel) ? salidaXferOperadorOscarSel : '—'
          });
        } else if (salidaXferPersonalTipo === 'acompanantes') {
          lineas.push({
            k: 'Acompañamiento',
            v: selCatalogoValido(salidaXferAcompananteSel) ? salidaXferAcompananteSel : '—'
          });
        } else if (salidaXferPersonalTipo === 'operador_sin_den') {
          lineas.push({
            k: 'Operadores sin denominativo',
            v: String(salidaXferOperadorSinDen || '').trim() || '—',
            multiline: true
          });
        }
        lineas.push({
          k: 'Dolis vacíos',
          v: formatSiNoCantidad(salidaXferDolisVacios, salidaXferCantDolisVacios)
        });
        lineas.push({
          k: 'Carretas vacías',
          v: formatSiNoCantidad(salidaXferCanetasVacias, salidaXferCantCanetasVacias)
        });
        lineas.push({
          k: 'Observaciones',
          v: String(salidaXferObservaciones || '').trim() || '—',
          multiline: true
        });
        lineas.push({
          k: 'Servicios especiales',
          v: salidaXferServiciosEspeciales === true ? 'Sí' : salidaXferServiciosEspeciales === false ? 'No' : '—'
        });
        if (salidaXferServiciosEspeciales === true) {
          lineas.push({ k: 'Cant. servicios especiales', v: String(salidaXferCantidadServicios || '—') });
          const nServX = parseInt(String(salidaXferCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          const slotsX = buildSalidaXferSlotOrder(true, Number.isFinite(nServX) && nServX >= 1 ? nServX : 0);
          for (let i = 0; i < slotsX.length; i += 2) {
            const idx = Math.floor(i / 2);
            const slotGuia = slotsX[i];
            const slotBultos = slotsX[i + 1];
            lineas.push({ k: `Servicio ${idx + 1} guía`, v: `${n(slotGuia)} foto(s)`, slot: slotGuia });
            lineas.push({ k: `Servicio ${idx + 1} bultos`, v: `${n(slotBultos)} foto(s)`, slot: slotBultos });
            lineas.push({
              k: `Servicio ${idx + 1} observación`,
              v: String(salidaXferObservacionesServicios[idx] || '').trim() || '—',
              multiline: true
            });
          }
        }
      } else if (tipo === 'salida_dist_alfa') {
        lineas.push({
          k: 'Operadores',
          v: selCatalogoValido(salidaAlfaOperadorSel) ? salidaAlfaOperadorSel : '—'
        });
        if (salidaAlfaVehiculoTipo === 'camion') {
        lineas.push({ k: 'Camión', v: selCatalogoValido(salidaAlfaCamionSel) ? salidaAlfaCamionSel : '—' });
        } else if (salidaAlfaVehiculoTipo === 'mula') {
        lineas.push({
          k: 'Mula',
          v: selCatalogoValido(salidaAlfaMulaSel) ? salidaAlfaMulaSel : '—'
        });
        } else if (salidaAlfaVehiculoTipo === 'montacargas') {
        lineas.push({
          k: 'Montacargas',
          v: selCatalogoValido(salidaAlfaMontacargasSel) ? salidaAlfaMontacargasSel : '—'
          });
        }
        lineas.push({
          k: 'JacPalet',
          v: selCatalogoValido(salidaAlfaJacpaletSel) ? salidaAlfaJacpaletSel : '—'
        });
        lineas.push({
          k: 'JacPalet manual',
          v: String(salidaAlfaJacpaletManual || '').trim() || '—'
        });
        lineas.push({
          k: 'Dolis',
          v: formatTipoCantidad(salidaAlfaDolisTipo, salidaAlfaCantDolis, {
            vacio: 'Vacíos',
            lleno: 'Llenos'
          })
        });
        lineas.push({
          k: 'Carretas',
          v: formatTipoCantidad(salidaAlfaCanetasTipo, salidaAlfaCantCanetas, {
            vacia: 'Vacías',
            llena: 'Llenas'
          })
        });
        lineas.push({
          k: 'Observaciones',
          v: String(salidaAlfaObservaciones || '').trim() || '—',
          multiline: true
        });
      } else if (tipo === 'ingreso_alfa_distrib') {
        lineas.push({
          k: 'Operadores',
          v: selCatalogoValido(ingresoAlfaOperadorSel) ? ingresoAlfaOperadorSel : '—'
        });
        if (ingresoAlfaVehiculoTipo === 'camion') {
          lineas.push({ k: 'Camión', v: selCatalogoValido(ingresoAlfaCamionSel) ? ingresoAlfaCamionSel : '—' });
        } else if (ingresoAlfaVehiculoTipo === 'mula') {
          lineas.push({
            k: 'Mula',
            v: selCatalogoValido(ingresoAlfaMulaSel) ? ingresoAlfaMulaSel : '—'
          });
        } else if (ingresoAlfaVehiculoTipo === 'tractomula') {
          lineas.push({
            k: 'Tractomula',
            v: selCatalogoValido(ingresoAlfaTractomulaSel) ? ingresoAlfaTractomulaSel : '—'
          });
        } else if (ingresoAlfaVehiculoTipo === 'montacargas') {
          lineas.push({
            k: 'Montacargas',
            v: selCatalogoValido(ingresoAlfaMontacargasSel) ? ingresoAlfaMontacargasSel : '—'
          });
        }
        lineas.push({
          k: 'JacPalet',
          v: selCatalogoValido(ingresoAlfaJacpaletSel) ? ingresoAlfaJacpaletSel : '—'
        });
        lineas.push({
          k: 'JacPalet manual',
          v: String(ingresoAlfaJacpaletManual || '').trim() || '—'
        });
        lineas.push({
          k: 'Dolis',
          v: formatTipoCantidad(ingresoAlfaDolisTipo, ingresoAlfaCantDolis, {
            vacio: 'Vacíos',
            lleno: 'Llenos'
          })
        });
        lineas.push({
          k: 'Carretas',
          v: formatTipoCantidad(ingresoAlfaCanetasTipo, ingresoAlfaCantCanetas, {
            vacia: 'Vacías',
            llena: 'Llenas'
          })
        });
        lineas.push({
          k: 'Observaciones',
          v: String(ingresoAlfaObservaciones || '').trim() || '—',
          multiline: true
        });
      } else if (tipo === 'salida_dist_r') {
        if (salidaRPersonalTipo === 'operador') {
        lineas.push({
          k: 'Operador',
          v: selCatalogoValido(salidaROperadorSel) ? salidaROperadorSel : '—'
        });
        } else if (salidaRPersonalTipo === 'estivador') {
        lineas.push({
            k: 'Estivador',
          v: String(salidaREstrivador || '').trim() || '—'
        });
        } else if (salidaRPersonalTipo === 'sierra_oscar') {
        lineas.push({
            k: 'Sierra Oscar',
          v: selCatalogoValido(salidaRSierroOscarSel) ? salidaRSierroOscarSel : '—'
        });
        }
        lineas.push({
          k: salidaRTipoUnidad === 'mula' ? 'Mula' : salidaRTipoUnidad === 'montacargas' ? 'Montacargas' : 'Unidad',
          v: selCatalogoValido(salidaRUnidadSel) ? salidaRUnidadSel : '—'
        });
        lineas.push({
          k: 'Dolis',
          v: ETIQUETAS_MATERIAL_OBS_R[salidaRDolisObs] || '—'
        });
        lineas.push({
          k: 'Carretas',
          v: ETIQUETAS_MATERIAL_OBS_R[salidaRCanetasObs] || '—'
        });
        lineas.push({
          k: 'Observación',
          v: String(salidaRObservacionTexto || '').trim() || '—',
          multiline: true
        });
      }
      return lineas;
    },
    [
      aerolineaSel,
      cargaCamionSel,
      choferSel,
      fotoFiles,
      ingresoAcompananteSel,
      ingresoAerolineaSel,
      ingresoCamionSel,
      ingresoCantidadCanetas,
      ingresoCantidadContenedores,
      ingresoCantidadCorreos,
      ingresoCantidadPalets,
      ingresoMulaSel,
      ingresoNumeroVuelo,
      ingresoOperadorOscarSel,
      ingresoOperadorSierroSel,
      ingresoObservaciones,
      ingresoOperadorSinDenominativo,
      ingresoTieneCanetas,
      ingresoTieneContenedores,
      ingresoTieneCorreos,
      ingresoTienePalets,
      ingresoTractomulaSel,
      ingresoVehiculoTipo,
      ingresoPersonalTipo,
      ingresoAlfaCamionSel,
      ingresoAlfaCanetasTipo,
      ingresoAlfaCantCanetas,
      ingresoAlfaCantDolis,
      ingresoAlfaDolisTipo,
      ingresoAlfaJacpaletManual,
      ingresoAlfaJacpaletSel,
      ingresoAlfaMontacargasSel,
      ingresoAlfaMulaSel,
      ingresoAlfaObservaciones,
      ingresoAlfaOperadorSel,
      ingresoAlfaTractomulaSel,
      ingresoAlfaVehiculoTipo,
      salidaDistAcompananteSel,
      salidaDistCamionSel,
      salidaDistCanetasVacias,
      salidaDistCantCanetasVacias,
      salidaDistCantDolisVacios,
      salidaDistCantidadServicios,
      salidaDistDolisVacios,
      salidaDistObservaciones,
      salidaDistObservacionesServicios,
      salidaDistOperadorOscarSel,
      salidaDistOperadorSierroSel,
      salidaDistOperadorSinDen,
      salidaDistPersonalTipo,
      salidaDistServiciosEspeciales,
      salidaDistUnidadSel,
      salidaDistVehiculoTipo,
      salidaXferAcompananteSel,
      salidaXferCamionSel,
      salidaXferCanetasVacias,
      salidaXferCantCanetasVacias,
      salidaXferCantDolisVacios,
      salidaXferCantidadServicios,
      salidaXferDolisVacios,
      salidaXferObservaciones,
      salidaXferObservacionesServicios,
      salidaXferOperadorOscarSel,
      salidaXferOperadorSierroSel,
      salidaXferOperadorSinDen,
      salidaXferPersonalTipo,
      salidaXferServiciosEspeciales,
      salidaXferUnidadSel,
      salidaXferVehiculoTipo,
      salidaAlfaCamionSel,
      salidaAlfaCanetasTipo,
      salidaAlfaCantCanetas,
      salidaAlfaCantDolis,
      salidaAlfaDolisTipo,
      salidaAlfaJacpaletManual,
      salidaAlfaJacpaletSel,
      salidaAlfaMontacargasSel,
      salidaAlfaObservaciones,
      salidaAlfaMulaSel,
      salidaAlfaOperadorSel,
      salidaAlfaVehiculoTipo,
      salidaRCanetasObs,
      salidaRDolisObs,
      salidaREstrivador,
      salidaRObservacionTexto,
      salidaROperadorSel,
      salidaRPersonalTipo,
      salidaRSierroOscarSel,
      salidaRTipoUnidad,
      salidaRUnidadSel,
      salidaCantidadServicios,
      salidaCarretasQty,
      salidaConductorNombre,
      salidaDollyQty,
      salidaServiciosAdicionales,
      salidaSvcCodigos,
      unidadSel
    ]
  );

  const handleSubmitAt2 = useCallback(async () => {
    if (!revisionTipo) return;

    const flujoBackend = getFlujoBackend(revisionTipo);
    const slotOrder = getSlotOrder(revisionTipo);
    if (!flujoBackend) return;
    if (revisionTipo === 'carga' && !slotOrder.length) return;
    if (
      revisionTipo === 'salida_alfa2' &&
      salidaServiciosAdicionales === true &&
      !slotOrder.length
    ) {
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('idGenerado', String(user?.id_generado ?? ''));
      formData.append('tipo', 'ALFA_TANGO_2');
      formData.append('alfaTango2Flujo', flujoBackend);
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');

      if (revisionTipo === 'carga') {
        formData.append('alfaTango2Chofer', choferSel);
        formData.append('alfaTango2Aerolinea', aerolineaSel);
        formData.append('alfaTango2CamionEquipo', cargaCamionSel);
      } else if (revisionTipo === 'salida_alfa2') {
        formData.append('alfaTango2Unidad', unidadSel);
        formData.append('alfaTango2ConductorNombre', salidaConductorNombre.trim());
        const dNorm = String(salidaDollyQty || '').replace(/\D/g, '');
        const cNorm = String(salidaCarretasQty || '').replace(/\D/g, '');
        formData.append('alfaTango2Dolly', dNorm === '' ? '0' : dNorm);
        formData.append('alfaTango2Carretas', cNorm === '' ? '0' : cNorm);
        const svcSi = salidaServiciosAdicionales === true;
        formData.append('alfaTango2ServiciosAdicionales', svcSi ? 'SI' : 'NO');
        const nParsed = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
        const nServ = svcSi && Number.isFinite(nParsed) && nParsed >= 1 ? nParsed : 0;
        formData.append('alfaTango2CantidadServicios', String(nServ));
        formData.append('alfaTango2ServiciosCodigos', JSON.stringify(svcSi ? salidaSvcCodigos : []));
      } else if (revisionTipo === 'ingreso_aire_distrib') {
        formData.append('alfaTango2IngresoCamion', ingresoVehiculoTipo === 'camion' ? ingresoCamionSel : '');
        formData.append('alfaTango2IngresoMula', ingresoVehiculoTipo === 'mula' ? ingresoMulaSel : '');
        formData.append(
          'alfaTango2IngresoTractomula',
          ingresoVehiculoTipo === 'tractomula' ? ingresoTractomulaSel : ''
        );
        formData.append(
          'alfaTango2IngresoOperadores',
          ingresoPersonalTipo === 'operadores' ? ingresoOperadorSierroSel : ''
        );
        formData.append(
          'alfaTango2IngresoSierroOscar',
          ingresoPersonalTipo === 'sierra_oscar' ? ingresoOperadorOscarSel : ''
        );
        formData.append(
          'alfaTango2IngresoOpSinDen',
          ingresoPersonalTipo === 'operador_sin_den' ? String(ingresoOperadorSinDenominativo || '').trim() : ''
        );
        formData.append(
          'alfaTango2IngresoAcompanante',
          ingresoPersonalTipo === 'acompanantes' ? ingresoAcompananteSel : ''
        );
        formData.append('alfaTango2IngresoAerolinea', ingresoAerolineaSel);
        formData.append('alfaTango2IngresoNumeroVuelo', String(ingresoNumeroVuelo || '').trim());
        const appendSiNoIngreso = (keyFlag, tiene, keyCant, cantidad) => {
          formData.append(keyFlag, tiene === true ? 'SI' : 'NO');
          if (tiene === true) {
            formData.append(keyCant, String(cantidad || '').replace(/\D/g, ''));
          }
        };
        appendSiNoIngreso(
          'alfaTango2IngresoContenedores',
          ingresoTieneContenedores,
          'alfaTango2IngresoCantContenedores',
          ingresoCantidadContenedores
        );
        appendSiNoIngreso(
          'alfaTango2IngresoCanetas',
          ingresoTieneCanetas,
          'alfaTango2IngresoCantCanetas',
          ingresoCantidadCanetas
        );
        appendSiNoIngreso(
          'alfaTango2IngresoPalets',
          ingresoTienePalets,
          'alfaTango2IngresoCantPalets',
          ingresoCantidadPalets
        );
        appendSiNoIngreso(
          'alfaTango2IngresoCorreos',
          ingresoTieneCorreos,
          'alfaTango2IngresoCantCorreos',
          ingresoCantidadCorreos
        );
        formData.append('alfaTango2IngresoObservaciones', String(ingresoObservaciones || '').trim());
      } else if (revisionTipo === 'salida_dist_aire') {
        formData.append(
          'alfaTango2SalidaDistCamion',
          salidaDistVehiculoTipo === 'camion' ? salidaDistCamionSel : ''
        );
        formData.append(
          'alfaTango2SalidaDistTipoUnidad',
          salidaDistVehiculoTipo === 'mula' || salidaDistVehiculoTipo === 'tractomula'
            ? salidaDistVehiculoTipo
            : ''
        );
        formData.append(
          'alfaTango2SalidaDistUnidad',
          salidaDistVehiculoTipo === 'mula' || salidaDistVehiculoTipo === 'tractomula'
            ? salidaDistUnidadSel
            : ''
        );
        formData.append(
          'alfaTango2SalidaDistOperadores',
          salidaDistPersonalTipo === 'operadores' ? salidaDistOperadorSierroSel : ''
        );
        formData.append(
          'alfaTango2SalidaDistSierroOscar',
          salidaDistPersonalTipo === 'sierra_oscar' ? salidaDistOperadorOscarSel : ''
        );
        formData.append(
          'alfaTango2SalidaDistOpSinDen',
          salidaDistPersonalTipo === 'operador_sin_den'
            ? String(salidaDistOperadorSinDen || '').trim()
            : ''
        );
        formData.append(
          'alfaTango2SalidaDistAcompanante',
          salidaDistPersonalTipo === 'acompanantes' ? salidaDistAcompananteSel : ''
        );
        const appendSiNoSalidaDist = (keyFlag, tiene, keyCant, cantidad) => {
          formData.append(keyFlag, tiene === true ? 'SI' : 'NO');
          if (tiene === true) {
            formData.append(keyCant, String(cantidad || '').replace(/\D/g, ''));
          }
        };
        appendSiNoSalidaDist(
          'alfaTango2SalidaDistDolisVacios',
          salidaDistDolisVacios,
          'alfaTango2SalidaDistCantDolisVacios',
          salidaDistCantDolisVacios
        );
        appendSiNoSalidaDist(
          'alfaTango2SalidaDistCanetasVacias',
          salidaDistCanetasVacias,
          'alfaTango2SalidaDistCantCanetasVacias',
          salidaDistCantCanetasVacias
        );
        formData.append(
          'alfaTango2SalidaDistObservaciones',
          String(salidaDistObservaciones || '').trim()
        );
        const svcEsp = salidaDistServiciosEspeciales === true;
        formData.append('alfaTango2SalidaDistServiciosEspeciales', svcEsp ? 'SI' : 'NO');
        if (svcEsp) {
          const nServ = parseInt(String(salidaDistCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          const obsServicios = Array.from(
            { length: Number.isFinite(nServ) && nServ > 0 ? nServ : 0 },
            (_, idx) => String(salidaDistObservacionesServicios[idx] || '').trim()
          );
          formData.append('alfaTango2SalidaDistCantidadServicios', String(nServ || 0));
          formData.append('alfaTango2SalidaDistObservacionesServicios', JSON.stringify(obsServicios));
        }
      } else if (revisionTipo === 'salida_dist_transferencia') {
          formData.append(
          'alfaTango2SalidaXferCamion',
          salidaXferVehiculoTipo === 'camion' ? salidaXferCamionSel : ''
          );
          formData.append(
          'alfaTango2SalidaXferTipoUnidad',
          salidaXferVehiculoTipo === 'mula' || salidaXferVehiculoTipo === 'tractomula'
            ? salidaXferVehiculoTipo
            : ''
        );
        formData.append(
          'alfaTango2SalidaXferUnidad',
          salidaXferVehiculoTipo === 'mula' || salidaXferVehiculoTipo === 'tractomula'
            ? salidaXferUnidadSel
            : ''
        );
        formData.append(
          'alfaTango2SalidaXferOperadores',
          salidaXferPersonalTipo === 'operadores' ? salidaXferOperadorSierroSel : ''
        );
        formData.append(
          'alfaTango2SalidaXferSierroOscar',
          salidaXferPersonalTipo === 'sierra_oscar' ? salidaXferOperadorOscarSel : ''
        );
        formData.append(
          'alfaTango2SalidaXferOpSinDen',
          salidaXferPersonalTipo === 'operador_sin_den'
            ? String(salidaXferOperadorSinDen || '').trim()
            : ''
        );
        formData.append(
          'alfaTango2SalidaXferAcompanante',
          salidaXferPersonalTipo === 'acompanantes' ? salidaXferAcompananteSel : ''
        );
        const appendSiNoSalidaXfer = (keyFlag, tiene, keyCant, cantidad) => {
          formData.append(keyFlag, tiene === true ? 'SI' : 'NO');
          if (tiene === true) {
            formData.append(keyCant, String(cantidad || '').replace(/\D/g, ''));
          }
        };
        appendSiNoSalidaXfer(
          'alfaTango2SalidaXferDolisVacios',
          salidaXferDolisVacios,
          'alfaTango2SalidaXferCantDolisVacios',
          salidaXferCantDolisVacios
        );
        appendSiNoSalidaXfer(
          'alfaTango2SalidaXferCanetasVacias',
          salidaXferCanetasVacias,
          'alfaTango2SalidaXferCantCanetasVacias',
          salidaXferCantCanetasVacias
        );
        formData.append(
          'alfaTango2SalidaXferObservaciones',
          String(salidaXferObservaciones || '').trim()
        );
        const svcEspX = salidaXferServiciosEspeciales === true;
        formData.append('alfaTango2SalidaXferServiciosEspeciales', svcEspX ? 'SI' : 'NO');
        if (svcEspX) {
          const nServX = parseInt(String(salidaXferCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          const obsServiciosX = Array.from(
            { length: Number.isFinite(nServX) && nServX > 0 ? nServX : 0 },
            (_, idx) => String(salidaXferObservacionesServicios[idx] || '').trim()
          );
          formData.append('alfaTango2SalidaXferCantidadServicios', String(nServX || 0));
          formData.append('alfaTango2SalidaXferObservacionesServicios', JSON.stringify(obsServiciosX));
        }
      } else if (revisionTipo === 'salida_dist_r') {
        formData.append(
          'alfaTango2SalidaROperador',
          salidaRPersonalTipo === 'operador' ? salidaROperadorSel : ''
        );
        formData.append(
          'alfaTango2SalidaREstrivador',
          salidaRPersonalTipo === 'estivador' ? String(salidaREstrivador || '').trim() : ''
        );
        formData.append(
          'alfaTango2SalidaRSierroOscar',
          salidaRPersonalTipo === 'sierra_oscar' ? salidaRSierroOscarSel : ''
        );
        formData.append('alfaTango2SalidaRTipoUnidad', salidaRTipoUnidad);
        formData.append('alfaTango2SalidaRUnidad', salidaRUnidadSel);
        formData.append('alfaTango2SalidaRDolis', salidaRDolisObs || '');
        formData.append('alfaTango2SalidaRCanetas', salidaRCanetasObs || '');
        formData.append('alfaTango2SalidaRObservacionDetalle', String(salidaRObservacionTexto || '').trim());
      } else if (revisionTipo === 'salida_dist_alfa') {
        formData.append('alfaTango2SalidaAlfaOperador', salidaAlfaOperadorSel);
        formData.append(
          'alfaTango2SalidaAlfaCamion',
          salidaAlfaVehiculoTipo === 'camion' ? salidaAlfaCamionSel : ''
        );
        formData.append(
          'alfaTango2SalidaAlfaMula',
          salidaAlfaVehiculoTipo === 'mula' ? salidaAlfaMulaSel : ''
        );
          formData.append('alfaTango2SalidaAlfaJacpalet', salidaAlfaJacpaletSel);
        formData.append(
          'alfaTango2SalidaAlfaMontacargas',
          salidaAlfaVehiculoTipo === 'montacargas' ? salidaAlfaMontacargasSel : ''
        );
        formData.append(
          'alfaTango2SalidaAlfaJacpaletManual',
          String(salidaAlfaJacpaletManual || '').trim()
        );
        formData.append('alfaTango2SalidaAlfaDolisTipo', salidaAlfaDolisTipo || 'no');
        if (salidaAlfaDolisTipo && salidaAlfaDolisTipo !== 'no') {
          formData.append(
            'alfaTango2SalidaAlfaCantDolis',
            String(salidaAlfaCantDolis || '').replace(/\D/g, '')
          );
        }
        formData.append('alfaTango2SalidaAlfaCanetasTipo', salidaAlfaCanetasTipo || 'no');
        if (salidaAlfaCanetasTipo && salidaAlfaCanetasTipo !== 'no') {
          formData.append(
            'alfaTango2SalidaAlfaCantCanetas',
            String(salidaAlfaCantCanetas || '').replace(/\D/g, '')
          );
        }
        formData.append(
          'alfaTango2SalidaAlfaObservaciones',
          String(salidaAlfaObservaciones || '').trim()
        );
      } else if (revisionTipo === 'ingreso_alfa_distrib') {
        formData.append('alfaTango2IngresoAlfaOperador', ingresoAlfaOperadorSel);
        formData.append(
          'alfaTango2IngresoAlfaCamion',
          ingresoAlfaVehiculoTipo === 'camion' ? ingresoAlfaCamionSel : ''
        );
        formData.append(
          'alfaTango2IngresoAlfaMula',
          ingresoAlfaVehiculoTipo === 'mula' ? ingresoAlfaMulaSel : ''
        );
        formData.append(
          'alfaTango2IngresoAlfaTractomula',
          ingresoAlfaVehiculoTipo === 'tractomula' ? ingresoAlfaTractomulaSel : ''
        );
        formData.append(
          'alfaTango2IngresoAlfaMontacargas',
          ingresoAlfaVehiculoTipo === 'montacargas' ? ingresoAlfaMontacargasSel : ''
        );
        formData.append('alfaTango2IngresoAlfaJacpalet', ingresoAlfaJacpaletSel);
        formData.append(
          'alfaTango2IngresoAlfaJacpaletManual',
          String(ingresoAlfaJacpaletManual || '').trim()
        );
        formData.append('alfaTango2IngresoAlfaDolisTipo', ingresoAlfaDolisTipo || 'no');
        if (ingresoAlfaDolisTipo && ingresoAlfaDolisTipo !== 'no') {
          formData.append(
            'alfaTango2IngresoAlfaCantDolis',
            String(ingresoAlfaCantDolis || '').replace(/\D/g, '')
          );
        }
        formData.append('alfaTango2IngresoAlfaCanetasTipo', ingresoAlfaCanetasTipo || 'no');
        if (ingresoAlfaCanetasTipo && ingresoAlfaCanetasTipo !== 'no') {
          formData.append(
            'alfaTango2IngresoAlfaCantCanetas',
            String(ingresoAlfaCantCanetas || '').replace(/\D/g, '')
          );
        }
        formData.append(
          'alfaTango2IngresoAlfaObservaciones',
          String(ingresoAlfaObservaciones || '').trim()
        );
      }

      if (
        revisionTipo === 'ingreso_aire_distrib' ||
        revisionTipo === 'ingreso_alfa_distrib' ||
        revisionTipo === 'salida_dist_r' ||
        revisionTipo === 'salida_dist_alfa'
      ) {
        formData.append('alfaTango2FotoSlots', '[]');
      } else if (revisionTipo === 'salida_dist_aire') {
        if (salidaDistServiciosEspeciales === true) {
          const slots = [];
          const nServ = parseInt(String(salidaDistCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          const slotOrderDist = buildSalidaDistSlotOrder(true, Number.isFinite(nServ) && nServ >= 1 ? nServ : 0);
          for (const slot of slotOrderDist) {
            for (const file of fotoFiles[slot] || []) {
            formData.append('at2_fotos', file);
              slots.push(slot);
            }
          }
          formData.append('alfaTango2FotoSlots', JSON.stringify(slots));
        } else {
          formData.append('alfaTango2FotoSlots', '[]');
        }
      } else if (revisionTipo === 'salida_dist_transferencia') {
        if (salidaXferServiciosEspeciales === true) {
          const slots = [];
          const nServX = parseInt(String(salidaXferCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          const slotOrderXfer = buildSalidaXferSlotOrder(true, Number.isFinite(nServX) && nServX >= 1 ? nServX : 0);
          for (const slot of slotOrderXfer) {
            for (const file of fotoFiles[slot] || []) {
              formData.append('at2_fotos', file);
              slots.push(slot);
            }
          }
          formData.append('alfaTango2FotoSlots', JSON.stringify(slots));
        } else {
          formData.append('alfaTango2FotoSlots', '[]');
        }
      } else {
        const slots = [];
        for (const slot of slotOrder) {
          for (const file of fotoFiles[slot] || []) {
            formData.append('at2_fotos', file);
            slots.push(slot);
          }
        }
        formData.append('alfaTango2FotoSlots', JSON.stringify(slots));
      }

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }

      setSuccess(data.message || 'Alfa Tango 2 guardado.');
      setShowConfirmModal(false);
      resetFlujoForms();
      setVista(VISTA_INICIO);
    } catch (_) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  }, [
    aerolineaSel,
    cargaCamionSel,
    choferSel,
    fotoFiles,
    getFlujoBackend,
    getSlotOrder,
    ingresoAcompananteSel,
    ingresoAerolineaSel,
    ingresoCamionSel,
    ingresoCantidadCanetas,
    ingresoCantidadContenedores,
    ingresoCantidadCorreos,
    ingresoCantidadPalets,
    ingresoMulaSel,
    ingresoNumeroVuelo,
    ingresoObservaciones,
    ingresoOperadorOscarSel,
    ingresoOperadorSinDenominativo,
    ingresoOperadorSierroSel,
    ingresoTieneCanetas,
    ingresoTieneContenedores,
    ingresoTieneCorreos,
    ingresoTienePalets,
    ingresoTractomulaSel,
    ingresoVehiculoTipo,
    ingresoPersonalTipo,
    ingresoAlfaCamionSel,
    ingresoAlfaCanetasTipo,
    ingresoAlfaCantCanetas,
    ingresoAlfaCantDolis,
    ingresoAlfaDolisTipo,
    ingresoAlfaJacpaletManual,
    ingresoAlfaJacpaletSel,
    ingresoAlfaMontacargasSel,
    ingresoAlfaMulaSel,
    ingresoAlfaObservaciones,
    ingresoAlfaOperadorSel,
    ingresoAlfaTractomulaSel,
    ingresoAlfaVehiculoTipo,
    resetFlujoForms,
    revisionTipo,
    salidaDistAcompananteSel,
    salidaDistCamionSel,
    salidaDistCanetasVacias,
    salidaDistCantCanetasVacias,
    salidaDistCantDolisVacios,
    salidaDistCantidadServicios,
    salidaDistDolisVacios,
    salidaDistObservaciones,
    salidaDistObservacionesServicios,
    salidaDistOperadorOscarSel,
    salidaDistOperadorSinDen,
    salidaDistOperadorSierroSel,
    salidaDistPersonalTipo,
    salidaDistServiciosEspeciales,
    salidaDistUnidadSel,
    salidaDistVehiculoTipo,
    salidaXferAcompananteSel,
    salidaXferCamionSel,
    salidaXferCanetasVacias,
    salidaXferCantCanetasVacias,
    salidaXferCantDolisVacios,
    salidaXferCantidadServicios,
    salidaXferDolisVacios,
    salidaXferObservaciones,
    salidaXferObservacionesServicios,
    salidaXferOperadorOscarSel,
    salidaXferOperadorSinDen,
    salidaXferOperadorSierroSel,
    salidaXferPersonalTipo,
    salidaXferServiciosEspeciales,
    salidaXferUnidadSel,
    salidaXferVehiculoTipo,
    salidaAlfaCamionSel,
    salidaAlfaCanetasTipo,
    salidaAlfaCantCanetas,
    salidaAlfaCantDolis,
    salidaAlfaDolisTipo,
    salidaAlfaJacpaletManual,
    salidaAlfaJacpaletSel,
    salidaAlfaMontacargasSel,
    salidaAlfaMulaSel,
    salidaAlfaObservaciones,
    salidaAlfaOperadorSel,
    salidaAlfaVehiculoTipo,
    salidaRCanetasObs,
    salidaRDolisObs,
    salidaREstrivador,
    salidaRObservacionTexto,
    salidaROperadorSel,
    salidaRPersonalTipo,
    salidaRSierroOscarSel,
    salidaRTipoUnidad,
    salidaRUnidadSel,
    salidaCantidadServicios,
    salidaCarretasQty,
    salidaConductorNombre,
    salidaDollyQty,
    salidaServiciosAdicionales,
    salidaSvcCodigos,
    unidadSel,
    user?.appid,
    user?.id_generado,
    user?.username
  ]);

  const pasoCargaIndex = useMemo(() => PASOS_CARGA.indexOf(cargaPaso), [cargaPaso]);
  const pasoIngresoAireIndex = useMemo(
    () => PASOS_INGRESO_AIRE_DISTRIB.indexOf(ingresoAirePaso),
    [ingresoAirePaso]
  );

  const validarPasoIngresoAire = useCallback(
    (paso) => {
      if (paso === 'vehiculo') {
        if (ingresoVehiculoTipo === 'camion' && !selCatalogoValido(ingresoCamionSel)) {
        return 'Seleccione o agregue el número de camión.';
      }
        if (ingresoVehiculoTipo === 'mula' && !selCatalogoValido(ingresoMulaSel)) {
        return 'Seleccione o agregue el número de mula.';
      }
        if (ingresoVehiculoTipo === 'tractomula' && !selCatalogoValido(ingresoTractomulaSel)) {
        return 'Seleccione o agregue el número de tractomula.';
      }
        return '';
      }
      if (paso === 'personal') {
        if (ingresoPersonalTipo === 'operadores' && !selCatalogoValido(ingresoOperadorSierroSel)) {
          return 'Seleccione o agregue en Operadores.';
        }
        if (ingresoPersonalTipo === 'sierra_oscar' && !selCatalogoValido(ingresoOperadorOscarSel)) {
          return 'Seleccione o agregue en Sierra Oscar.';
        }
        if (
          ingresoPersonalTipo === 'operador_sin_den' &&
          !String(ingresoOperadorSinDenominativo || '').trim()
        ) {
        return 'Indique operadores sin denominativo.';
      }
        if (ingresoPersonalTipo === 'acompanantes' && !selCatalogoValido(ingresoAcompananteSel)) {
        return 'Seleccione o agregue un acompañante.';
        }
        return '';
      }
      if (paso === 'aerolinea_vuelo') {
        if (!selCatalogoValido(ingresoAerolineaSel)) return 'Seleccione o agregue una aerolínea.';
        if (!String(ingresoNumeroVuelo || '').trim()) return 'Ingrese el número de vuelo.';
        return '';
      }
      const validarSiNo = (tiene, cantidad, etiqueta) => {
        if (tiene !== true && tiene !== false) return `Indique si tiene ${etiqueta}.`;
        if (tiene === true) {
          const n = parseInt(String(cantidad || '').replace(/\D/g, '') || '0', 10);
          if (!Number.isFinite(n) || n < 1) return `Indique la cantidad de ${etiqueta}.`;
        }
        return '';
      };
      if (paso === 'carga_tipos') {
        const errCarga =
          validarSiNo(ingresoTieneContenedores, ingresoCantidadContenedores, 'contenedores') ||
          validarSiNo(ingresoTieneCanetas, ingresoCantidadCanetas, 'carretas') ||
          validarSiNo(ingresoTienePalets, ingresoCantidadPalets, 'palets') ||
          validarSiNo(ingresoTieneCorreos, ingresoCantidadCorreos, 'correos');
        if (errCarga) return errCarga;
        if (!String(ingresoObservaciones || '').trim()) return 'Ingrese las observaciones.';
        return '';
      }
      return '';
    },
    [
      ingresoAcompananteSel,
      ingresoAerolineaSel,
      ingresoCamionSel,
      ingresoCantidadCanetas,
      ingresoCantidadContenedores,
      ingresoCantidadCorreos,
      ingresoCantidadPalets,
      ingresoMulaSel,
      ingresoNumeroVuelo,
      ingresoObservaciones,
      ingresoOperadorOscarSel,
      ingresoOperadorSinDenominativo,
      ingresoOperadorSierroSel,
      ingresoTieneCanetas,
      ingresoTieneContenedores,
      ingresoTieneCorreos,
      ingresoTienePalets,
      ingresoTractomulaSel,
      ingresoVehiculoTipo,
      ingresoPersonalTipo
    ]
  );

  const salidaDistServiciosCount = useMemo(() => {
    if (salidaDistServiciosEspeciales !== true) return 0;
    const n = parseInt(String(salidaDistCantidadServicios || '').replace(/\D/g, '') || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [salidaDistCantidadServicios, salidaDistServiciosEspeciales]);

  const pasoSalidaDistIndex = useMemo(() => {
    const idxBase = PASOS_SALIDA_DIST_AIRE.indexOf(salidaDistPaso);
    if (idxBase >= 0) return idxBase;
    const svc = parseSalidaDistServicioPaso(salidaDistPaso);
    if (!svc) return -1;
    const baseLen = PASOS_SALIDA_DIST_AIRE.length;
    return baseLen + svc.idx;
  }, [salidaDistPaso]);

  const totalPasosSalidaDist = useMemo(
    () => PASOS_SALIDA_DIST_AIRE.length + salidaDistServiciosCount,
    [salidaDistServiciosCount]
  );

  const salidaDistBotonPrincipalLabel = useMemo(() => {
    if (salidaDistPaso === 'servicios_especiales' && salidaDistServiciosEspeciales === false) {
      return 'Guardar en bitácora';
    }
    const svc = parseSalidaDistServicioPaso(salidaDistPaso);
    if (svc) {
      return svc.idx >= salidaDistServiciosCount - 1 ? 'Guardar en bitácora' : 'Continuar';
    }
    return pasoSalidaDistIndex >= totalPasosSalidaDist - 1 ? 'Guardar en bitácora' : 'Continuar';
  }, [
    salidaDistPaso,
    salidaDistServiciosCount,
    salidaDistServiciosEspeciales,
    pasoSalidaDistIndex,
    totalPasosSalidaDist
  ]);

  const validarPasoSalidaDist = useCallback(
    (paso) => {
      if (paso === 'vehiculo') {
        if (salidaDistVehiculoTipo === 'camion' && !selCatalogoValido(salidaDistCamionSel)) {
        return 'Seleccione o agregue el número de camión.';
      }
        if (salidaDistVehiculoTipo === 'mula' && !selCatalogoValido(salidaDistUnidadSel)) {
          return 'Seleccione o agregue la mula.';
        }
        if (salidaDistVehiculoTipo === 'tractomula' && !selCatalogoValido(salidaDistUnidadSel)) {
          return 'Seleccione o agregue la tractomula.';
        }
        return '';
      }
      if (paso === 'personal') {
        if (salidaDistPersonalTipo === 'operadores' && !selCatalogoValido(salidaDistOperadorSierroSel)) {
          return 'Seleccione o agregue en Operadores.';
        }
        if (salidaDistPersonalTipo === 'sierra_oscar' && !selCatalogoValido(salidaDistOperadorOscarSel)) {
          return 'Seleccione o agregue en Sierra Oscar.';
        }
        if (
          salidaDistPersonalTipo === 'operador_sin_den' &&
          !String(salidaDistOperadorSinDen || '').trim()
        ) {
        return 'Indique operadores sin denominativo.';
        }
        if (salidaDistPersonalTipo === 'acompanantes' && !selCatalogoValido(salidaDistAcompananteSel)) {
          return 'Seleccione o agregue un acompañante.';
        }
        return '';
      }
      const validarSiNo = (tiene, cantidad, etiqueta) => {
        if (tiene !== true && tiene !== false) return `Indique: ${etiqueta}.`;
        if (tiene === true) {
          const n = parseInt(String(cantidad || '').replace(/\D/g, '') || '0', 10);
          if (!Number.isFinite(n) || n < 1) return `Indique cuántos: ${etiqueta}.`;
        }
        return '';
      };
      if (paso === 'carga_lleva') {
        return (
          validarSiNo(salidaDistDolisVacios, salidaDistCantDolisVacios, 'dolis vacíos') ||
          validarSiNo(salidaDistCanetasVacias, salidaDistCantCanetasVacias, 'carretas vacías') ||
          (!String(salidaDistObservaciones || '').trim() ? 'Ingrese las observaciones.' : '')
        );
      }
      if (paso === 'servicios_especiales') {
        if (salidaDistServiciosEspeciales !== true && salidaDistServiciosEspeciales !== false) {
          return 'Indique si lleva servicios especiales.';
        }
        if (salidaDistServiciosEspeciales === true) {
        const n = parseInt(String(salidaDistCantidadServicios || '').replace(/\D/g, '') || '0', 10);
        if (!Number.isFinite(n) || n < 1) return 'Indique cuántos servicios especiales.';
        }
        return '';
      }
      const svcPaso = parseSalidaDistServicioPaso(paso);
      if (svcPaso) {
        const idxSvc = svcPaso.idx;
        if (idxSvc < 0 || idxSvc >= salidaDistServiciosCount) {
          return 'Servicio especial inválido.';
        }
        const obs = String(salidaDistObservacionesServicios[idxSvc] || '').trim();
        if (!obs) return `Ingrese la observación del servicio ${idxSvc + 1}.`;
        const slotGuia = `salida_dist_svc_${idxSvc}_guia`;
        const slotBultos = `salida_dist_svc_${idxSvc}_bultos`;
        const nGuia = (fotoFiles[slotGuia] || []).length;
        const nBultos = (fotoFiles[slotBultos] || []).length;
        if (nGuia !== 1) return `Servicio ${idxSvc + 1}: suba exactamente 1 foto de guía.`;
        if (nBultos < 1) return `Servicio ${idxSvc + 1}: suba al menos 1 foto de bultos.`;
        if (nBultos > 2) return `Servicio ${idxSvc + 1}: máximo 2 fotos de bultos.`;
        return '';
      }
      return '';
    },
    [
      fotoFiles,
      salidaDistAcompananteSel,
      salidaDistCamionSel,
      salidaDistCanetasVacias,
      salidaDistCantCanetasVacias,
      salidaDistCantDolisVacios,
      salidaDistCantidadServicios,
      salidaDistDolisVacios,
      salidaDistObservaciones,
      salidaDistServiciosCount,
      salidaDistObservacionesServicios,
      salidaDistOperadorOscarSel,
      salidaDistOperadorSinDen,
      salidaDistOperadorSierroSel,
      salidaDistPersonalTipo,
      salidaDistServiciosEspeciales,
      salidaDistUnidadSel,
      salidaDistVehiculoTipo
    ]
  );

  const salidaDistContinuar = useCallback(() => {
    setError('');
    stopCamera();
    const msg = validarPasoSalidaDist(salidaDistPaso);
    if (msg) {
      setError(msg);
      return;
    }
    if (salidaDistPaso === 'servicios_especiales') {
      if (salidaDistServiciosEspeciales === false) {
        setRevisionTipo('salida_dist_aire');
        openConfirmGuardar();
        return;
      }
      setSalidaDistObservacionesServicios((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return Array.from(
          { length: salidaDistServiciosCount },
          (_, idx) => (base[idx] != null ? base[idx] : '')
        );
      });
      setFotoFiles((prev) => ensureSalidaDistFotoSlotsInMap(prev, salidaDistServiciosCount));
      setFotoPreview((prev) => ensureSalidaDistFotoSlotsInMap(prev, salidaDistServiciosCount));
      setSalidaDistPaso('servicio_0');
      return;
    }
    const svcPaso = parseSalidaDistServicioPaso(salidaDistPaso);
    if (svcPaso) {
      if (svcPaso.idx >= salidaDistServiciosCount - 1) {
        setRevisionTipo('salida_dist_aire');
        openConfirmGuardar();
        return;
      }
      setSalidaDistPaso(`servicio_${svcPaso.idx + 1}`);
      return;
    }
    const idx = pasoSalidaDistIndex;
    if (idx === PASOS_SALIDA_DIST_AIRE.length - 1) {
      setRevisionTipo('salida_dist_aire');
      openConfirmGuardar();
      return;
    }
    setSalidaDistPaso(PASOS_SALIDA_DIST_AIRE[idx + 1]);
  }, [
    openConfirmGuardar,
    pasoSalidaDistIndex,
    salidaDistPaso,
    salidaDistServiciosCount,
    salidaDistServiciosEspeciales,
    stopCamera,
    validarPasoSalidaDist
  ]);

  const salidaDistRetroceder = useCallback(() => {
    setError('');
    const svcPaso = parseSalidaDistServicioPaso(salidaDistPaso);
    if (svcPaso) {
      if (svcPaso.idx <= 0) {
        setSalidaDistPaso('servicios_especiales');
      } else {
        setSalidaDistPaso(`servicio_${svcPaso.idx - 1}`);
      }
      return;
    }
    const idx = pasoSalidaDistIndex;
    if (idx <= 0) return;
    setSalidaDistPaso(PASOS_SALIDA_DIST_AIRE[idx - 1]);
  }, [pasoSalidaDistIndex, salidaDistPaso]);

  const salidaXferServiciosCount = useMemo(() => {
    if (salidaXferServiciosEspeciales !== true) return 0;
    const n = parseInt(String(salidaXferCantidadServicios || '').replace(/\D/g, '') || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [salidaXferCantidadServicios, salidaXferServiciosEspeciales]);

  const pasoSalidaXferIndex = useMemo(() => {
    const idxBase = PASOS_SALIDA_DIST_TRANSFERENCIA.indexOf(salidaXferPaso);
    if (idxBase >= 0) return idxBase;
    const svc = parseSalidaDistServicioPaso(salidaXferPaso);
    if (!svc) return -1;
    const baseLen = PASOS_SALIDA_DIST_TRANSFERENCIA.length;
    return baseLen + svc.idx;
  }, [salidaXferPaso]);

  const totalPasosSalidaXfer = useMemo(
    () => PASOS_SALIDA_DIST_TRANSFERENCIA.length + salidaXferServiciosCount,
    [salidaXferServiciosCount]
  );

  const salidaXferBotonPrincipalLabel = useMemo(() => {
    if (salidaXferPaso === 'servicios_especiales' && salidaXferServiciosEspeciales === false) {
      return 'Guardar en bitácora';
    }
    const svc = parseSalidaDistServicioPaso(salidaXferPaso);
    if (svc) {
      return svc.idx >= salidaXferServiciosCount - 1 ? 'Guardar en bitácora' : 'Continuar';
    }
    return pasoSalidaXferIndex >= totalPasosSalidaXfer - 1 ? 'Guardar en bitácora' : 'Continuar';
  }, [
    salidaXferPaso,
    salidaXferServiciosCount,
    salidaXferServiciosEspeciales,
    pasoSalidaXferIndex,
    totalPasosSalidaXfer
  ]);

  const validarPasoSalidaXfer = useCallback(
    (paso) => {
      if (paso === 'vehiculo') {
        if (salidaXferVehiculoTipo === 'camion' && !selCatalogoValido(salidaXferCamionSel)) {
          return 'Seleccione o agregue el número de camión.';
        }
        if (salidaXferVehiculoTipo === 'mula' && !selCatalogoValido(salidaXferUnidadSel)) {
          return 'Seleccione o agregue la mula.';
        }
        if (salidaXferVehiculoTipo === 'tractomula' && !selCatalogoValido(salidaXferUnidadSel)) {
          return 'Seleccione o agregue la tractomula.';
        }
        return '';
      }
      if (paso === 'personal') {
        if (salidaXferPersonalTipo === 'operadores' && !selCatalogoValido(salidaXferOperadorSierroSel)) {
          return 'Seleccione o agregue en Operadores.';
        }
        if (salidaXferPersonalTipo === 'sierra_oscar' && !selCatalogoValido(salidaXferOperadorOscarSel)) {
          return 'Seleccione o agregue en Sierra Oscar.';
        }
        if (
          salidaXferPersonalTipo === 'operador_sin_den' &&
          !String(salidaXferOperadorSinDen || '').trim()
        ) {
          return 'Indique operadores sin denominativo.';
        }
        if (salidaXferPersonalTipo === 'acompanantes' && !selCatalogoValido(salidaXferAcompananteSel)) {
          return 'Seleccione o agregue un acompañante.';
        }
        return '';
      }
      const validarSiNo = (tiene, cantidad, etiqueta) => {
        if (tiene !== true && tiene !== false) return `Indique: ${etiqueta}.`;
        if (tiene === true) {
          const n = parseInt(String(cantidad || '').replace(/\D/g, '') || '0', 10);
          if (!Number.isFinite(n) || n < 1) return `Indique cuántos: ${etiqueta}.`;
        }
        return '';
      };
      if (paso === 'carga_lleva') {
        return (
          validarSiNo(salidaXferDolisVacios, salidaXferCantDolisVacios, 'dolis vacíos') ||
          validarSiNo(salidaXferCanetasVacias, salidaXferCantCanetasVacias, 'carretas vacías') ||
          (!String(salidaXferObservaciones || '').trim() ? 'Ingrese las observaciones.' : '')
        );
      }
      if (paso === 'servicios_especiales') {
        if (salidaXferServiciosEspeciales !== true && salidaXferServiciosEspeciales !== false) {
          return 'Indique si lleva servicios especiales.';
        }
        if (salidaXferServiciosEspeciales === true) {
          const n = parseInt(String(salidaXferCantidadServicios || '').replace(/\D/g, '') || '0', 10);
          if (!Number.isFinite(n) || n < 1) return 'Indique cuántos servicios especiales.';
        }
        return '';
      }
      const svcPaso = parseSalidaDistServicioPaso(paso);
      if (svcPaso) {
        const idxSvc = svcPaso.idx;
        if (idxSvc < 0 || idxSvc >= salidaXferServiciosCount) {
          return 'Servicio especial inválido.';
        }
        const obs = String(salidaXferObservacionesServicios[idxSvc] || '').trim();
        if (!obs) return `Ingrese la observación del servicio ${idxSvc + 1}.`;
        const slotGuia = `salida_xfer_svc_${idxSvc}_guia`;
        const slotBultos = `salida_xfer_svc_${idxSvc}_bultos`;
        const nGuia = (fotoFiles[slotGuia] || []).length;
        const nBultos = (fotoFiles[slotBultos] || []).length;
        if (nGuia !== 1) return `Servicio ${idxSvc + 1}: suba exactamente 1 foto de guía.`;
        if (nBultos < 1) return `Servicio ${idxSvc + 1}: suba al menos 1 foto de bultos.`;
        if (nBultos > 2) return `Servicio ${idxSvc + 1}: máximo 2 fotos de bultos.`;
        return '';
      }
      return '';
    },
    [
      fotoFiles,
      salidaXferAcompananteSel,
      salidaXferCamionSel,
      salidaXferCanetasVacias,
      salidaXferCantCanetasVacias,
      salidaXferCantDolisVacios,
      salidaXferCantidadServicios,
      salidaXferDolisVacios,
      salidaXferObservaciones,
      salidaXferServiciosCount,
      salidaXferObservacionesServicios,
      salidaXferOperadorOscarSel,
      salidaXferOperadorSinDen,
      salidaXferOperadorSierroSel,
      salidaXferPersonalTipo,
      salidaXferServiciosEspeciales,
      salidaXferUnidadSel,
      salidaXferVehiculoTipo
    ]
  );

  const salidaXferContinuar = useCallback(() => {
    setError('');
    stopCamera();
    const msg = validarPasoSalidaXfer(salidaXferPaso);
    if (msg) {
      setError(msg);
      return;
    }
    if (salidaXferPaso === 'servicios_especiales') {
      if (salidaXferServiciosEspeciales === false) {
        setRevisionTipo('salida_dist_transferencia');
        openConfirmGuardar();
        return;
      }
      setSalidaXferObservacionesServicios((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return Array.from(
          { length: salidaXferServiciosCount },
          (_, idx) => (base[idx] != null ? base[idx] : '')
        );
      });
      setFotoFiles((prev) => ensureSalidaXferFotoSlotsInMap(prev, salidaXferServiciosCount));
      setFotoPreview((prev) => ensureSalidaXferFotoSlotsInMap(prev, salidaXferServiciosCount));
      setSalidaXferPaso('servicio_0');
      return;
    }
    const svcPaso = parseSalidaDistServicioPaso(salidaXferPaso);
    if (svcPaso) {
      if (svcPaso.idx >= salidaXferServiciosCount - 1) {
        setRevisionTipo('salida_dist_transferencia');
        openConfirmGuardar();
        return;
      }
      setSalidaXferPaso(`servicio_${svcPaso.idx + 1}`);
      return;
    }
    const idx = pasoSalidaXferIndex;
    if (idx === PASOS_SALIDA_DIST_TRANSFERENCIA.length - 1) {
      setRevisionTipo('salida_dist_transferencia');
      openConfirmGuardar();
      return;
    }
    setSalidaXferPaso(PASOS_SALIDA_DIST_TRANSFERENCIA[idx + 1]);
  }, [
    openConfirmGuardar,
    pasoSalidaXferIndex,
    salidaXferPaso,
    salidaXferServiciosCount,
    salidaXferServiciosEspeciales,
    stopCamera,
    validarPasoSalidaXfer
  ]);

  const salidaXferRetroceder = useCallback(() => {
    setError('');
    const svcPaso = parseSalidaDistServicioPaso(salidaXferPaso);
    if (svcPaso) {
      if (svcPaso.idx <= 0) {
        setSalidaXferPaso('servicios_especiales');
      } else {
        setSalidaXferPaso(`servicio_${svcPaso.idx - 1}`);
      }
      return;
    }
    const idx = pasoSalidaXferIndex;
    if (idx <= 0) return;
    setSalidaXferPaso(PASOS_SALIDA_DIST_TRANSFERENCIA[idx - 1]);
  }, [pasoSalidaXferIndex, salidaXferPaso]);

  const pasoSalidaAlfaIndex = useMemo(
    () => PASOS_SALIDA_DIST_ALFA.indexOf(salidaAlfaPaso),
    [salidaAlfaPaso]
  );

  const validarTipoCantidadCargaAlfaAt2 = (tipo, cantidad, nombre) => {
    if (!tipo) return `Indique si lleva ${nombre}.`;
    if (tipo === 'no') return '';
    if (tipo === 'vacio' || tipo === 'lleno' || tipo === 'vacia' || tipo === 'llena') {
      const n = parseInt(String(cantidad || '').replace(/\D/g, '') || '0', 10);
      if (!Number.isFinite(n) || n < 1) return `Indique cuántos ${nombre}.`;
    }
    return '';
  };

  const validarPasoSalidaAlfa = useCallback(
    (paso) => {
      if (paso === 'operadores' && !selCatalogoValido(salidaAlfaOperadorSel)) {
        return 'Seleccione o agregue un operador.';
      }
      if (paso === 'vehiculo') {
        if (salidaAlfaVehiculoTipo === 'camion' && !selCatalogoValido(salidaAlfaCamionSel)) {
        return 'Seleccione o agregue el número de camión.';
      }
        if (salidaAlfaVehiculoTipo === 'mula' && !selCatalogoValido(salidaAlfaMulaSel)) {
        return 'Seleccione o agregue el número de mula.';
      }
        if (salidaAlfaVehiculoTipo === 'montacargas' && !selCatalogoValido(salidaAlfaMontacargasSel)) {
          return 'Seleccione o agregue el montacargas.';
        }
      }
      if (paso === 'jacpalet' && !selCatalogoValido(salidaAlfaJacpaletSel)) {
        return 'Seleccione o agregue el JacPalet.';
      }
      if (paso === 'jacpalet_manual' && !String(salidaAlfaJacpaletManual || '').trim()) {
        return 'Ingrese el JacPalet manual.';
      }
      if (paso === 'observaciones') {
        const errDolis = validarTipoCantidadCargaAlfaAt2(
          salidaAlfaDolisTipo,
          salidaAlfaCantDolis,
          'dolis'
        );
        if (errDolis) return errDolis;
        const errCanetas = validarTipoCantidadCargaAlfaAt2(
          salidaAlfaCanetasTipo,
          salidaAlfaCantCanetas,
          'carretas'
        );
        if (errCanetas) return errCanetas;
        if (!String(salidaAlfaObservaciones || '').trim()) return 'Ingrese las observaciones.';
      }
      return '';
    },
    [
      salidaAlfaCamionSel,
      salidaAlfaCanetasTipo,
      salidaAlfaCantCanetas,
      salidaAlfaCantDolis,
      salidaAlfaDolisTipo,
      salidaAlfaJacpaletManual,
      salidaAlfaJacpaletSel,
      salidaAlfaMontacargasSel,
      salidaAlfaMulaSel,
      salidaAlfaObservaciones,
      salidaAlfaOperadorSel,
      salidaAlfaVehiculoTipo
    ]
  );

  const salidaAlfaContinuar = useCallback(() => {
    setError('');
    const msg = validarPasoSalidaAlfa(salidaAlfaPaso);
    if (msg) {
      setError(msg);
      return;
    }
    const idx = pasoSalidaAlfaIndex;
    if (idx === PASOS_SALIDA_DIST_ALFA.length - 1) {
      setRevisionTipo('salida_dist_alfa');
      openConfirmGuardar();
      return;
    }
    setSalidaAlfaPaso(PASOS_SALIDA_DIST_ALFA[idx + 1]);
  }, [openConfirmGuardar, pasoSalidaAlfaIndex, salidaAlfaPaso, validarPasoSalidaAlfa]);

  const salidaAlfaRetroceder = useCallback(() => {
    setError('');
    const idx = pasoSalidaAlfaIndex;
    if (idx <= 0) return;
    setSalidaAlfaPaso(PASOS_SALIDA_DIST_ALFA[idx - 1]);
  }, [pasoSalidaAlfaIndex]);

  const pasoIngresoAlfaIndex = useMemo(
    () => PASOS_INGRESO_ALFA_DISTRIB.indexOf(ingresoAlfaPaso),
    [ingresoAlfaPaso]
  );

  const validarPasoIngresoAlfa = useCallback(
    (paso) => {
      if (paso === 'operadores' && !selCatalogoValido(ingresoAlfaOperadorSel)) {
        return 'Seleccione o agregue un operador.';
      }
      if (paso === 'vehiculo') {
        if (ingresoAlfaVehiculoTipo === 'camion' && !selCatalogoValido(ingresoAlfaCamionSel)) {
          return 'Seleccione o agregue el número de camión.';
        }
        if (ingresoAlfaVehiculoTipo === 'mula' && !selCatalogoValido(ingresoAlfaMulaSel)) {
          return 'Seleccione o agregue el número de mula.';
        }
        if (ingresoAlfaVehiculoTipo === 'tractomula' && !selCatalogoValido(ingresoAlfaTractomulaSel)) {
          return 'Seleccione o agregue el número de tractomula.';
        }
        if (ingresoAlfaVehiculoTipo === 'montacargas' && !selCatalogoValido(ingresoAlfaMontacargasSel)) {
          return 'Seleccione o agregue el montacargas.';
        }
        return '';
      }
      if (paso === 'jacpalet' && !selCatalogoValido(ingresoAlfaJacpaletSel)) {
        return 'Seleccione o agregue el JacPalet.';
      }
      if (paso === 'jacpalet_manual' && !String(ingresoAlfaJacpaletManual || '').trim()) {
        return 'Ingrese el JacPalet manual.';
      }
      if (paso === 'observaciones') {
        const errDolis = validarTipoCantidadCargaAlfaAt2(
          ingresoAlfaDolisTipo,
          ingresoAlfaCantDolis,
          'dolis'
        );
        if (errDolis) return errDolis;
        const errCanetas = validarTipoCantidadCargaAlfaAt2(
          ingresoAlfaCanetasTipo,
          ingresoAlfaCantCanetas,
          'carretas'
        );
        if (errCanetas) return errCanetas;
        if (!String(ingresoAlfaObservaciones || '').trim()) return 'Ingrese las observaciones.';
      }
      return '';
    },
    [
      ingresoAlfaCamionSel,
      ingresoAlfaCanetasTipo,
      ingresoAlfaCantCanetas,
      ingresoAlfaCantDolis,
      ingresoAlfaDolisTipo,
      ingresoAlfaJacpaletManual,
      ingresoAlfaJacpaletSel,
      ingresoAlfaMontacargasSel,
      ingresoAlfaMulaSel,
      ingresoAlfaObservaciones,
      ingresoAlfaOperadorSel,
      ingresoAlfaTractomulaSel,
      ingresoAlfaVehiculoTipo
    ]
  );

  const ingresoAlfaContinuar = useCallback(() => {
    setError('');
    const msg = validarPasoIngresoAlfa(ingresoAlfaPaso);
    if (msg) {
      setError(msg);
      return;
    }
    const idx = pasoIngresoAlfaIndex;
    if (idx === PASOS_INGRESO_ALFA_DISTRIB.length - 1) {
      setRevisionTipo('ingreso_alfa_distrib');
      openConfirmGuardar();
      return;
    }
    setIngresoAlfaPaso(PASOS_INGRESO_ALFA_DISTRIB[idx + 1]);
  }, [ingresoAlfaPaso, openConfirmGuardar, pasoIngresoAlfaIndex, validarPasoIngresoAlfa]);

  const ingresoAlfaRetroceder = useCallback(() => {
    setError('');
    const idx = pasoIngresoAlfaIndex;
    if (idx <= 0) return;
    setIngresoAlfaPaso(PASOS_INGRESO_ALFA_DISTRIB[idx - 1]);
  }, [pasoIngresoAlfaIndex]);

  const pasoSalidaRIndex = useMemo(() => PASOS_SALIDA_DIST_R.indexOf(salidaRPaso), [salidaRPaso]);

  const validarPasoSalidaR = useCallback(
    (paso) => {
      if (paso === 'personal') {
        if (salidaRPersonalTipo === 'operador' && !selCatalogoValido(salidaROperadorSel)) {
        return 'Seleccione o agregue un operador.';
      }
        if (salidaRPersonalTipo === 'estivador' && !String(salidaREstrivador || '').trim()) {
          return 'Ingrese el estivador.';
      }
        if (salidaRPersonalTipo === 'sierra_oscar' && !selCatalogoValido(salidaRSierroOscarSel)) {
          return 'Seleccione o agregue en Sierra Oscar.';
      }
      }
      if (paso === 'vehiculo') {
        if (salidaRTipoUnidad !== 'mula' && salidaRTipoUnidad !== 'montacargas') {
          return 'Indique si es mula o montacargas.';
        }
        if (!selCatalogoValido(salidaRUnidadSel)) {
          return salidaRTipoUnidad === 'mula'
            ? 'Seleccione o agregue la mula.'
            : 'Seleccione o agregue el montacargas.';
        }
        return '';
      }
      if (paso === 'observacion') {
        if (!salidaRDolisObs) return 'Indique si lleva dolis (plástico, madera o no).';
        if (!salidaRCanetasObs) return 'Indique si lleva carretas (plástico, madera o no).';
        if (!String(salidaRObservacionTexto || '').trim()) return 'Ingrese el detalle adicional.';
      }
      return '';
    },
    [
      salidaRCanetasObs,
      salidaRDolisObs,
      salidaREstrivador,
      salidaRObservacionTexto,
      salidaROperadorSel,
      salidaRPersonalTipo,
      salidaRSierroOscarSel,
      salidaRTipoUnidad,
      salidaRUnidadSel
    ]
  );

  const salidaRContinuar = useCallback(() => {
    setError('');
    const msg = validarPasoSalidaR(salidaRPaso);
    if (msg) {
      setError(msg);
      return;
    }
    const idx = pasoSalidaRIndex;
    if (idx === PASOS_SALIDA_DIST_R.length - 1) {
      setRevisionTipo('salida_dist_r');
      openConfirmGuardar();
      return;
    }
    setSalidaRPaso(PASOS_SALIDA_DIST_R[idx + 1]);
  }, [openConfirmGuardar, pasoSalidaRIndex, salidaRPaso, validarPasoSalidaR]);

  const salidaRRetroceder = useCallback(() => {
    setError('');
    const idx = pasoSalidaRIndex;
    if (idx <= 0) return;
    setSalidaRPaso(PASOS_SALIDA_DIST_R[idx - 1]);
  }, [pasoSalidaRIndex]);

  const salidaPasosTotales = useMemo(() => {
    if (salidaServiciosAdicionales !== true) return 4;
    const nParsed = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
    const nn = Number.isFinite(nParsed) && nParsed >= 1 ? nParsed : 1;
    return 5 + 3 * nn;
  }, [salidaCantidadServicios, salidaServiciosAdicionales]);

  const salidaPasoIndice = useMemo(() => {
    const mapBase = { camion: 0, conductor: 1, dolly: 2, servicios: 3 };
    if (mapBase[salidaPaso] != null) return mapBase[salidaPaso];
    if (salidaPaso === 'cantidad') return 4;
    const svc = parseSalidaSvcPaso(salidaPaso);
    if (!svc || salidaServiciosAdicionales !== true) return 0;
    const nParsed = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
    const nn = Number.isFinite(nParsed) && nParsed >= 1 ? nParsed : 1;
    if (svc.idx < 0 || svc.idx >= nn) return 0;
    const base = 5;
    const off = svc.idx * 3 + (svc.part === 'codigo' ? 0 : svc.part === 'guia' ? 1 : 2);
    return base + off;
  }, [salidaCantidadServicios, salidaPaso, salidaServiciosAdicionales]);

  const resumenCabeceraRevision = useMemo(() => {
    if (revisionTipo === 'carga') return { tipo: 'Ingreso', opcion: 'Carga' };
    if (revisionTipo === 'ingreso_aire_distrib') {
      return { tipo: 'Ingreso', opcion: 'Z. Aire a Z. Distribución' };
    }
    if (revisionTipo === 'ingreso_alfa_distrib') {
      return { tipo: 'Ingreso', opcion: 'Z. Alfa a Z. Distribución' };
    }
    if (revisionTipo === 'salida_dist_aire') {
      return { tipo: 'Salida', opcion: 'Z. Distribución a Z. Aire' };
    }
    if (revisionTipo === 'salida_dist_transferencia') {
      return { tipo: 'Salida', opcion: 'Z. Distribución a Z. Transferencia' };
    }
    if (revisionTipo === 'salida_dist_alfa') {
      return { tipo: 'Salida', opcion: 'Z. Distribución a Z. Alfa' };
    }
    if (revisionTipo === 'salida_dist_r') {
      return { tipo: 'Salida', opcion: 'Z. Distribución a Z. R' };
    }
    if (revisionTipo === 'salida_alfa2') return { tipo: 'Salida', opcion: '' };
    return { tipo: '', opcion: '' };
  }, [revisionTipo]);
  const renderProgress = (label, idx, total) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{label}</span>
        <span>
          Paso {idx + 1} / {total}
        </span>
      </div>
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-indigo-600 transition-[width] duration-300 ease-out"
          style={{ width: `${((idx + 1) / total) * 100}%` }}
        />
      </div>
    </div>
  );

  const headerBg = envConfig.loginButtonColor || '#4f46e5';

  const countFotos = (slot) => (fotoFiles[slot] || []).length;

  const salidaRetroceder = useCallback(() => {
    stopCamera();
    setError('');
    if (salidaPaso === 'conductor') setSalidaPaso('camion');
    else if (salidaPaso === 'dolly') setSalidaPaso('conductor');
    else if (salidaPaso === 'servicios') setSalidaPaso('dolly');
    else if (salidaPaso === 'cantidad') setSalidaPaso('servicios');
    else {
      const svc = parseSalidaSvcPaso(salidaPaso);
      if (svc?.part === 'codigo') {
        if (svc.idx === 0) setSalidaPaso('cantidad');
        else setSalidaPaso(`svc_${svc.idx - 1}_bultos`);
      } else if (svc?.part === 'guia') setSalidaPaso(`svc_${svc.idx}_codigo`);
      else if (svc?.part === 'bultos') setSalidaPaso(`svc_${svc.idx}_guia`);
    }
  }, [salidaPaso, stopCamera]);

  const salidaContinuar = useCallback(() => {
    setError('');
    stopCamera();
    if (salidaPaso === 'camion') {
      if (!unidadSel || unidadSel === ADD_SENTINEL) {
        setError('Seleccione o agregue el camión / unidad.');
        return;
      }
      setSalidaPaso('conductor');
      return;
    }
    if (salidaPaso === 'conductor') {
      if (!salidaConductorNombre.trim()) {
        setError('Ingrese el nombre del conductor.');
        return;
      }
      setSalidaPaso('dolly');
      return;
    }
    if (salidaPaso === 'dolly') {
      const dRaw = String(salidaDollyQty || '').replace(/\D/g, '');
      const cRaw = String(salidaCarretasQty || '').replace(/\D/g, '');
      const d = dRaw === '' ? 0 : parseInt(dRaw, 10);
      const c = cRaw === '' ? 0 : parseInt(cRaw, 10);
      if (!Number.isFinite(d) || d < 0 || d > 999) {
        setError('Cantidad de dolly inválida (0–999).');
        return;
      }
      if (!Number.isFinite(c) || c < 0 || c > 999) {
        setError('Cantidad de carretas inválida (0–999).');
        return;
      }
      setSalidaPaso('servicios');
      return;
    }
    if (salidaPaso === 'cantidad') {
      const n = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
      if (!Number.isFinite(n) || n < 1) {
        setError('Indique el número de servicios especiales (solo números, mínimo 1).');
        return;
      }
      setSalidaSvcCodigos((prev) => Array.from({ length: n }, (_, i) => (prev[i] != null ? prev[i] : '')));
      setFotoFiles((prev) => ensureSalidaFotoSlotsInMap(prev, n));
      setFotoPreview((prev) => ensureSalidaFotoSlotsInMap(prev, n));
      setSalidaPaso('svc_0_codigo');
      return;
    }
    const svc = parseSalidaSvcPaso(salidaPaso);
    if (svc) {
      if (svc.part === 'codigo') {
        const cod = (salidaSvcCodigos[svc.idx] || '').trim();
        if (!cod) {
          setError('Ingrese el número / código del servicio adicional.');
          return;
        }
        setSalidaPaso(`svc_${svc.idx}_guia`);
        return;
      }
      if (svc.part === 'guia') {
        const slotG = `salida_svc_${svc.idx}_guia`;
        if ((fotoFiles[slotG] || []).length < 1) {
          setError('Adjunte al menos una foto de la guía master.');
          return;
        }
        setSalidaPaso(`svc_${svc.idx}_bultos`);
        return;
      }
      if (svc.part === 'bultos') {
        const slotB = `salida_svc_${svc.idx}_bultos`;
        if ((fotoFiles[slotB] || []).length < 1) {
          setError('Adjunte una foto general de los bultos.');
          return;
        }
        const nTot = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
        const nn = Number.isFinite(nTot) && nTot >= 1 ? nTot : 1;
        if (svc.idx + 1 < nn) {
          setSalidaPaso(`svc_${svc.idx + 1}_codigo`);
          return;
        }
        setRevisionTipo('salida_alfa2');
        openConfirmGuardar();
      }
    }
  }, [
    fotoFiles,
    openConfirmGuardar,
    salidaCantidadServicios,
    salidaCarretasQty,
    salidaConductorNombre,
    salidaDollyQty,
    salidaPaso,
    salidaSvcCodigos,
    stopCamera,
    unidadSel
  ]);

  const salidaSvcUi = parseSalidaSvcPaso(salidaPaso);

  if (user && user.rol !== 'V') return null;

  return (
    <Layout>
      <input
        ref={inputFotoMobileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFotoMobileChange}
      />
      <input
        ref={inputFotoDesktopRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFotoDesktopChange}
      />

      <div className="h-full min-h-full w-full flex flex-col overflow-auto p-3 sm:p-4 md:p-6">
        <div className="w-full max-w-lg mx-auto flex flex-col flex-1 min-h-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Alfa Tango 2</h1>
          {puedeBitacora === 'NO' && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              La bitácora no está habilitada para registrar hasta que haya apertura de turno.
            </p>
          )}
          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-800 text-sm">{success}</div>}

          {vista !== VISTA_INICIO && (
            <button
              type="button"
              onClick={irInicio}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium self-start mb-3"
            >
              ← Volver al menú principal
            </button>
          )}

          {vista === VISTA_INICIO && (
            <div className="grid gap-3">
              <button
                type="button"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  setVista(VISTA_MENU_INGRESO);
                }}
                className="w-full px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400"
              >
                Ingreso
              </button>
              <button
                type="button"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setVista(VISTA_MENU_SALIDA);
                }}
                className="w-full px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400"
              >
                Salida
              </button>
            </div>
          )}

          {vista === VISTA_MENU_INGRESO && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Ingreso</h2>
                <p className="text-sm text-gray-600 mt-1">Seleccione la ruta de ingreso.</p>
              </div>
              <BotonRutaAt2
                zona="aire"
                origen="Z. Aire"
                destino="Z. Distribución"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setVista(VISTA_INGRESO_AIRE_DISTRIB);
                }}
              />
              <BotonRutaAt2
                zona="alfa"
                origen="Z. Alfa"
                destino="Z. Distribución"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setIngresoAlfaPaso('operadores');
                  setVista(VISTA_INGRESO_ALFA_DISTRIB);
                }}
              />
            </div>
          )}

          {vista === VISTA_INGRESO_AIRE_DISTRIB && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress(
                'Ingreso — Z. Aire a Z. Distribución',
                pasoIngresoAireIndex >= 0 ? pasoIngresoAireIndex : 0,
                PASOS_INGRESO_AIRE_DISTRIB.length
              )}

              {ingresoAirePaso === 'vehiculo' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Vehículo</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo de vehículo e ingrese su número.</p>
                    <div className="flex flex-wrap gap-2">
                      {INGRESO_AIRE_VEHICULO_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            ingresoVehiculoTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-ingreso-vehiculo-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={ingresoVehiculoTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setIngresoVehiculoTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {ingresoVehiculoTipo === 'camion' && (
                <SelectConAgregar
                  titulo="Número de camión"
                  opciones={unidades}
                  valor={ingresoCamionSel}
                  onValorChange={(v) => {
                    setIngresoCamionSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaUnidad('');
                  }}
                  inputNuevo={inputNuevaUnidad}
                  onInputNuevoChange={setInputNuevaUnidad}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevaUnidad,
                      unidades,
                      setUnidades,
                      setIngresoCamionSel,
                      setInputNuevaUnidad,
                      'VEHICULO'
                    )
                  }
                  placeholderNuevo="Número de camión"
                  etiquetaAnadir="➕ Agregar camión"
                />
              )}

                  {ingresoVehiculoTipo === 'mula' && (
                <SelectConAgregar
                  titulo="Número de mula"
                  opciones={mulas}
                  valor={ingresoMulaSel}
                  onValorChange={(v) => {
                    setIngresoMulaSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaMula('');
                  }}
                  inputNuevo={inputNuevaMula}
                  onInputNuevoChange={setInputNuevaMula}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevaMula,
                      mulas,
                      setMulas,
                      setIngresoMulaSel,
                      setInputNuevaMula,
                      'ALFA_TANGO_2_MULA'
                    )
                  }
                  placeholderNuevo="Número de mula"
                  etiquetaAnadir="➕ Agregar mula"
                />
              )}

                  {ingresoVehiculoTipo === 'tractomula' && (
                <SelectConAgregar
                  titulo="Número de tractomula"
                  opciones={tractomulas}
                  valor={ingresoTractomulaSel}
                  onValorChange={(v) => {
                    setIngresoTractomulaSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaTractomula('');
                  }}
                  inputNuevo={inputNuevaTractomula}
                  onInputNuevoChange={setInputNuevaTractomula}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevaTractomula,
                      tractomulas,
                      setTractomulas,
                      setIngresoTractomulaSel,
                      setInputNuevaTractomula,
                      'ALFA_TANGO_2_TRACTOMULA'
                    )
                  }
                  placeholderNuevo="Número de tractomula"
                  etiquetaAnadir="➕ Agregar tractomula"
                />
                  )}
                </div>
              )}

              {ingresoAirePaso === 'personal' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Personal</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo e ingrese el dato correspondiente.</p>
                    <div className="flex flex-wrap gap-2">
                      {INGRESO_AIRE_PERSONAL_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            ingresoPersonalTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-ingreso-personal-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={ingresoPersonalTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setIngresoPersonalTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {ingresoPersonalTipo === 'operadores' && (
                  <SelectConAgregar
                    titulo="Operadores"
                    opciones={operadores}
                    valor={ingresoOperadorSierroSel}
                    onValorChange={(v) => {
                      setIngresoOperadorSierroSel(v);
                      if (v !== ADD_SENTINEL) setInputNuevoOperadorSierro('');
                    }}
                    inputNuevo={inputNuevoOperadorSierro}
                    onInputNuevoChange={setInputNuevoOperadorSierro}
                    onAnadir={() =>
                      addToListSelectAndCatalog(
                        inputNuevoOperadorSierro,
                        operadores,
                        setOperadores,
                        setIngresoOperadorSierroSel,
                        setInputNuevoOperadorSierro,
                        'ALFA_TANGO_2_OPERADOR'
                      )
                    }
                    placeholderNuevo="Operador"
                    etiquetaAnadir="➕ Agregar operador"
                  />
                  )}

                  {ingresoPersonalTipo === 'sierra_oscar' && (
                  <SelectConAgregar
                      titulo="Sierra Oscar"
                    opciones={sierroOscar}
                    valor={ingresoOperadorOscarSel}
                    onValorChange={(v) => {
                      setIngresoOperadorOscarSel(v);
                      if (v !== ADD_SENTINEL) setInputNuevoOperadorOscar('');
                    }}
                    inputNuevo={inputNuevoOperadorOscar}
                    onInputNuevoChange={setInputNuevoOperadorOscar}
                    onAnadir={() =>
                      addToListSelectAndCatalog(
                        inputNuevoOperadorOscar,
                        sierroOscar,
                        setSierroOscar,
                        setIngresoOperadorOscarSel,
                        setInputNuevoOperadorOscar,
                        'ALFA_TANGO_2_SIERRO_OSCAR'
                      )
                    }
                      placeholderNuevo="Sierra Oscar"
                      etiquetaAnadir="➕ Agregar Sierra Oscar"
                  />
              )}

                  {ingresoPersonalTipo === 'operador_sin_den' && (
                <div className="space-y-3">
                      <p className="text-lg font-semibold text-gray-900">Operadores sin denominativo</p>
                  <textarea
                    id="at2-op-sin-den"
                    rows={4}
                    value={ingresoOperadorSinDenominativo}
                    onChange={(e) => setIngresoOperadorSinDenominativo(e.target.value)}
                    className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                    placeholder="Ingrese operadores sin denominativo"
                  />
                </div>
              )}

                  {ingresoPersonalTipo === 'acompanantes' && (
                <SelectConAgregar
                  titulo="Acompañantes"
                  opciones={acompanantes}
                  valor={ingresoAcompananteSel}
                  onValorChange={(v) => {
                    setIngresoAcompananteSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoAcompanante('');
                  }}
                  inputNuevo={inputNuevoAcompanante}
                  onInputNuevoChange={setInputNuevoAcompanante}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevoAcompanante,
                      acompanantes,
                      setAcompanantes,
                      setIngresoAcompananteSel,
                      setInputNuevoAcompanante,
                      'ALFA_TANGO_2_ACOMPANANTE'
                    )
                  }
                  placeholderNuevo="Nombre de acompañante"
                  etiquetaAnadir="➕ Agregar acompañante"
                />
                  )}
                </div>
              )}

              {ingresoAirePaso === 'aerolinea_vuelo' && (
                <div className="space-y-5">
                  <SelectConAgregar
                    titulo="Aerolínea y vuelo"
                    descripcion="Seleccione la aerolínea e indique el número de vuelo correspondiente."
                    opciones={aerolineas}
                    valor={ingresoAerolineaSel}
                    onValorChange={(v) => {
                      setIngresoAerolineaSel(v);
                      if (v !== ADD_SENTINEL) setInputNuevaAerolinea('');
                    }}
                    inputNuevo={inputNuevaAerolinea}
                    onInputNuevoChange={setInputNuevaAerolinea}
                    onAnadir={() =>
                      addToListSelectAndCatalog(
                        inputNuevaAerolinea,
                        aerolineas,
                        setAerolineas,
                        setIngresoAerolineaSel,
                        setInputNuevaAerolinea,
                        'ALFA_TANGO_2_AEROLINEA'
                      )
                    }
                    placeholderNuevo="Nombre de aerolínea"
                    etiquetaAnadir="➕ Agregar aerolínea"
                  />
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="at2-num-vuelo">
                      Número de vuelo
                    </label>
                    <input
                      id="at2-num-vuelo"
                      type="text"
                      value={ingresoNumeroVuelo}
                      onChange={(e) => setIngresoNumeroVuelo(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      placeholder="Ej. AV1234"
                    />
                  </div>
                </div>
              )}

              {ingresoAirePaso === 'carga_tipos' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Observaciones</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Indique si hay contenedores, carretas, palets o correos y la cantidad cuando aplique.
                    </p>
                  </div>
                  <BloqueSiNoCantidad
                    titulo="¿Tiene contenedores?"
                    tiene={ingresoTieneContenedores}
                    setTiene={setIngresoTieneContenedores}
                    cantidad={ingresoCantidadContenedores}
                    setCantidad={setIngresoCantidadContenedores}
                    placeholderCantidad="Cantidad de contenedores"
                  />
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueSiNoCantidad
                      titulo="¿Tiene carretas?"
                      tiene={ingresoTieneCanetas}
                      setTiene={setIngresoTieneCanetas}
                      cantidad={ingresoCantidadCanetas}
                      setCantidad={setIngresoCantidadCanetas}
                      placeholderCantidad="Cantidad de carretas"
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueSiNoCantidad
                      titulo="¿Tiene palets?"
                      tiene={ingresoTienePalets}
                      setTiene={setIngresoTienePalets}
                      cantidad={ingresoCantidadPalets}
                      setCantidad={setIngresoCantidadPalets}
                      placeholderCantidad="Cantidad de palets"
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueSiNoCantidad
                      titulo="¿Tiene correos?"
                      tiene={ingresoTieneCorreos}
                      setTiene={setIngresoTieneCorreos}
                      cantidad={ingresoCantidadCorreos}
                      setCantidad={setIngresoCantidadCorreos}
                      placeholderCantidad="Cantidad de correos"
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="at2-ingreso-observaciones">
                      Observaciones (obligatorio)
                    </label>
                    <textarea
                      id="at2-ingreso-observaciones"
                      rows={4}
                      value={ingresoObservaciones}
                      onChange={(e) => setIngresoObservaciones(e.target.value)}
                      className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="¿Son primeros, hay más o son últimos?"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoIngresoAireIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      setIngresoAirePaso(PASOS_INGRESO_AIRE_DISTRIB[pasoIngresoAireIndex - 1]);
                    }}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    const msg = validarPasoIngresoAire(ingresoAirePaso);
                    if (msg) {
                      setError(msg);
                      return;
                    }
                    if (pasoIngresoAireIndex === PASOS_INGRESO_AIRE_DISTRIB.length - 1) {
                      setRevisionTipo('ingreso_aire_distrib');
                      openConfirmGuardar();
                      return;
                    }
                    setIngresoAirePaso(PASOS_INGRESO_AIRE_DISTRIB[pasoIngresoAireIndex + 1]);
                  }}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                    pasoIngresoAireIndex > 0 ? 'sm:flex-1' : ''
                  }`}
                >
                  {pasoIngresoAireIndex === PASOS_INGRESO_AIRE_DISTRIB.length - 1
                    ? 'Guardar en bitácora'
                    : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          {vista === VISTA_SALIDA_DIST_AIRE && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress(
                'Salida — Z. Distribución a Z. Aire',
                pasoSalidaDistIndex >= 0 ? pasoSalidaDistIndex : 0,
                totalPasosSalidaDist
              )}

              {salidaDistPaso === 'vehiculo' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Vehículo</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo de vehículo e ingrese su número.</p>
                    <div className="flex flex-wrap gap-2">
                      {INGRESO_AIRE_VEHICULO_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaDistVehiculoTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-salida-dist-vehiculo-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaDistVehiculoTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setSalidaDistVehiculoTipo(op.valor);
                              setSalidaDistTipoUnidad(
                                op.valor === 'mula' || op.valor === 'tractomula' ? op.valor : ''
                              );
                              if (op.valor === 'camion') {
                                setSalidaDistUnidadSel('');
                                setInputNuevaSalidaDistMula('');
                                setInputNuevaSalidaDistTractomula('');
                              } else {
                                setSalidaDistCamionSel('');
                                setInputNuevaSalidaDistCamion('');
                                setSalidaDistUnidadSel('');
                                setInputNuevaSalidaDistMula('');
                                setInputNuevaSalidaDistTractomula('');
                              }
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {salidaDistVehiculoTipo === 'camion' && (
                <SelectConAgregar
                  titulo="Número de camión"
                  opciones={unidades}
                  valor={salidaDistCamionSel}
                  onValorChange={(v) => {
                    setSalidaDistCamionSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaSalidaDistCamion('');
                  }}
                  inputNuevo={inputNuevaSalidaDistCamion}
                  onInputNuevoChange={setInputNuevaSalidaDistCamion}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevaSalidaDistCamion,
                      unidades,
                      setUnidades,
                      setSalidaDistCamionSel,
                      setInputNuevaSalidaDistCamion,
                      'VEHICULO'
                    )
                  }
                  placeholderNuevo="Número de camión"
                  etiquetaAnadir="➕ Agregar camión"
                />
              )}

                  {salidaDistVehiculoTipo === 'mula' && (
                    <SelectConAgregar
                      titulo="Número de mula"
                      opciones={mulas}
                      valor={salidaDistUnidadSel}
                      onValorChange={(v) => {
                        setSalidaDistUnidadSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaDistMula('');
                      }}
                      inputNuevo={inputNuevaSalidaDistMula}
                      onInputNuevoChange={setInputNuevaSalidaDistMula}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaDistMula,
                          mulas,
                          setMulas,
                          setSalidaDistUnidadSel,
                          setInputNuevaSalidaDistMula,
                          'ALFA_TANGO_2_MULA'
                        )
                      }
                      placeholderNuevo="Número de mula"
                      etiquetaAnadir="➕ Agregar mula"
                    />
                  )}

                  {salidaDistVehiculoTipo === 'tractomula' && (
                    <SelectConAgregar
                      titulo="Número de tractomula"
                      opciones={tractomulas}
                      valor={salidaDistUnidadSel}
                      onValorChange={(v) => {
                        setSalidaDistUnidadSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaDistTractomula('');
                      }}
                      inputNuevo={inputNuevaSalidaDistTractomula}
                      onInputNuevoChange={setInputNuevaSalidaDistTractomula}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaDistTractomula,
                          tractomulas,
                          setTractomulas,
                          setSalidaDistUnidadSel,
                          setInputNuevaSalidaDistTractomula,
                          'ALFA_TANGO_2_TRACTOMULA'
                        )
                      }
                      placeholderNuevo="Número de tractomula"
                      etiquetaAnadir="➕ Agregar tractomula"
                    />
                  )}
                </div>
              )}

              {salidaDistPaso === 'personal' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Personal</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo e ingrese el dato correspondiente.</p>
                    <div className="flex flex-wrap gap-2">
                      {SALIDA_DIST_AIRE_PERSONAL_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaDistPersonalTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-salida-dist-personal-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaDistPersonalTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setSalidaDistPersonalTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {salidaDistPersonalTipo === 'operadores' && (
                  <SelectConAgregar
                    titulo="Operadores"
                    opciones={operadores}
                    valor={salidaDistOperadorSierroSel}
                    onValorChange={(v) => {
                      setSalidaDistOperadorSierroSel(v);
                      if (v !== ADD_SENTINEL) setInputNuevoSalidaDistSierro('');
                    }}
                    inputNuevo={inputNuevoSalidaDistSierro}
                    onInputNuevoChange={setInputNuevoSalidaDistSierro}
                    onAnadir={() =>
                      addToListSelectAndCatalog(
                        inputNuevoSalidaDistSierro,
                        operadores,
                        setOperadores,
                        setSalidaDistOperadorSierroSel,
                        setInputNuevoSalidaDistSierro,
                        'ALFA_TANGO_2_OPERADOR'
                      )
                    }
                    placeholderNuevo="Operador"
                    etiquetaAnadir="➕ Agregar operador"
                  />
                  )}

                  {salidaDistPersonalTipo === 'sierra_oscar' && (
                  <SelectConAgregar
                      titulo="Sierra Oscar"
                    opciones={sierroOscar}
                    valor={salidaDistOperadorOscarSel}
                    onValorChange={(v) => {
                      setSalidaDistOperadorOscarSel(v);
                      if (v !== ADD_SENTINEL) setInputNuevoSalidaDistOscar('');
                    }}
                    inputNuevo={inputNuevoSalidaDistOscar}
                    onInputNuevoChange={setInputNuevoSalidaDistOscar}
                    onAnadir={() =>
                      addToListSelectAndCatalog(
                        inputNuevoSalidaDistOscar,
                        sierroOscar,
                        setSierroOscar,
                        setSalidaDistOperadorOscarSel,
                        setInputNuevoSalidaDistOscar,
                        'ALFA_TANGO_2_SIERRO_OSCAR'
                      )
                    }
                      placeholderNuevo="Sierra Oscar"
                      etiquetaAnadir="➕ Agregar Sierra Oscar"
                    />
                  )}

                  {salidaDistPersonalTipo === 'operador_sin_den' && (
                    <div className="space-y-3">
                      <p className="text-lg font-semibold text-gray-900">Operadores sin denominativo</p>
                      <textarea
                        id="at2-salida-dist-op-sin-den"
                        rows={4}
                        value={salidaDistOperadorSinDen}
                        onChange={(e) => setSalidaDistOperadorSinDen(e.target.value)}
                        className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                        placeholder="Ingrese operadores sin denominativo"
                  />
                </div>
              )}

                  {salidaDistPersonalTipo === 'acompanantes' && (
                <SelectConAgregar
                  titulo="Acompañamiento"
                  opciones={acompanantes}
                  valor={salidaDistAcompananteSel}
                  onValorChange={(v) => {
                    setSalidaDistAcompananteSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoSalidaDistAcompanante('');
                  }}
                  inputNuevo={inputNuevoSalidaDistAcompanante}
                  onInputNuevoChange={setInputNuevoSalidaDistAcompanante}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevoSalidaDistAcompanante,
                      acompanantes,
                      setAcompanantes,
                      setSalidaDistAcompananteSel,
                      setInputNuevoSalidaDistAcompanante,
                      'ALFA_TANGO_2_ACOMPANANTE'
                    )
                  }
                  placeholderNuevo="Nombre de acompañante"
                  etiquetaAnadir="➕ Agregar acompañante"
                />
              )}
                </div>
              )}

              {salidaDistPaso === 'carga_lleva' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Carga que lleva</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Indique para cada tipo si lleva carga y cuántas unidades cuando corresponda.
                    </p>
                  </div>
                  <BloqueSiNoCantidad
                    titulo="¿Lleva dolis vacíos?"
                    tiene={salidaDistDolisVacios}
                    setTiene={setSalidaDistDolisVacios}
                    cantidad={salidaDistCantDolisVacios}
                    setCantidad={setSalidaDistCantDolisVacios}
                    placeholderCantidad="Cantidad"
                  />
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueSiNoCantidad
                      titulo="¿Lleva carretas vacías?"
                      tiene={salidaDistCanetasVacias}
                      setTiene={setSalidaDistCanetasVacias}
                      cantidad={salidaDistCantCanetasVacias}
                      setCantidad={setSalidaDistCantCanetasVacias}
                      placeholderCantidad="Cantidad"
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="at2-salida-dist-observaciones"
                    >
                      Observaciones (obligatorio)
                    </label>
                    <textarea
                      id="at2-salida-dist-observaciones"
                      rows={4}
                      value={salidaDistObservaciones}
                      onChange={(e) => setSalidaDistObservaciones(e.target.value)}
                      className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="Ingrese observaciones"
                    />
                  </div>
                </div>
              )}

              {salidaDistPaso === 'servicios_especiales' && (
                <div className="space-y-3">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">¿Lleva servicios especiales?</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSalidaDistServiciosEspeciales(true)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
                        salidaDistServiciosEspeciales === true
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Sí
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSalidaDistServiciosEspeciales(false);
                        setSalidaDistCantidadServicios('');
                        setSalidaDistObservacionesServicios([]);
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
                        salidaDistServiciosEspeciales === false
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      No
                    </button>
                  </div>
                  {salidaDistServiciosEspeciales === true && (
                    <div className="space-y-2 border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-dist-cant-svc">
                      ¿Cuántos servicios especiales?
                    </label>
                    <input
                      id="at2-salida-dist-cant-svc"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={salidaDistCantidadServicios}
                        onChange={(e) => {
                          const nextRaw = soloDigitosMax(e.target.value, SALIDA_CANTIDAD_SVC_DIGITOS_MAX);
                          setSalidaDistCantidadServicios(nextRaw);
                        }}
                      className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      placeholder="Cantidad"
                    />
                  </div>
                  )}
                </div>
              )}

              {parseSalidaDistServicioPaso(salidaDistPaso) && (() => {
                const svc = parseSalidaDistServicioPaso(salidaDistPaso);
                if (!svc || svc.idx < 0) return null;
                const idx = svc.idx;
                const slotGuia = `salida_dist_svc_${idx}_guia`;
                const slotBultos = `salida_dist_svc_${idx}_bultos`;
                return (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      Servicio especial {idx + 1} de {salidaDistServiciosCount}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Complete la guía, fotos de bultos y observación de este servicio.
                    </p>
                  </div>
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <p className="text-sm font-semibold text-gray-900">Foto de guía (obligatorio, 1 foto)</p>
                    <BloqueFotosMultiAt2
                      destino={slotGuia}
                      useDeviceCamera={useDeviceCamera}
                      previewUrls={fotoPreview[slotGuia] || []}
                      previewAltBase={`Servicio ${idx + 1} guía`}
                      maxCount={1}
                      procesandoFoto={fotoProcesandoDestino === slotGuia}
                      cameraAbierta={cameraAbierta}
                      cameraDestinoActivo={cameraDestinoActivo}
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      onAbrirCapturaMovil={abrirCapturaMovil}
                      onIniciarCamaraEscritorio={startCameraDesktop}
                      onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                      onCapturarFotograma={capturarFotogramaDesktop}
                      onCerrarCamara={stopCamera}
                      onQuitarEnIndice={removeFotoAt}
                    />
                  </div>
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <p className="text-sm font-semibold text-gray-900">
                      Fotos de bultos (obligatorio, mínimo 1 y máximo 2)
                    </p>
                    <BloqueFotosMultiAt2
                      destino={slotBultos}
                      useDeviceCamera={useDeviceCamera}
                      previewUrls={fotoPreview[slotBultos] || []}
                      previewAltBase={`Servicio ${idx + 1} bultos`}
                      maxCount={2}
                      procesandoFoto={fotoProcesandoDestino === slotBultos}
                      cameraAbierta={cameraAbierta}
                      cameraDestinoActivo={cameraDestinoActivo}
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      onAbrirCapturaMovil={abrirCapturaMovil}
                      onIniciarCamaraEscritorio={startCameraDesktop}
                      onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                      onCapturarFotograma={capturarFotogramaDesktop}
                      onCerrarCamara={stopCamera}
                      onQuitarEnIndice={removeFotoAt}
                    />
                  </div>
                  <div className="space-y-2 border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700" htmlFor={`at2-salida-dist-obs-svc-${idx}`}>
                      Observación del servicio (obligatorio)
                    </label>
                    <textarea
                      id={`at2-salida-dist-obs-svc-${idx}`}
                      rows={3}
                      value={salidaDistObservacionesServicios[idx] || ''}
                      onChange={(e) =>
                        setSalidaDistObservacionesServicios((prev) => {
                          const next = Array.isArray(prev) ? [...prev] : [];
                          next[idx] = e.target.value;
                          return next;
                        })
                      }
                      className="w-full min-h-[5.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="Ej. cartones en buen estado con plástico, sellos intactos, etiquetado visible…"
                    />
                  </div>
                </div>
              );
              })()}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoSalidaDistIndex > 0 && (
                  <button
                    type="button"
                    onClick={salidaDistRetroceder}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={salidaDistContinuar}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                    pasoSalidaDistIndex > 0 ? 'sm:flex-1' : ''
                  }`}
                >
                  {salidaDistBotonPrincipalLabel}
                </button>
              </div>
            </div>
          )}

          {vista === VISTA_SALIDA_DIST_TRANSFERENCIA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress(
                'Salida — Z. Distribución a Z. Transferencia',
                pasoSalidaXferIndex >= 0 ? pasoSalidaXferIndex : 0,
                totalPasosSalidaXfer
              )}

              {salidaXferPaso === 'vehiculo' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Vehículo</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo de vehículo e ingrese su número.</p>
                    <div className="flex flex-wrap gap-2">
                      {INGRESO_AIRE_VEHICULO_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaXferVehiculoTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-salida-xfer-vehiculo-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaXferVehiculoTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setSalidaXferVehiculoTipo(op.valor);
                              setSalidaXferTipoUnidad(
                                op.valor === 'mula' || op.valor === 'tractomula' ? op.valor : ''
                              );
                              if (op.valor === 'camion') {
                                setSalidaXferUnidadSel('');
                                setInputNuevaSalidaXferMula('');
                                setInputNuevaSalidaXferTractomula('');
                              } else {
                                setSalidaXferCamionSel('');
                                setInputNuevaSalidaXferCamion('');
                                setSalidaXferUnidadSel('');
                                setInputNuevaSalidaXferMula('');
                                setInputNuevaSalidaXferTractomula('');
                              }
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {salidaXferVehiculoTipo === 'camion' && (
                    <SelectConAgregar
                      titulo="Número de camión"
                      opciones={unidades}
                      valor={salidaXferCamionSel}
                      onValorChange={(v) => {
                        setSalidaXferCamionSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaXferCamion('');
                      }}
                      inputNuevo={inputNuevaSalidaXferCamion}
                      onInputNuevoChange={setInputNuevaSalidaXferCamion}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaXferCamion,
                          unidades,
                          setUnidades,
                          setSalidaXferCamionSel,
                          setInputNuevaSalidaXferCamion,
                          'VEHICULO'
                        )
                      }
                      placeholderNuevo="Número de camión"
                      etiquetaAnadir="➕ Agregar camión"
                    />
                  )}

                  {salidaXferVehiculoTipo === 'mula' && (
                    <SelectConAgregar
                      titulo="Número de mula"
                      opciones={mulas}
                      valor={salidaXferUnidadSel}
                      onValorChange={(v) => {
                        setSalidaXferUnidadSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaXferMula('');
                      }}
                      inputNuevo={inputNuevaSalidaXferMula}
                      onInputNuevoChange={setInputNuevaSalidaXferMula}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaXferMula,
                          mulas,
                          setMulas,
                          setSalidaXferUnidadSel,
                          setInputNuevaSalidaXferMula,
                          'ALFA_TANGO_2_MULA'
                        )
                      }
                      placeholderNuevo="Número de mula"
                      etiquetaAnadir="➕ Agregar mula"
                    />
                  )}

                  {salidaXferVehiculoTipo === 'tractomula' && (
                    <SelectConAgregar
                      titulo="Número de tractomula"
                      opciones={tractomulas}
                      valor={salidaXferUnidadSel}
                      onValorChange={(v) => {
                        setSalidaXferUnidadSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaXferTractomula('');
                      }}
                      inputNuevo={inputNuevaSalidaXferTractomula}
                      onInputNuevoChange={setInputNuevaSalidaXferTractomula}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaXferTractomula,
                          tractomulas,
                          setTractomulas,
                          setSalidaXferUnidadSel,
                          setInputNuevaSalidaXferTractomula,
                          'ALFA_TANGO_2_TRACTOMULA'
                        )
                      }
                      placeholderNuevo="Número de tractomula"
                      etiquetaAnadir="➕ Agregar tractomula"
                    />
                  )}
                </div>
              )}

              {salidaXferPaso === 'personal' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Personal</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo e ingrese el dato correspondiente.</p>
                    <div className="flex flex-wrap gap-2">
                      {SALIDA_DIST_AIRE_PERSONAL_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaXferPersonalTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-salida-xfer-personal-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaXferPersonalTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setSalidaXferPersonalTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {salidaXferPersonalTipo === 'operadores' && (
                    <SelectConAgregar
                      titulo="Operadores"
                      opciones={operadores}
                      valor={salidaXferOperadorSierroSel}
                      onValorChange={(v) => {
                        setSalidaXferOperadorSierroSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevoSalidaXferSierro('');
                      }}
                      inputNuevo={inputNuevoSalidaXferSierro}
                      onInputNuevoChange={setInputNuevoSalidaXferSierro}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevoSalidaXferSierro,
                          operadores,
                          setOperadores,
                          setSalidaXferOperadorSierroSel,
                          setInputNuevoSalidaXferSierro,
                          'ALFA_TANGO_2_OPERADOR'
                        )
                      }
                      placeholderNuevo="Operador"
                      etiquetaAnadir="➕ Agregar operador"
                    />
                  )}

                  {salidaXferPersonalTipo === 'sierra_oscar' && (
                    <SelectConAgregar
                      titulo="Sierra Oscar"
                      opciones={sierroOscar}
                      valor={salidaXferOperadorOscarSel}
                      onValorChange={(v) => {
                        setSalidaXferOperadorOscarSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevoSalidaXferOscar('');
                      }}
                      inputNuevo={inputNuevoSalidaXferOscar}
                      onInputNuevoChange={setInputNuevoSalidaXferOscar}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevoSalidaXferOscar,
                          sierroOscar,
                          setSierroOscar,
                          setSalidaXferOperadorOscarSel,
                          setInputNuevoSalidaXferOscar,
                          'ALFA_TANGO_2_SIERRO_OSCAR'
                        )
                      }
                      placeholderNuevo="Sierra Oscar"
                      etiquetaAnadir="➕ Agregar Sierra Oscar"
                    />
                  )}

                  {salidaXferPersonalTipo === 'operador_sin_den' && (
                    <div className="space-y-3">
                      <p className="text-lg font-semibold text-gray-900">Operadores sin denominativo</p>
                      <textarea
                        id="at2-salida-xfer-op-sin-den"
                        rows={4}
                        value={salidaXferOperadorSinDen}
                        onChange={(e) => setSalidaXferOperadorSinDen(e.target.value)}
                        className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                        placeholder="Ingrese operadores sin denominativo"
                      />
                    </div>
                  )}

                  {salidaXferPersonalTipo === 'acompanantes' && (
                    <SelectConAgregar
                      titulo="Acompañamiento"
                      opciones={acompanantes}
                      valor={salidaXferAcompananteSel}
                      onValorChange={(v) => {
                        setSalidaXferAcompananteSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevoSalidaXferAcompanante('');
                      }}
                      inputNuevo={inputNuevoSalidaXferAcompanante}
                      onInputNuevoChange={setInputNuevoSalidaXferAcompanante}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevoSalidaXferAcompanante,
                          acompanantes,
                          setAcompanantes,
                          setSalidaXferAcompananteSel,
                          setInputNuevoSalidaXferAcompanante,
                          'ALFA_TANGO_2_ACOMPANANTE'
                        )
                      }
                      placeholderNuevo="Nombre de acompañante"
                      etiquetaAnadir="➕ Agregar acompañante"
                    />
                  )}
                </div>
              )}

              {salidaXferPaso === 'carga_lleva' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Carga que lleva</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Indique para cada tipo si lleva carga y cuántas unidades cuando corresponda.
                    </p>
                  </div>
                  <BloqueSiNoCantidad
                    titulo="¿Lleva dolis vacíos?"
                    tiene={salidaXferDolisVacios}
                    setTiene={setSalidaXferDolisVacios}
                    cantidad={salidaXferCantDolisVacios}
                    setCantidad={setSalidaXferCantDolisVacios}
                    placeholderCantidad="Cantidad"
                  />
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueSiNoCantidad
                      titulo="¿Lleva carretas vacías?"
                      tiene={salidaXferCanetasVacias}
                      setTiene={setSalidaXferCanetasVacias}
                      cantidad={salidaXferCantCanetasVacias}
                      setCantidad={setSalidaXferCantCanetasVacias}
                      placeholderCantidad="Cantidad"
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="at2-salida-xfer-observaciones"
                    >
                      Observaciones (obligatorio)
                    </label>
                    <textarea
                      id="at2-salida-xfer-observaciones"
                      rows={4}
                      value={salidaXferObservaciones}
                      onChange={(e) => setSalidaXferObservaciones(e.target.value)}
                      className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="Ingrese observaciones"
                    />
                  </div>
                </div>
              )}

              {salidaXferPaso === 'servicios_especiales' && (
                <div className="space-y-3">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">¿Lleva servicios especiales?</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSalidaXferServiciosEspeciales(true)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
                        salidaXferServiciosEspeciales === true
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Sí
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSalidaXferServiciosEspeciales(false);
                        setSalidaXferCantidadServicios('');
                        setSalidaXferObservacionesServicios([]);
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium shrink-0 ${
                        salidaXferServiciosEspeciales === false
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      No
                    </button>
                  </div>
                  {salidaXferServiciosEspeciales === true && (
                    <div className="space-y-2 border-t border-gray-100 pt-4">
                      <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-xfer-cant-svc">
                        ¿Cuántos servicios especiales?
                      </label>
                      <input
                        id="at2-salida-xfer-cant-svc"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={salidaXferCantidadServicios}
                        onChange={(e) => {
                          const nextRaw = soloDigitosMax(e.target.value, SALIDA_CANTIDAD_SVC_DIGITOS_MAX);
                          setSalidaXferCantidadServicios(nextRaw);
                        }}
                        className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        placeholder="Cantidad"
                      />
                    </div>
                  )}
                </div>
              )}

              {parseSalidaDistServicioPaso(salidaXferPaso) && (() => {
                const svc = parseSalidaDistServicioPaso(salidaXferPaso);
                if (!svc || svc.idx < 0) return null;
                const idx = svc.idx;
                const slotGuia = `salida_xfer_svc_${idx}_guia`;
                const slotBultos = `salida_xfer_svc_${idx}_bultos`;
                return (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      Servicio especial {idx + 1} de {salidaXferServiciosCount}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Complete la guía, fotos de bultos y observación de este servicio.
                    </p>
                  </div>
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <p className="text-sm font-semibold text-gray-900">Foto de guía (obligatorio, 1 foto)</p>
                    <BloqueFotosMultiAt2
                      destino={slotGuia}
                      useDeviceCamera={useDeviceCamera}
                      previewUrls={fotoPreview[slotGuia] || []}
                      previewAltBase={`Servicio ${idx + 1} guía`}
                      maxCount={1}
                      procesandoFoto={fotoProcesandoDestino === slotGuia}
                      cameraAbierta={cameraAbierta}
                      cameraDestinoActivo={cameraDestinoActivo}
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      onAbrirCapturaMovil={abrirCapturaMovil}
                      onIniciarCamaraEscritorio={startCameraDesktop}
                      onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                      onCapturarFotograma={capturarFotogramaDesktop}
                      onCerrarCamara={stopCamera}
                      onQuitarEnIndice={removeFotoAt}
                    />
                  </div>
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <p className="text-sm font-semibold text-gray-900">
                      Fotos de bultos (obligatorio, mínimo 1 y máximo 2)
                    </p>
                    <BloqueFotosMultiAt2
                      destino={slotBultos}
                      useDeviceCamera={useDeviceCamera}
                      previewUrls={fotoPreview[slotBultos] || []}
                      previewAltBase={`Servicio ${idx + 1} bultos`}
                      maxCount={2}
                      procesandoFoto={fotoProcesandoDestino === slotBultos}
                      cameraAbierta={cameraAbierta}
                      cameraDestinoActivo={cameraDestinoActivo}
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      onAbrirCapturaMovil={abrirCapturaMovil}
                      onIniciarCamaraEscritorio={startCameraDesktop}
                      onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                      onCapturarFotograma={capturarFotogramaDesktop}
                      onCerrarCamara={stopCamera}
                      onQuitarEnIndice={removeFotoAt}
                    />
                  </div>
                  <div className="space-y-2 border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700" htmlFor={`at2-salida-xfer-obs-svc-${idx}`}>
                      Observación del servicio (obligatorio)
                    </label>
                    <textarea
                      id={`at2-salida-xfer-obs-svc-${idx}`}
                      rows={3}
                      value={salidaXferObservacionesServicios[idx] || ''}
                      onChange={(e) =>
                        setSalidaXferObservacionesServicios((prev) => {
                          const next = Array.isArray(prev) ? [...prev] : [];
                          next[idx] = e.target.value;
                          return next;
                        })
                      }
                      className="w-full min-h-[5.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="Ej. cartones en buen estado con plástico, sellos intactos, etiquetado visible…"
                    />
                  </div>
                </div>
              );
              })()}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoSalidaXferIndex > 0 && (
                  <button
                    type="button"
                    onClick={salidaXferRetroceder}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={salidaXferContinuar}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                    pasoSalidaXferIndex > 0 ? 'sm:flex-1' : ''
                  }`}
                >
                  {salidaXferBotonPrincipalLabel}
                </button>
              </div>
            </div>
          )}

          {vista === VISTA_SALIDA_DIST_ALFA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress(
                'Salida — Z. Distribución a Z. Alfa',
                pasoSalidaAlfaIndex >= 0 ? pasoSalidaAlfaIndex : 0,
                PASOS_SALIDA_DIST_ALFA.length
              )}

              {salidaAlfaPaso === 'operadores' && (
                <SelectConAgregar
                  titulo="Operadores"
                  opciones={operadores}
                  valor={salidaAlfaOperadorSel}
                  onValorChange={(v) => {
                    setSalidaAlfaOperadorSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoSalidaAlfaOperador('');
                  }}
                  inputNuevo={inputNuevoSalidaAlfaOperador}
                  onInputNuevoChange={setInputNuevoSalidaAlfaOperador}
                  onAnadir={() =>
                    addToListAndSelect(
                      inputNuevoSalidaAlfaOperador,
                      operadores,
                      setOperadores,
                      setSalidaAlfaOperadorSel,
                      setInputNuevoSalidaAlfaOperador
                    )
                  }
                  placeholderNuevo="Operador"
                  etiquetaAnadir="➕ Agregar operador"
                />
              )}

              {salidaAlfaPaso === 'vehiculo' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Vehículo</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo de vehículo e ingrese su número.</p>
                    <div className="flex flex-wrap gap-2">
                      {SALIDA_ALFA_VEHICULO_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaAlfaVehiculoTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-salida-alfa-vehiculo-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaAlfaVehiculoTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setSalidaAlfaVehiculoTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {salidaAlfaVehiculoTipo === 'camion' && (
                <SelectConAgregar
                  titulo="Número de camión"
                  opciones={unidades}
                  valor={salidaAlfaCamionSel}
                  onValorChange={(v) => {
                    setSalidaAlfaCamionSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaSalidaAlfaCamion('');
                  }}
                  inputNuevo={inputNuevaSalidaAlfaCamion}
                  onInputNuevoChange={setInputNuevaSalidaAlfaCamion}
                  onAnadir={() =>
                    addToListAndSelect(
                      inputNuevaSalidaAlfaCamion,
                      unidades,
                      setUnidades,
                      setSalidaAlfaCamionSel,
                      setInputNuevaSalidaAlfaCamion
                    )
                  }
                  placeholderNuevo="Número de camión"
                  etiquetaAnadir="➕ Agregar camión"
                />
              )}

                  {salidaAlfaVehiculoTipo === 'mula' && (
                <SelectConAgregar
                  titulo="Número de mula"
                  opciones={mulas}
                  valor={salidaAlfaMulaSel}
                  onValorChange={(v) => {
                    setSalidaAlfaMulaSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaSalidaAlfaMula('');
                  }}
                  inputNuevo={inputNuevaSalidaAlfaMula}
                  onInputNuevoChange={setInputNuevaSalidaAlfaMula}
                  onAnadir={() =>
                    addToListAndSelect(
                      inputNuevaSalidaAlfaMula,
                      mulas,
                      setMulas,
                      setSalidaAlfaMulaSel,
                      setInputNuevaSalidaAlfaMula
                    )
                  }
                  placeholderNuevo="Número de mula"
                  etiquetaAnadir="➕ Agregar mula"
                />
                  )}

                  {salidaAlfaVehiculoTipo === 'montacargas' && (
                    <SelectConAgregar
                      titulo="Montacargas"
                      descripcion="Seleccione de la lista o agregue un registro."
                      opciones={montacargas}
                      valor={salidaAlfaMontacargasSel}
                      onValorChange={(v) => {
                        setSalidaAlfaMontacargasSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaAlfaMontacargas('');
                      }}
                      inputNuevo={inputNuevaSalidaAlfaMontacargas}
                      onInputNuevoChange={setInputNuevaSalidaAlfaMontacargas}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaAlfaMontacargas,
                          montacargas,
                          setMontacargas,
                          setSalidaAlfaMontacargasSel,
                          setInputNuevaSalidaAlfaMontacargas,
                          'ALFA_TANGO_2_MONTACARGAS'
                        )
                      }
                      placeholderNuevo="Identificador de montacargas"
                      etiquetaAnadir="➕ Agregar montacargas"
                    />
                  )}
                </div>
              )}

              {salidaAlfaPaso === 'jacpalet' && (
                <SelectConAgregar
                  titulo="JacPalet"
                  descripcion="Seleccione de la lista o agregue un registro."
                  opciones={jacPalets}
                  valor={salidaAlfaJacpaletSel}
                  onValorChange={(v) => {
                    setSalidaAlfaJacpaletSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaSalidaAlfaJacpalet('');
                  }}
                  inputNuevo={inputNuevaSalidaAlfaJacpalet}
                  onInputNuevoChange={setInputNuevaSalidaAlfaJacpalet}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevaSalidaAlfaJacpalet,
                      jacPalets,
                      setJacPalets,
                      setSalidaAlfaJacpaletSel,
                      setInputNuevaSalidaAlfaJacpalet,
                      'ALFA_TANGO_2_JAC_PALET'
                    )
                  }
                  placeholderNuevo="Número o identificador JacPalet"
                  etiquetaAnadir="➕ Agregar JacPalet"
                />
              )}

              {salidaAlfaPaso === 'jacpalet_manual' && (
                <div className="space-y-3">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">JacPalet manual</p>
                  <p className="text-sm text-gray-600">
                    Registro aparte del catálogo JacPalet.
                  </p>
                  <input
                    id="at2-salida-alfa-jac-manual"
                    type="text"
                    value={salidaAlfaJacpaletManual}
                    onChange={(e) => setSalidaAlfaJacpaletManual(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Número o identificador JacPalet manual"
                  />
                </div>
              )}

              {salidaAlfaPaso === 'observaciones' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Observaciones y carga</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Indique dolis, carretas y el detalle adicional (todos obligatorios).
                    </p>
                  </div>
                <BloqueTipoCantidadAt2
                  titulo="¿Lleva dolis?"
                  tipo={salidaAlfaDolisTipo}
                  setTipo={setSalidaAlfaDolisTipo}
                  cantidad={salidaAlfaCantDolis}
                  setCantidad={setSalidaAlfaCantDolis}
                  placeholderCantidad="Cantidad"
                  opciones={[
                    { value: 'no', label: 'No' },
                    { value: 'vacio', label: 'Vacíos' },
                    { value: 'lleno', label: 'Llenos' }
                  ]}
                />
                  <div className="border-t border-gray-100 pt-4">
                <BloqueTipoCantidadAt2
                      titulo="¿Lleva carretas?"
                  tipo={salidaAlfaCanetasTipo}
                  setTipo={setSalidaAlfaCanetasTipo}
                  cantidad={salidaAlfaCantCanetas}
                  setCantidad={setSalidaAlfaCantCanetas}
                  placeholderCantidad="Cantidad"
                  opciones={[
                    { value: 'no', label: 'No' },
                    { value: 'vacia', label: 'Vacías' },
                    { value: 'llena', label: 'Llenas' }
                  ]}
                />
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="at2-salida-alfa-observaciones"
                    >
                      Observaciones (obligatorio)
                    </label>
                  <textarea
                    id="at2-salida-alfa-observaciones"
                    rows={4}
                    value={salidaAlfaObservaciones}
                    onChange={(e) => setSalidaAlfaObservaciones(e.target.value)}
                    className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                    placeholder="Ej. desaduanamiento directo, traer palets…"
                  />
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoSalidaAlfaIndex > 0 && (
                  <button
                    type="button"
                    onClick={salidaAlfaRetroceder}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={salidaAlfaContinuar}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                    pasoSalidaAlfaIndex > 0 ? 'sm:flex-1' : ''
                  }`}
                >
                  {pasoSalidaAlfaIndex === PASOS_SALIDA_DIST_ALFA.length - 1
                    ? 'Guardar en bitácora'
                    : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          
          {vista === VISTA_INGRESO_ALFA_DISTRIB && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress(
                'Ingreso — Z. Alfa a Z. Distribución',
                pasoIngresoAlfaIndex >= 0 ? pasoIngresoAlfaIndex : 0,
                PASOS_INGRESO_ALFA_DISTRIB.length
              )}

              {ingresoAlfaPaso === 'operadores' && (
                <SelectConAgregar
                  titulo="Operadores"
                  opciones={operadores}
                  valor={ingresoAlfaOperadorSel}
                  onValorChange={(v) => {
                    setIngresoAlfaOperadorSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoIngresoAlfaOperador('');
                  }}
                  inputNuevo={inputNuevoIngresoAlfaOperador}
                  onInputNuevoChange={setInputNuevoIngresoAlfaOperador}
                  onAnadir={() =>
                    addToListAndSelect(
                      inputNuevoIngresoAlfaOperador,
                      operadores,
                      setOperadores,
                      setIngresoAlfaOperadorSel,
                      setInputNuevoIngresoAlfaOperador
                    )
                  }
                  placeholderNuevo="Operador"
                  etiquetaAnadir="➕ Agregar operador"
                />
              )}

              {ingresoAlfaPaso === 'vehiculo' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Vehículo</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo de vehículo e ingrese su número.</p>
                    <div className="flex flex-wrap gap-2">
                      {INGRESO_ALFA_VEHICULO_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            ingresoAlfaVehiculoTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-ingreso-alfa-vehiculo-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={ingresoAlfaVehiculoTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setIngresoAlfaVehiculoTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {ingresoAlfaVehiculoTipo === 'camion' && (
                    <SelectConAgregar
                      titulo="Número de camión"
                      opciones={unidades}
                      valor={ingresoAlfaCamionSel}
                      onValorChange={(v) => {
                        setIngresoAlfaCamionSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaIngresoAlfaCamion('');
                      }}
                      inputNuevo={inputNuevaIngresoAlfaCamion}
                      onInputNuevoChange={setInputNuevaIngresoAlfaCamion}
                      onAnadir={() =>
                        addToListAndSelect(
                          inputNuevaIngresoAlfaCamion,
                          unidades,
                          setUnidades,
                          setIngresoAlfaCamionSel,
                          setInputNuevaIngresoAlfaCamion
                        )
                      }
                      placeholderNuevo="Número de camión"
                      etiquetaAnadir="➕ Agregar camión"
                    />
                  )}

                  {ingresoAlfaVehiculoTipo === 'mula' && (
                    <SelectConAgregar
                      titulo="Número de mula"
                      opciones={mulas}
                      valor={ingresoAlfaMulaSel}
                      onValorChange={(v) => {
                        setIngresoAlfaMulaSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaIngresoAlfaMula('');
                      }}
                      inputNuevo={inputNuevaIngresoAlfaMula}
                      onInputNuevoChange={setInputNuevaIngresoAlfaMula}
                      onAnadir={() =>
                        addToListAndSelect(
                          inputNuevaIngresoAlfaMula,
                          mulas,
                          setMulas,
                          setIngresoAlfaMulaSel,
                          setInputNuevaIngresoAlfaMula
                        )
                      }
                      placeholderNuevo="Número de mula"
                      etiquetaAnadir="➕ Agregar mula"
                    />
                  )}

                  {ingresoAlfaVehiculoTipo === 'tractomula' && (
                    <SelectConAgregar
                      titulo="Número de tractomula"
                      opciones={tractomulas}
                      valor={ingresoAlfaTractomulaSel}
                      onValorChange={(v) => {
                        setIngresoAlfaTractomulaSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaTractomula('');
                      }}
                      inputNuevo={inputNuevaTractomula}
                      onInputNuevoChange={setInputNuevaTractomula}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaTractomula,
                          tractomulas,
                          setTractomulas,
                          setIngresoAlfaTractomulaSel,
                          setInputNuevaTractomula,
                          'ALFA_TANGO_2_TRACTOMULA'
                        )
                      }
                      placeholderNuevo="Número de tractomula"
                      etiquetaAnadir="➕ Agregar tractomula"
                    />
                  )}

                  {ingresoAlfaVehiculoTipo === 'montacargas' && (
                    <SelectConAgregar
                      titulo="Montacargas"
                      descripcion="Seleccione de la lista o agregue un registro."
                      opciones={montacargas}
                      valor={ingresoAlfaMontacargasSel}
                      onValorChange={(v) => {
                        setIngresoAlfaMontacargasSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaIngresoAlfaMontacargas('');
                      }}
                      inputNuevo={inputNuevaIngresoAlfaMontacargas}
                      onInputNuevoChange={setInputNuevaIngresoAlfaMontacargas}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaIngresoAlfaMontacargas,
                          montacargas,
                          setMontacargas,
                          setIngresoAlfaMontacargasSel,
                          setInputNuevaIngresoAlfaMontacargas,
                          'ALFA_TANGO_2_MONTACARGAS'
                        )
                      }
                      placeholderNuevo="Identificador de montacargas"
                      etiquetaAnadir="➕ Agregar montacargas"
                    />
                  )}
                </div>
              )}

              {ingresoAlfaPaso === 'jacpalet' && (
                <SelectConAgregar
                  titulo="JacPalet"
                  descripcion="Seleccione de la lista o agregue un registro."
                  opciones={jacPalets}
                  valor={ingresoAlfaJacpaletSel}
                  onValorChange={(v) => {
                    setIngresoAlfaJacpaletSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaIngresoAlfaJacpalet('');
                  }}
                  inputNuevo={inputNuevaIngresoAlfaJacpalet}
                  onInputNuevoChange={setInputNuevaIngresoAlfaJacpalet}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevaIngresoAlfaJacpalet,
                      jacPalets,
                      setJacPalets,
                      setIngresoAlfaJacpaletSel,
                      setInputNuevaIngresoAlfaJacpalet,
                      'ALFA_TANGO_2_JAC_PALET'
                    )
                  }
                  placeholderNuevo="Número o identificador JacPalet"
                  etiquetaAnadir="➕ Agregar JacPalet"
                />
              )}

              {ingresoAlfaPaso === 'jacpalet_manual' && (
                <div className="space-y-3">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">JacPalet manual</p>
                  <p className="text-sm text-gray-600">
                    Registro aparte del catálogo JacPalet.
                  </p>
                  <input
                    id="at2-ingreso-alfa-jac-manual"
                    type="text"
                    value={ingresoAlfaJacpaletManual}
                    onChange={(e) => setIngresoAlfaJacpaletManual(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Número o identificador JacPalet manual"
                  />
                </div>
              )}

              {ingresoAlfaPaso === 'observaciones' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Observaciones y carga</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Indique dolis, carretas y el detalle adicional (todos obligatorios).
                    </p>
                  </div>
                  <BloqueTipoCantidadAt2
                    titulo="¿Lleva dolis?"
                    tipo={ingresoAlfaDolisTipo}
                    setTipo={setIngresoAlfaDolisTipo}
                    cantidad={ingresoAlfaCantDolis}
                    setCantidad={setIngresoAlfaCantDolis}
                    placeholderCantidad="Cantidad"
                    opciones={[
                      { value: 'no', label: 'No' },
                      { value: 'vacio', label: 'Vacíos' },
                      { value: 'lleno', label: 'Llenos' }
                    ]}
                  />
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueTipoCantidadAt2
                      titulo="¿Lleva carretas?"
                      tipo={ingresoAlfaCanetasTipo}
                      setTipo={setIngresoAlfaCanetasTipo}
                      cantidad={ingresoAlfaCantCanetas}
                      setCantidad={setIngresoAlfaCantCanetas}
                      placeholderCantidad="Cantidad"
                      opciones={[
                        { value: 'no', label: 'No' },
                        { value: 'vacia', label: 'Vacías' },
                        { value: 'llena', label: 'Llenas' }
                      ]}
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="at2-ingreso-alfa-observaciones"
                    >
                      Observaciones (obligatorio)
                    </label>
                    <textarea
                      id="at2-ingreso-alfa-observaciones"
                      rows={4}
                      value={ingresoAlfaObservaciones}
                      onChange={(e) => setIngresoAlfaObservaciones(e.target.value)}
                      className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="Ej. desaduanamiento directo, traer palets…"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoIngresoAlfaIndex > 0 && (
                  <button
                    type="button"
                    onClick={ingresoAlfaRetroceder}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={ingresoAlfaContinuar}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                    pasoIngresoAlfaIndex > 0 ? 'sm:flex-1' : ''
                  }`}
                >
                  {pasoIngresoAlfaIndex === PASOS_INGRESO_ALFA_DISTRIB.length - 1
                    ? 'Guardar en bitácora'
                    : 'Continuar'}
                </button>
              </div>
            </div>
          )}


          {vista === VISTA_SALIDA_DIST_R && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress(
                'Salida — Z. Distribución a Z. R',
                pasoSalidaRIndex >= 0 ? pasoSalidaRIndex : 0,
                PASOS_SALIDA_DIST_R.length
              )}

              {salidaRPaso === 'personal' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Personal</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo e ingrese el dato correspondiente.</p>
                    <div className="flex flex-wrap gap-2">
                      {SALIDA_R_PERSONAL_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaRPersonalTipo === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="at2-salida-r-personal-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaRPersonalTipo === op.valor}
                            onChange={() => {
                              setError('');
                              setSalidaRPersonalTipo(op.valor);
                            }}
                          />
                          {op.etiqueta}
                        </label>
                      ))}
                    </div>
                  </div>

                  {salidaRPersonalTipo === 'operador' && (
                <SelectConAgregar
                  titulo="Operador"
                  opciones={operadores}
                  valor={salidaROperadorSel}
                  onValorChange={(v) => {
                    setSalidaROperadorSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoSalidaROperador('');
                  }}
                  inputNuevo={inputNuevoSalidaROperador}
                  onInputNuevoChange={setInputNuevoSalidaROperador}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevoSalidaROperador,
                      operadores,
                      setOperadores,
                      setSalidaROperadorSel,
                      setInputNuevoSalidaROperador,
                      'ALFA_TANGO_2_OPERADOR'
                    )
                  }
                  placeholderNuevo="Operador"
                  etiquetaAnadir="➕ Agregar operador"
                />
              )}

                  {salidaRPersonalTipo === 'estivador' && (
                <div className="space-y-3">
                      <p className="text-lg font-semibold text-gray-900">Estivador</p>
                      <p className="text-sm text-gray-600">Ingrese el nombre o identificador del estivador.</p>
                  <input
                    id="at2-salida-r-estrivador"
                    type="text"
                    value={salidaREstrivador}
                    onChange={(e) => setSalidaREstrivador(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        placeholder="Estivador"
                  />
                </div>
              )}

                  {salidaRPersonalTipo === 'sierra_oscar' && (
                <SelectConAgregar
                      titulo="Sierra Oscar"
                  opciones={sierroOscar}
                  valor={salidaRSierroOscarSel}
                  onValorChange={(v) => {
                    setSalidaRSierroOscarSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoSalidaRSierroOscar('');
                  }}
                  inputNuevo={inputNuevoSalidaRSierroOscar}
                  onInputNuevoChange={setInputNuevoSalidaRSierroOscar}
                  onAnadir={() =>
                    addToListSelectAndCatalog(
                      inputNuevoSalidaRSierroOscar,
                      sierroOscar,
                      setSierroOscar,
                      setSalidaRSierroOscarSel,
                      setInputNuevoSalidaRSierroOscar,
                      'ALFA_TANGO_2_SIERRO_OSCAR'
                    )
                  }
                      placeholderNuevo="Sierra Oscar"
                      etiquetaAnadir="➕ Agregar Sierra Oscar"
                />
                  )}
                </div>
              )}

              {salidaRPaso === 'vehiculo' && (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">Vehículo</p>
                    <p className="text-sm text-gray-600">Seleccione el tipo de vehículo e ingrese su número.</p>
                    <div className="flex flex-wrap gap-2">
                      {SALIDA_R_VEHICULO_OPCIONES.map((op) => (
                        <label
                          key={op.valor}
                          className={`cursor-pointer select-none rounded-lg border px-4 py-2 text-sm font-medium ${
                            salidaRTipoUnidad === op.valor
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                        <input
                          type="radio"
                            name="at2-salida-r-vehiculo-tipo"
                            className="sr-only"
                            value={op.valor}
                            checked={salidaRTipoUnidad === op.valor}
                          onChange={() => {
                              setError('');
                              setSalidaRTipoUnidad(op.valor);
                            setSalidaRUnidadSel('');
                            setInputNuevaSalidaRMula('');
                            setInputNuevaSalidaRMontacargas('');
                          }}
                        />
                          {op.etiqueta}
                      </label>
                      ))}
                    </div>
                  </div>
                  {salidaRTipoUnidad === 'mula' && (
                    <SelectConAgregar
                      titulo="Número de mula"
                      opciones={mulas}
                      valor={salidaRUnidadSel}
                      onValorChange={(v) => {
                        setSalidaRUnidadSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaRMula('');
                      }}
                      inputNuevo={inputNuevaSalidaRMula}
                      onInputNuevoChange={setInputNuevaSalidaRMula}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaRMula,
                          mulas,
                          setMulas,
                          setSalidaRUnidadSel,
                          setInputNuevaSalidaRMula,
                          'ALFA_TANGO_2_MULA'
                        )
                      }
                      placeholderNuevo="Número de mula"
                      etiquetaAnadir="➕ Agregar mula"
                    />
                  )}
                  {salidaRTipoUnidad === 'montacargas' && (
                    <SelectConAgregar
                      titulo="Montacargas"
                      opciones={montacargas}
                      valor={salidaRUnidadSel}
                      onValorChange={(v) => {
                        setSalidaRUnidadSel(v);
                        if (v !== ADD_SENTINEL) setInputNuevaSalidaRMontacargas('');
                      }}
                      inputNuevo={inputNuevaSalidaRMontacargas}
                      onInputNuevoChange={setInputNuevaSalidaRMontacargas}
                      onAnadir={() =>
                        addToListSelectAndCatalog(
                          inputNuevaSalidaRMontacargas,
                          montacargas,
                          setMontacargas,
                          setSalidaRUnidadSel,
                          setInputNuevaSalidaRMontacargas,
                          'ALFA_TANGO_2_MONTACARGAS'
                        )
                      }
                      placeholderNuevo="Identificador de montacargas"
                      etiquetaAnadir="➕ Agregar montacargas"
                    />
                  )}
                </div>
              )}

              {salidaRPaso === 'observacion' && (
                <div className="space-y-5">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">Observación</p>
                  <BloqueRadioOpcionesAt2
                    titulo="¿Lleva dolis?"
                    valor={salidaRDolisObs}
                    setValor={setSalidaRDolisObs}
                    opciones={OPCIONES_MATERIAL_OBS_R}
                  />
                  <div className="border-t border-gray-100 pt-4">
                    <BloqueRadioOpcionesAt2
                      titulo="¿Lleva carretas?"
                      valor={salidaRCanetasObs}
                      setValor={setSalidaRCanetasObs}
                      opciones={OPCIONES_MATERIAL_OBS_R}
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-r-obs-texto">
                      Detalle adicional (obligatorio)
                    </label>
                    <textarea
                      id="at2-salida-r-obs-texto"
                      rows={4}
                      value={salidaRObservacionTexto}
                      onChange={(e) => setSalidaRObservacionTexto(e.target.value)}
                      className="w-full min-h-[6.5rem] rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none whitespace-pre-wrap break-words"
                      placeholder="Ej. si trae dolis vacíos, palet o materiales…"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoSalidaRIndex > 0 && (
                  <button
                    type="button"
                    onClick={salidaRRetroceder}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={salidaRContinuar}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                    pasoSalidaRIndex > 0 ? 'sm:flex-1' : ''
                  }`}
                >
                  {pasoSalidaRIndex === PASOS_SALIDA_DIST_R.length - 1
                    ? 'Guardar en bitácora'
                    : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          {vista === VISTA_MENU_SALIDA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Salida</h2>
                <p className="text-sm text-gray-600 mt-1">Seleccione la ruta de salida.</p>
              </div>
              <BotonRutaAt2
                zona="aire"
                origen="Z. Distribución"
                destino="Z. Aire"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setSalidaDistPaso('vehiculo');
                  setVista(VISTA_SALIDA_DIST_AIRE);
                }}
              />
              <BotonRutaAt2
                zona="transferencia"
                origen="Z. Distribución"
                destino="Z. Transferencia"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setSalidaXferPaso('vehiculo');
                  setVista(VISTA_SALIDA_DIST_TRANSFERENCIA);
                }}
              />
              <BotonRutaAt2
                zona="alfa"
                origen="Z. Distribución"
                destino="Z. Alfa"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setSalidaAlfaPaso('operadores');
                  setVista(VISTA_SALIDA_DIST_ALFA);
                }}
              />
              <BotonRutaAt2
                zona="r"
                origen="Z. Distribución"
                destino="Z. R"
                onClick={() => {
                  setSuccess('');
                  setError('');
                  resetFlujoForms();
                  setSalidaRPaso('personal');
                  setVista(VISTA_SALIDA_DIST_R);
                }}
              />
            </div>
          )}

          {vista === VISTA_CARGA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress('Ingreso - Carga', pasoCargaIndex, PASOS_CARGA.length)}

              {cargaPaso === 'unidad' && (
                <SelectConAgregar
                  titulo="Número de Camión o equipo"
                  descripcion="Seleccione la unidad o equipo de transporte, o agréguelo al catálogo."
                  opciones={unidades}
                  valor={cargaCamionSel}
                  onValorChange={(v) => {
                    setCargaCamionSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaCargaCamion('');
                  }}
                  inputNuevo={inputNuevaCargaCamion}
                  onInputNuevoChange={setInputNuevaCargaCamion}
                  onAnadir={() =>
                    (async () => {
                      const valor = inputNuevaCargaCamion.trim();
                      addToListAndSelect(
                        valor,
                        unidades,
                        setUnidades,
                        setCargaCamionSel,
                        setInputNuevaCargaCamion
                      );
                      await saveAt2DatoInCatalogo(valor, 'VEHICULO');
                    })()
                  }
                  placeholderNuevo="Camión o equipo"
                  etiquetaAnadir="➕ Agregar"
                  textoBotonGuardar="Agregar y guardar"
                />
              )}

              {cargaPaso === 'chofer' && (
                <SelectConAgregar
                  titulo="Ingrese el nombre del chofer"
                  descripcion="Seleccione un chofer existente o agréguelo"
                  opciones={choferes}
                  valor={choferSel}
                  onValorChange={(v) => {
                    setChoferSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevoChofer('');
                  }}
                  inputNuevo={inputNuevoChofer}
                  onInputNuevoChange={setInputNuevoChofer}
                  onAnadir={() =>
                    (async () => {
                      const valor = inputNuevoChofer.trim();
                      addToListAndSelect(valor, choferes, setChoferes, setChoferSel, setInputNuevoChofer);
                      await saveAt2DatoInCatalogo(valor, 'CONDUCTOR');
                    })()
                  }
                  placeholderNuevo="Nombre del chofer"
                  etiquetaAnadir="➕ Agregar"
                  textoBotonGuardar="Agregar y guardar chofer"
                />
              )}

              {cargaPaso === 'aerolinea' && (
                <SelectConAgregar
                  titulo="Seleccione la aerolínea"
                  descripcion="Seleccione una aerolínea existente o agréguela."
                  opciones={aerolineas}
                  valor={aerolineaSel}
                  onValorChange={(v) => {
                    setAerolineaSel(v);
                    if (v !== ADD_SENTINEL) setInputNuevaAerolinea('');
                  }}
                  inputNuevo={inputNuevaAerolinea}
                  onInputNuevoChange={setInputNuevaAerolinea}
                  onAnadir={() =>
                    (async () => {
                      const valor = inputNuevaAerolinea.trim();
                      addToListAndSelect(
                      valor,
                      aerolineas,
                      setAerolineas,
                      setAerolineaSel,
                      setInputNuevaAerolinea
                    );
                      await saveAt2DatoInCatalogo(valor, 'ALFA_TANGO_2_AEROLINEA');
                    })()
                  }
                  placeholderNuevo="Nombre de la aerolínea"
                  etiquetaAnadir="➕ Agregar"
                  textoBotonGuardar="Agregar y guardar aerolínea"
                />
              )}

              {cargaPaso === 'foto_elementos' && (
                <div className="space-y-4">
                  <p className="text-xl font-bold text-gray-900">Fotos de los elementos</p>
                  <p className="text-sm text-gray-600">
                    Adjunte hasta {MAX_FOTOS_SLOT} fotos de la carga o elementos relevantes (mínimo 1).
                  </p>
                  <BloqueFotosMultiAt2
                    destino="carga_elementos"
                    useDeviceCamera={useDeviceCamera}
                    previewUrls={fotoPreview.carga_elementos}
                    previewAltBase="Elementos carga"
                    maxCount={MAX_FOTOS_SLOT}
                    procesandoFoto={fotoProcesandoDestino === 'carga_elementos'}
                    cameraAbierta={cameraAbierta}
                    cameraDestinoActivo={cameraDestinoActivo}
                    videoRef={videoRef}
                    canvasRef={canvasRef}
                    onAbrirCapturaMovil={abrirCapturaMovil}
                    onIniciarCamaraEscritorio={startCameraDesktop}
                    onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                    onCapturarFotograma={capturarFotogramaDesktop}
                    onCerrarCamara={stopCamera}
                    onQuitarEnIndice={removeFotoAt}
                  />
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {pasoCargaIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      stopCamera();
                      setCargaPaso(PASOS_CARGA[pasoCargaIndex - 1]);
                    }}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    stopCamera();
                    if (cargaPaso === 'unidad' && (!cargaCamionSel || cargaCamionSel === ADD_SENTINEL)) {
                      setError('Seleccione o agregue camión o equipo.');
                      return;
                    }
                    if (cargaPaso === 'chofer' && (!choferSel || choferSel === ADD_SENTINEL)) {
                      setError('Seleccione o agregue un chofer.');
                      return;
                    }
                    if (cargaPaso === 'aerolinea' && (!aerolineaSel || aerolineaSel === ADD_SENTINEL)) {
                      setError('Seleccione o agregue una aerolínea.');
                      return;
                    }
                    if (cargaPaso === 'foto_elementos' && countFotos('carga_elementos') < 1) {
                      setError('Adjunte al menos una foto de los elementos.');
                      return;
                    }
                    if (pasoCargaIndex === PASOS_CARGA.length - 1) {
                      setRevisionTipo('carga');
                      openConfirmGuardar();
                      return;
                    }
                    setCargaPaso(PASOS_CARGA[pasoCargaIndex + 1]);
                  }}
                  className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${pasoCargaIndex > 0 ? 'sm:flex-1' : ''}`}
                >
                  {pasoCargaIndex === PASOS_CARGA.length - 1 ? 'Guardar en bitácora' : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          {vista === VISTA_INGRESO && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-5">
              {renderProgress('Salida Alfa Tango 2', salidaPasoIndice, salidaPasosTotales)}

              {salidaPaso === 'camion' && (
                <div className="space-y-4">
                  <SelectConAgregar
                    titulo="Número de camión"
                    descripcion="Seleccione una unidad existente o agréguela al catálogo. No se solicita foto del camión."
                    opciones={unidades}
                    valor={unidadSel}
                    onValorChange={(v) => {
                      setUnidadSel(v);
                      if (v !== ADD_SENTINEL) setInputNuevaUnidad('');
                    }}
                    inputNuevo={inputNuevaUnidad}
                    onInputNuevoChange={setInputNuevaUnidad}
                    onAnadir={() =>
                      (async () => {
                        const valor = inputNuevaUnidad.trim();
                        addToListAndSelect(valor, unidades, setUnidades, setUnidadSel, setInputNuevaUnidad);
                        await saveAt2DatoInCatalogo(valor, 'VEHICULO');
                      })()
                    }
                    placeholderNuevo="Unidad o número de camión"
                    etiquetaAnadir="➕ Agregar"
                    textoBotonGuardar="Agregar y guardar unidad"
                  />
                </div>
              )}

              {salidaPaso === 'conductor' && (
                <div className="space-y-3">
                  <p className="text-xl font-bold text-gray-900">Nombre del conductor</p>
                  <p className="text-sm text-gray-600">Escriba el nombre completo tal como lo indica el registro.</p>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-conductor">
                    Conductor
                  </label>
                  <input
                    id="at2-salida-conductor"
                    type="text"
                    autoComplete="name"
                    value={salidaConductorNombre}
                    onChange={(e) => setSalidaConductorNombre(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Nombre y apellido"
                  />
                </div>
              )}

              {salidaPaso === 'dolly' && (
                <div className="space-y-4">
                  <p className="text-xl font-bold text-gray-900">Dolly y carretas</p>
                  <p className="text-sm text-gray-600">
                    Solo números (0–999). Deje vacío si la cantidad es cero.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-dolly">
                        Dolly
                      </label>
                      <input
                        id="at2-salida-dolly"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={salidaDollyQty}
                        onChange={(e) => setSalidaDollyQty(soloDigitosMax(e.target.value, 3))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        placeholder="Ej. 2"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-carretas">
                        Carretas
                      </label>
                      <input
                        id="at2-salida-carretas"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={salidaCarretasQty}
                        onChange={(e) => setSalidaCarretasQty(soloDigitosMax(e.target.value, 3))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        placeholder="Ej. 1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {salidaPaso === 'servicios' && (
                <div className="space-y-4">
                  <p className="text-xl font-bold text-gray-900">¿Servicios adicionales?</p>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => {
                        stopCamera();
                        setError('');
                        setSalidaServiciosAdicionales(false);
                        setRevisionTipo('salida_alfa2');
                        openConfirmGuardar();
                      }}
                      className="w-full sm:flex-1 py-3 rounded-lg border-2 border-gray-300 bg-white text-gray-800 font-medium text-sm hover:bg-gray-50"
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setError('');
                        setSalidaServiciosAdicionales(true);
                        setSalidaCantidadServicios('');
                        setSalidaPaso('cantidad');
                      }}
                      className="w-full sm:flex-1 py-3 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700"
                    >
                      Sí
                    </button>
                  </div>
                </div>
              )}

              {salidaPaso === 'cantidad' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="at2-salida-n-svc">
                    Número de servicios especiales
                  </label>
                  <input
                    id="at2-salida-n-svc"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={salidaCantidadServicios}
                    onChange={(e) =>
                      setSalidaCantidadServicios(soloDigitosMax(e.target.value, SALIDA_CANTIDAD_SVC_DIGITOS_MAX))
                    }
                    className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Ej. 3"
                  />
                </div>
              )}

              {salidaSvcUi?.part === 'codigo' && (
                <div className="space-y-3">
                  <p className="text-xl font-bold text-gray-900">
                    Servicio adicional {salidaSvcUi.idx + 1} — código o número
                  </p>
                  <p className="text-sm text-gray-600">Identificador del servicio (texto o número según corresponda).</p>
                  <input
                    type="text"
                    value={salidaSvcCodigos[salidaSvcUi.idx] ?? ''}
                    onChange={(e) => {
                      const ix = salidaSvcUi.idx;
                      setSalidaSvcCodigos((prev) => {
                        const next = [...prev];
                        next[ix] = e.target.value;
                        return next;
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Código / número de servicio"
                  />
                </div>
              )}

              {salidaSvcUi?.part === 'guia' && (
                <div className="space-y-4">
                  <p className="text-xl font-bold text-gray-900">
                    Servicio {salidaSvcUi.idx + 1} — guía master
                  </p>
                  <p className="text-sm text-gray-600">Adjunte al menos una foto clara de la guía master.</p>
                  <BloqueFotosMultiAt2
                    destino={`salida_svc_${salidaSvcUi.idx}_guia`}
                    useDeviceCamera={useDeviceCamera}
                    previewUrls={fotoPreview[`salida_svc_${salidaSvcUi.idx}_guia`]}
                    previewAltBase={`Guía servicio ${salidaSvcUi.idx + 1}`}
                    maxCount={MAX_FOTOS_SLOT}
                    procesandoFoto={fotoProcesandoDestino === `salida_svc_${salidaSvcUi.idx}_guia`}
                    cameraAbierta={cameraAbierta}
                    cameraDestinoActivo={cameraDestinoActivo}
                    videoRef={videoRef}
                    canvasRef={canvasRef}
                    onAbrirCapturaMovil={abrirCapturaMovil}
                    onIniciarCamaraEscritorio={startCameraDesktop}
                    onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                    onCapturarFotograma={capturarFotogramaDesktop}
                    onCerrarCamara={stopCamera}
                    onQuitarEnIndice={removeFotoAt}
                  />
                </div>
              )}

              {salidaSvcUi?.part === 'bultos' && (
                <div className="space-y-4">
                  <p className="text-xl font-bold text-gray-900">
                    Servicio {salidaSvcUi.idx + 1} — bultos
                  </p>
                  <p className="text-sm text-gray-600">Una foto general de los bultos de este servicio.</p>
                  <BloqueFotosMultiAt2
                    destino={`salida_svc_${salidaSvcUi.idx}_bultos`}
                    useDeviceCamera={useDeviceCamera}
                    previewUrls={fotoPreview[`salida_svc_${salidaSvcUi.idx}_bultos`]}
                    previewAltBase={`Bultos servicio ${salidaSvcUi.idx + 1}`}
                    maxCount={1}
                    procesandoFoto={fotoProcesandoDestino === `salida_svc_${salidaSvcUi.idx}_bultos`}
                    cameraAbierta={cameraAbierta}
                    cameraDestinoActivo={cameraDestinoActivo}
                    videoRef={videoRef}
                    canvasRef={canvasRef}
                    onAbrirCapturaMovil={abrirCapturaMovil}
                    onIniciarCamaraEscritorio={startCameraDesktop}
                    onAbrirArchivoEscritorio={abrirArchivoEscritorio}
                    onCapturarFotograma={capturarFotogramaDesktop}
                    onCerrarCamara={stopCamera}
                    onQuitarEnIndice={removeFotoAt}
                  />
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {salidaPaso !== 'camion' && (
                  <button
                    type="button"
                    onClick={salidaRetroceder}
                    className="w-full sm:flex-1 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50"
                  >
                    ← Paso anterior
                  </button>
                )}
                {salidaPaso !== 'servicios' && (
                  <button
                    type="button"
                    onClick={salidaContinuar}
                    className={`w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 ${
                      salidaPaso !== 'camion' ? 'sm:flex-1' : ''
                    }`}
                  >
                    {(() => {
                      const nTot = parseInt(String(salidaCantidadServicios || '').replace(/\D/g, '') || '0', 10);
                      const nn = Number.isFinite(nTot) && nTot >= 1 ? nTot : 1;
                      const ultimoBultos =
                        salidaSvcUi?.part === 'bultos' &&
                        salidaSvcUi.idx + 1 >= nn &&
                        salidaServiciosAdicionales === true;
                      return ultimoBultos ? 'Guardar en bitácora' : 'Continuar';
                    })()}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfirmModal && revisionTipo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-4 py-3 text-white rounded-t-xl" style={{ backgroundColor: headerBg }}>
              <h2 className="text-xl font-bold leading-tight">Alfa Tango 2</h2>
              <p className="text-xs mt-1 opacity-95">
                <span className="font-semibold">Tipo:</span> {resumenCabeceraRevision.tipo || '—'}
              </p>
              {resumenCabeceraRevision.opcion ? (
                <p className="text-xs opacity-95">
                  <span className="font-semibold">Opción:</span> {resumenCabeceraRevision.opcion}
                </p>
              ) : null}
            </div>
            <div className="p-6 space-y-4 border border-t-0 border-gray-200 rounded-b-xl">
              {revisionTipo !== 'ingreso_aire_distrib' &&
                revisionTipo !== 'ingreso_alfa_distrib' &&
                revisionTipo !== 'salida_dist_aire' &&
                revisionTipo !== 'salida_dist_alfa' &&
                revisionTipo !== 'salida_dist_r' && (
                <p className="text-sm text-gray-700">Confirme para guardar el registro.</p>
              )}
              <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
                {buildResumenLineas(revisionTipo).map((row) => (
                  <div key={`${row.k}-${row.slot || 'txt'}`} className="space-y-2">
                    <p className={row.multiline ? 'whitespace-pre-wrap break-words' : undefined}>
                      <span className="font-medium text-gray-700">{row.k}:</span>{' '}
                      <strong className={row.multiline ? 'font-medium' : undefined}>{row.v}</strong>
                    </p>
                    {row.slot && (fotoPreview[row.slot] || []).length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {(fotoPreview[row.slot] || []).map((url, i) => (
                          <img
                            key={`${row.slot}-${url}-${i}`}
                            src={url}
                            alt={`${row.k} ${i + 1}`}
                            className="h-16 w-16 sm:h-20 sm:w-20 rounded-md border object-cover bg-white"
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setRevisionTipo(null);
                    if (vista === VISTA_INGRESO_AIRE_DISTRIB) {
                      setIngresoAirePaso('carga_tipos');
                    }
                    if (vista === VISTA_SALIDA_DIST_AIRE) {
                      if (salidaDistServiciosEspeciales === true) {
                        const n = parseInt(String(salidaDistCantidadServicios || '').replace(/\D/g, '') || '0', 10);
                        const idx = Number.isFinite(n) && n > 0 ? n - 1 : 0;
                        setSalidaDistPaso(`servicio_${idx}`);
                      } else {
                        setSalidaDistPaso('servicios_especiales');
                      }
                    }
                    if (vista === VISTA_SALIDA_DIST_TRANSFERENCIA) {
                      if (salidaXferServiciosEspeciales === true) {
                        const n = parseInt(String(salidaXferCantidadServicios || '').replace(/\D/g, '') || '0', 10);
                        const idx = Number.isFinite(n) && n > 0 ? n - 1 : 0;
                        setSalidaXferPaso(`servicio_${idx}`);
                      } else {
                        setSalidaXferPaso('servicios_especiales');
                      }
                    }
                    if (vista === VISTA_SALIDA_DIST_ALFA) {
                      setSalidaAlfaPaso('observaciones');
                    }
                    if (vista === VISTA_INGRESO_ALFA_DISTRIB) {
                      setIngresoAlfaPaso('observaciones');
                    }
                    if (vista === VISTA_SALIDA_DIST_R) {
                      setSalidaRPaso('observacion');
                    }
                  }}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmitAt2}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50"
                  style={{ backgroundColor: headerBg }}
                >
                  {submitting ? 'Guardando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AlfaTango2;
